/* ============================================================================
   configuratore-universale.js — MOTORE UNICO dei configuratori Fotorecord
   ----------------------------------------------------------------------------
   Un solo file per i 4 tipi: aste su misura (casm) · montature a giorno (cmg)
   · pannelli solo vetro (cpv) · opere d'autore (caoa).

   Architettura: ConfiguratoreBase (logica condivisa: upload foto, stampa,
   loaders immagini, 9-slice, quantità, preset, clip, carrello nativo, switch
   tipo con handoff) + 4 classi tipo che portano la logica specifica
   (dati, gallery, disegno canvas, calcolo prezzo, properties carrello).

   Sostituisce: configuratore-aste-su-misura.js, configuratore-montature-giorno.js,
   configuratore-pannelli-solo-vetro.js, configuratore-opere-autore.js.
   Dipendenze (caricate prima): configuratore-pricing.js, configuratore-core.js,
   configuratore-aste-data.js (solo aste/opere).

   Compat esterna: window.ConfiguratoreAsteSuMisura resta esposta con la stessa
   API (constructor senza init, _drawPreview(canvas), loaders, __bgColor/__noShadow/__aperturaVuota)
   — è consumata dal renderer headless (generate_frame_images.py) e dal
   configuratore preventivi interno (configura_preventivo.html).

   Pricing SSOT: scripts/price_logic.py → window.FOTORECORD.PRICING_SSOT.
   ============================================================================ */
(function () {
  'use strict';

  const FR = window.FOTORECORD = window.FOTORECORD || {};
  /* «FRAS0055 - Cornice Sagomata Blu Poro Aperto»: il nome che il cliente legge in
     riepilogo, carrello e ordine (core.nomeBreveCliente, decisione 21/09/2026).
     Record assente → '' cosi' `nb(x) || 'Nessuno'` resta leggibile. */
  const nb = (rec) => (rec && FR.core && FR.core.nomeBreveCliente) ? FR.core.nomeBreveCliente(rec) : (rec && rec.nome ? String(rec.nome).split('|')[0].trim() : '');
  FR.INSTANCES = FR.INSTANCES || {};

  const NESSUNO_SKU        = '';   // fronteSku/retroSku «vuoto» = senza lastra / senza fondo
  /* Gallery (profili, passepartout cartone e legno): righe visibili, il resto scorre. Le
     colonne le fissa il CSS (repeat(N, …)); l'altezza si MISURA dalla prima cella, cosi'
     vale a ogni larghezza senza un numero copiato tra CSS e JS (_limitaRighe). Anche le
     righe le dichiara lo stesso CSS che decide le colonne, nella custom property
     `--gallery-righe` sulla griglia (le aste la cambiano col breakpoint mobile, vedi
     configuratore-aste-su-misura.css); GALLERY_RIGHE e' solo il ripiego per i fogli che
     non la dichiarano (montature, opere). */
  const GALLERY_RIGHE      = 3;
  // Soglia INCLUSIVA: battente >= questo valore = "alto". Letta da PRICING_SSOT
  // (price_logic.PP_LEGNO_MIN_BATTENTE_MM), era una terza copia a mano dello stesso 20.
  const BATTENTE_ALTO_MM   = FR.PRICING_SSOT.PP_LEGNO_MIN_BATTENTE_MM;
  // identity/terminologia_standard.md §4 dice "Battente Alto almeno 20 mm" e
  // Basso "inferiori a 20 mm"; price_logic.PP_LEGNO_MIN_BATTENTE_MM = 20.0 è il
  // minimo NECESSARIO, quindi 20 basta. Fino al 27/07/2026 i confronti erano
  // `> 20` / `<= 20`: i 10 profili con battente esattamente 20 mm risultavano
  // "basso" e si vedevano rifiutare passepartout in legno e stampa su tela,
  // mentre la pagina diceva "almeno 20 mm".
  // Battente minimo per stampa su tela. Letto da PRICING_SSOT (generato da price_logic):
  // era un 20 copiato a mano, col commento che dichiarava la SSOT senza che nulla la
  // garantisse — cioè il commento faceva da promessa al posto del codice.
  const BATTENTE_MIN_TELA_MM = FR.PRICING_SSOT.BATTENTE_MIN_TELA_MM;
  /* Passepartout in cartone: bordo minimo e default proposto, da PRICING_SSOT
     (price_logic.PASSEPARTOUT_CARTONE_LARGH_MIN_CM / _DEFAULT_CM). Erano otto copie a
     mano dello stesso numero, con accanto un commento che dichiarava la SSOT senza che
     nulla la garantisse. Il minimo vale come ripiego SOLO quando il dato del passepartout
     (larghezzaMinimaPass dal metaobject) non lo dichiara. */
  const PASS_CARTONE_LARGH_MIN_CM     = FR.PRICING_SSOT.PASSEPARTOUT_CARTONE_LARGH_MIN_CM;
  const PASS_CARTONE_LARGH_DEFAULT_CM = FR.PRICING_SSOT.PASSEPARTOUT_CARTONE_LARGH_DEFAULT_CM;
  // tetto del bordo: lo stesso che price_logic.validate_config impone lato server — un
  // cursore che lo supera produce una configurazione rifiutata al checkout (14/09/2026)
  const PASS_CARTONE_LARGH_MAX_CM     = FR.PRICING_SSOT.PASSEPARTOUT_CARTONE_LARGH_MAX_CM;

  /* ── Retro "da tavolo": il montaggio è un DATO, non una parola dentro lo sku ──
     Il campo `montaggio` del metaobject retro_configuratore ("tavolo" | "parete") arriva
     nel dataset come `r.montaggio`. Fino a oggi si leggeva `sku.includes('tavolo')`: con
     gli id opachi (mdftavolo → frre0060) quell'includes non solleva, torna false — e il
     gate sul formato sparisce senza che nulla lo dica. */
  const isRetroTavolo = r => !!r && String(r.montaggio || '').toLowerCase() === 'tavolo';

  /* Limiti di formato del retro da tavolo — SSOT price_logic.MDF_TAVOLO_MAX_BASE_CM /
     MDF_TAVOLO_MAX_ALTEZZA_CM, esposte da PRICING_SSOT: erano un 24/30 copiato a mano nei
     due ripieghi di updateRetroAvailability.
     Il limite lo dichiara il dato (maxBase/maxAltezza dal metaobject, come in
     _defaultRetro): la SSOT entra SOLO quando il dato si contraddice — un retro che si
     dichiara da tavolo e non porta alcun limite (999 = sentinella "nessun limite") — e lo
     si dice, invece di lasciar passare un formato che il piedistallo non regge. */
  const TAVOLO_MAX_BASE_CM    = FR.PRICING_SSOT.MDF_TAVOLO_MAX_BASE_CM;
  const TAVOLO_MAX_ALTEZZA_CM = FR.PRICING_SSOT.MDF_TAVOLO_MAX_ALTEZZA_CM;
  function limitiRetroTavolo(retro, tag) {
    const maxB = retro.maxBase, maxA = retro.maxAltezza;
    if (maxB > 0 && maxB < 999 && maxA > 0 && maxA < 999) return { maxB, maxA };
    console.error(`[${tag}] il retro con montaggio="tavolo" non dichiara un limite di `
                + 'formato (maxBase/maxAltezza): uso PRICING_SSOT.MDF_TAVOLO_MAX_*', retro);
    return { maxB: TAVOLO_MAX_BASE_CM, maxA: TAVOLO_MAX_ALTEZZA_CM };
  }

  /* canvas */
  const CVS_W = 400, CVS_H = 400, CVS_PAD = 44;
  /* Il render di catalogo dei kit (headless, `__margineCatalogo`): la cornice sta all'86% della
     tela sul lato lungo, come le scene su bianco (scene_comuni.QUOTA_BIANCO; il guard
     test_ombra_cornice_parete li confronta), cosi' l'ombra ci sta intera. Fino al 25/10/2026
     arrivava a 44 px dal bordo su 2000 e l'ombra nuova usciva tagliata (Andrea: «rimpicciolisci
     la cornice del 10%»). */
  const MARGINE_CATALOGO = 0.07;
  const SVG_PAD = 48;              // padding preview montature/pannelli (eredità layout SVG)
  const PROF_VISIBILE_CM = 0.4;    // larghezza visiva profilo montatura (solo preview)

  const PASS_LEGNO_COLOR   = '#C8A96E';
  const PASS_CARTONE_COLOR = '#F0ECE0';

  /* ── Modi di montaggio dell'OPERA (chip nel configuratore opere) ──
     Dal 29/07/2026 questi tre non sono più tre pagine fra cui navigare: sono tre modi
     di montare la stessa opera, e si cambiano restando dove si è. Nei tre configuratori
     pubblici il chip è stato tolto (ci si arriva già dal prodotto giusto). */
  const MODI_MONTAGGIO = [
    { key: 'cornice',   label: 'Cornice su misura'   },
    { key: 'montatura', label: 'Montatura a giorno'  },
    { key: 'pannello',  label: 'Pannello solo vetro' },
  ];

  /* ══════════════════════════════════════════════════════════════════════════
     FORMULE CONDIVISE — montatura a giorno e pannello solo vetro
     Stanno qui, e non dentro una classe, perché le usano DUE consumatori: i
     configuratori pubblici (ConfiguratoreMontature / ConfiguratorePannelli) e i modi
     "montatura"/"pannello" del configuratore OPERE. Una seconda copia della stessa
     formula è il modo in cui, in questo repo, i prezzi divergono senza che nessuno
     se ne accorga — e l'unico guardiano che le confronta è a valle (parità sito↔app).
     Restituiscono il NETTO per pezzo: niente IVA, niente supplemento formato grande,
     niente valore dell'opera. Quelli li aggiunge il chiamante, che sa in che pagina sta.
     ══════════════════════════════════════════════════════════════════════════ */

  /* Numero di clip: 4 fino a 35×50 cm, 8 oltre (SSOT soglie: PRICING_SSOT). */
  FR.numeroClip = function (baseCm, altCm, P) {
    const small = Math.min(baseCm, altCm) <= P.SOGLIA_CLIP_LATO_MIN
               && Math.max(baseCm, altCm) <= P.SOGLIA_CLIP_LATO_MAX;
    return small ? P.N_CLIPS_STD : P.N_CLIPS_GRANDE;
  };

  /* Montatura a giorno: profilo sottile sul perimetro esterno, fronte sull'area INTERNA,
     retro sull'AREA ESTERNA (il retro copre tutta la montatura), clip a prezzo di vendita
     netto (niente markup: è già un prezzo deciso). */
  FR.montaturaNetto = function (o) {
    const profCm      = o.profCm;
    const areaIntM2   = (o.base * o.altezza) / 10000;
    const areaEstM2   = ((o.base + 2 * profCm) * (o.altezza + 2 * profCm)) / 10000;
    /* Il costo al metro del profilo arriva con DUE nomi diversi a seconda della fonte:
       `costoMetro` dal metaobject `profili_montature_a_giorno` (configuratore montature),
       `costoMl` da ASTE_CORNICI_FULL (configuratore opere, modo montatura). Leggere solo
       il primo faceva costare il profilo ZERO nelle opere — prezzo più basso del
       configuratore pubblico per lo stesso montaggio, e in silenzio (29/07/2026). */
    const costoMetro  = parseFloat(o.profilo?.costoMetro ?? o.profilo?.costoMl ?? 0) || 0;
    const costoAsta   = FR.perimEsternoM(o.base, o.altezza, profCm * 10) * costoMetro;
    const costoFronte = o.fronte ? areaIntM2 * parseFloat(o.fronte.costoMq || 0) : 0;
    const costoRetro  = o.retro  ? areaEstM2 * parseFloat(o.retro.costoMq  || 0) : 0;
    const costoStampa = o.stampa ? areaIntM2 * (o.stampa.costoMq || 0) : 0;
    const costoClip   = o.clipUnit * o.nClips;
    const mk = o.mk;
    /* `vendita` = gli stessi addendi di `netto`, tenuti separati: li leggono i summary degli
       accordion (detail-accordion.js) per dire quanto pesa ogni componente. Non e' una
       seconda formula: netto e' la loro somma. */
    const vendita = {
      asta:   costoAsta   * mk(o.profilo),
      fronte: costoFronte * mk(o.fronte),
      retro:  costoRetro  * mk(o.retro),
      stampa: costoStampa * mk(o.stampa, o.stampaMkRef),
      clip:   costoClip,
    };
    return {
      costoAsta, costoFronte, costoRetro, costoStampa, costoClip, nClips: o.nClips,
      vendita,
      netto: vendita.asta + vendita.fronte + vendita.retro + vendita.stampa + vendita.clip,
    };
  };

  /* Pannello solo vetro: nessun profilo, fronte e retro entrambi sull'area interna. */
  FR.pannelloNetto = function (o) {
    const areaM2      = (o.base * o.altezza) / 10000;
    const costoFronte = o.fronte ? areaM2 * parseFloat(o.fronte.costoMq || 0) : 0;
    const costoRetro  = o.retro  ? areaM2 * parseFloat(o.retro.costoMq  || 0) : 0;
    const costoStampa = o.stampa ? areaM2 * (o.stampa.costoMq || 0) : 0;
    const costoClip   = o.clipUnit * o.nClips;
    const mk = o.mk;
    const vendita = {
      fronte: costoFronte * mk(o.fronte),
      retro:  costoRetro  * mk(o.retro),
      stampa: costoStampa * mk(o.stampa, o.stampaMkRef),
      clip:   costoClip,
    };
    return {
      costoFronte, costoRetro, costoStampa, costoClip, nClips: o.nClips,
      vendita,
      netto: vendita.fronte + vendita.retro + vendita.stampa + vendita.clip,
    };
  };

  /* ── Helper: compatibilità passepartout cartone con le dimensioni opera ── */
  function _passCartoneCompatibile(p, base, altezza) {
    if (!base || !altezza) return true;
    if (!p.lastraBase || !p.lastraAlt) return true;
    const minL = p.larghezzaMinimaPass > 0 ? p.larghezzaMinimaPass : PASS_CARTONE_LARGH_MIN_CM;
    return (base   + 2 * minL) <= p.lastraBase
        && (altezza + 2 * minL) <= p.lastraAlt;
  }

  /* ══════════════════════════════════════════════════════════════════════════
     EFFETTI SUPERFICIE (fronte vetro/crilex + stampa lucida/tela) — PROCEDURALI
     ----------------------------------------------------------------------------
     Sostituiscono l'overlay PNG lattiginoso (drawImage source-over @0.35) che
     opacizzava la foto. Riflessi/lucido in 'screen' (aggiunge luce, non vela),
     tela/tinta crilex in 'soft-light' (modula, non copre).
     USE_PROCEDURAL_FX=false → torna alle PNG (fallback: window.__EFFETTO_*).
     SSOT calibrazione: tenere in SYNC con la copia in
     apps/preventivi/configuratore/assets/js/frame-render-engine.js.
     ══════════════════════════════════════════════════════════════════════════ */
  const USE_PROCEDURAL_FX = true;
  const FX = {
    pos:    0.50,                                   // posizione banda riflesso (diag TL→BR)
    vetro:  { int: 0.14, spr: 0.30, streak: 0.10 },
    crilex: { int: 0.12, spr: 0.30, streak: 0.07, blu: 0.07 },
    lucida: { int: 0.07, spr: 0.50 },
    tela:   { filiCm: 12, depth: 0.55, alpha: 0.55 },
  };
  const _fxCl01 = v => (v < 0 ? 0 : v > 1 ? 1 : v);

  // Riflesso diagonale (screen): banda larga morbida + streak stretta più viva.
  function fxReflect(ctx, x, y, w, h, intensity, spread, streak) {
    ctx.save();
    ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
    ctx.globalCompositeOperation = 'screen';
    const c = FX.pos;
    const g = ctx.createLinearGradient(x, y, x + w, y + h);
    g.addColorStop(_fxCl01(c - spread), 'rgba(255,255,255,0)');
    g.addColorStop(c,                   'rgba(255,255,255,' + intensity + ')');
    g.addColorStop(_fxCl01(c + spread), 'rgba(255,255,255,0)');
    ctx.fillStyle = g; ctx.fillRect(x, y, w, h);
    if (streak > 0) {
      const c2 = _fxCl01(c + 0.11);
      const g2 = ctx.createLinearGradient(x, y, x + w, y + h);
      g2.addColorStop(_fxCl01(c2 - 0.035), 'rgba(255,255,255,0)');
      g2.addColorStop(c2,                  'rgba(255,255,255,' + streak + ')');
      g2.addColorStop(_fxCl01(c2 + 0.035), 'rgba(255,255,255,0)');
      ctx.fillStyle = g2; ctx.fillRect(x, y, w, h);
    }
    ctx.restore();
  }

  // Tinta azzurra crilex (soft-light): sposta il colore senza opacizzare.
  function fxTint(ctx, x, y, w, h, amt) {
    ctx.save();
    ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
    ctx.globalCompositeOperation = 'soft-light';
    ctx.fillStyle = 'rgba(90,150,255,' + amt + ')';
    ctx.fillRect(x, y, w, h);
    ctx.restore();
  }

  // Trama tela: tile basketweave grigio-50% cacheato (emboss via soft-light).
  let _fxWeaveTile = null, _fxWeaveKey = '';
  function fxWeaveTileFor(pitch, depth) {
    const key = pitch + '|' + depth;
    if (key === _fxWeaveKey && _fxWeaveTile) return _fxWeaveTile;
    const N = pitch;                                     // repeat = 1 filo (trama fine, non basketweave 2x)
    const t = document.createElement('canvas'); t.width = N; t.height = N;
    const c = t.getContext('2d'); const img = c.createImageData(N, N); const d = img.data;
    for (let yy = 0; yy < N; yy++) for (let xx = 0; xx < N; xx++) {
      const fx = Math.sin((xx + 0.5) / pitch * Math.PI);   // 0..1 lungo il filo (ordito)
      const fy = Math.sin((yy + 0.5) / pitch * Math.PI);   // 0..1 lungo il filo (trama)
      const lum = fx + fy - 1;                             // -1 (incrocio/cimosa) .. +1 (centro fili)
      let v = 128 + lum * depth * 90; v = v < 0 ? 0 : v > 255 ? 255 : v;
      const i = (yy * N + xx) * 4; d[i] = d[i + 1] = d[i + 2] = v; d[i + 3] = 255;
    }
    c.putImageData(img, 0, 0);
    _fxWeaveTile = t; _fxWeaveKey = key; return t;
  }
  function fxWeave(ctx, x, y, w, h, baseCm) {
    const pxPerCm = w / Math.max(1, baseCm);
    const pitch = Math.max(3, Math.round(pxPerCm / FX.tela.filiCm));
    const pat = ctx.createPattern(fxWeaveTileFor(pitch, FX.tela.depth), 'repeat');
    ctx.save();
    ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
    ctx.globalCompositeOperation = 'soft-light';
    ctx.globalAlpha = FX.tela.alpha;
    if (pat.setTransform) pat.setTransform(new DOMMatrix().translate(x, y));
    ctx.fillStyle = pat; ctx.fillRect(x, y, w, h);
    ctx.restore();
  }

  // Cache immagini di fallback (usate solo se USE_PROCEDURAL_FX=false).
  const _fxFbCache = Object.create(null);
  function fxFallbackImg(url, onready) {
    if (!url) return null;
    const c = _fxFbCache[url];
    if (c === undefined) {
      _fxFbCache[url] = null;
      const im = new Image(); im.crossOrigin = 'anonymous';
      im.onload  = () => { _fxFbCache[url] = im;    if (onready) onready(); };
      im.onerror = () => { _fxFbCache[url] = false; };
      im.src = url;
      return null;
    }
    return c || null;
  }

  /* ══════════════════════════════════════════════════════════════════════════
     BASE — logica condivisa fra i 4 tipi
     ══════════════════════════════════════════════════════════════════════════ */
  class ConfiguratoreBase {
    constructor(p, opts) {
      opts = opts || {};
      this.p       = p;                              // prefisso id/classi DOM
      this.logTag  = opts.logTag || p.toUpperCase(); // per i log console
      this.ids     = Object.assign({ cta: p + '-cta' }, opts.ids || {});
      this.PRICING = Object.assign({}, FR.PRICING_SSOT, opts.pricing || {});
      this._debounceTimers = {};
      /* artwork (foto del cliente) */
      this._artworkImg  = null;
      this._artworkFile = null;
      /* immagini condivise (usate dai tipi che le caricano) */
      this._effettoImg = null;  this._effettoImgUrl = '';
      this._clipImg    = null;  this._clipImgUrl    = '';
    }

    /* ── dati/fascia ── */
    _dataGlobal() { return null; }  // override per tipo (window.SHOPIFY_*)
    _getFascia(baseCm, altCm) {
      return FR.getFascia(baseCm, altCm, this._dataGlobal()?.spedizioni);
    }
    $(suffix) { return document.getElementById(this.p + '-' + suffix); }

    /* ── Barra fissa mobile: totale + CTA sempre in vista (12/09/2026).
       Su telefono il bottone stava a ~4.300 px dopo otto passi e niente restava in
       vista: Italia 90 gg, al carrello 2,7% da mobile contro 8,1% da desktop, con l'85%
       delle visite da telefono. La barra e' uno SPECCHIO: legge prezzo e CTA veri
       (MutationObserver) e il suo bottone clicca quello vero, quindi nessuna seconda
       macchina a stati. Compare solo quando la CTA vera e' fuori schermo. ── */
    _mountMobileBar() {
      const cta   = document.getElementById(this.ids.cta);
      const price = document.querySelector('.' + this.p + '-price-amount');
      if (!cta || !price || document.getElementById(this.p + '-mobile-bar')) return;
      const bar = document.createElement('div');
      bar.id = this.p + '-mobile-bar';
      bar.className = 'cfg-mobile-bar';
      bar.setAttribute('aria-hidden', 'true');
      bar.innerHTML =
        '<div class="cfg-mobile-bar__price"><span class="cfg-mobile-bar__label">Totale</span>' +
        '<strong class="cfg-mobile-bar__amount"></strong></div>' +
        '<button type="button" class="cfg-mobile-bar__cta"></button>';
      document.body.appendChild(bar);
      const amount = bar.querySelector('.cfg-mobile-bar__amount');
      const btn    = bar.querySelector('.cfg-mobile-bar__cta');
      btn.addEventListener('click', () => cta.click());
      const sync = () => {
        const placeholder = price.dataset.placeholder === 'true';
        amount.textContent = placeholder ? '—' : price.textContent.trim();
        btn.textContent = cta.textContent;
        btn.disabled    = cta.disabled;
        bar.classList.toggle('is-placeholder', placeholder);
      };
      const obs = { childList: true, characterData: true, subtree: true, attributes: true };
      new MutationObserver(sync).observe(price, obs);
      new MutationObserver(sync).observe(cta, obs);
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([e]) => {
          const via = !e.isIntersecting;
          bar.classList.toggle('is-visible', via);
          bar.setAttribute('aria-hidden', via ? 'false' : 'true');
          document.body.classList.toggle('cfg-mobile-bar-space', via);
        }, { threshold: 0 }).observe(cta);
      } else {
        bar.classList.add('is-visible');
      }
      sync();
    }

    /**
     * Markup PER-COMPONENTE (SSOT: colonne `markup`/`markup_grande` dei DB, via metaobject).
     * Ogni voce che concorre al prezzo porta `markup` (standard) e `markupGrande` (fascia
     * 61-105 cm); in fascia grande si usa il secondo, col primo come ripiego legittimo.
     *
     * NON esiste un valore di ripiego numerico. Fino al 18/07/2026 i chiamanti scrivevano
     * `mk(x) || 2.9`: al primo campo mancante il prezzo restava plausibile e sbagliato
     * invece di segnalare il difetto — è così che le opere in fascia grande sono uscite
     * sottoprezzate del 3% per giorni. Un markup assente su un componente che ha davvero un
     * costo è un difetto di DATO, non una condizione da compensare: viene raccolto in
     * `mk.mancanti` e chi calcola deve rinunciare al prezzo (vedi `_markupMancante`).
     *
     * Il costo zero non è un difetto: le voci "Nessuno" e i componenti non selezionati non
     * hanno markup perché non entrano nel conto.
     */
    _makeMk(isGrande) { return FR.makeMk(isGrande); }   // una sola lettura dei markup, in pricing

    _markupMancante(mk) {
      if (!mk.mancanti.length) return false;
      console.error(
        `[${this.p}] markup assente su: ${[...new Set(mk.mancanti)].join(', ')} — prezzo non calcolabile. ` +
        'Controlla le colonne markup/markup_grande dei DB e risincronizza i metaobject.'
      );
      return true;
    }

    /* ── utils ── */
    _esc(s) {
      return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
    /**
     * URL di una THUMBNAIL: chiede a Shopify il derivato invece del file intero.
     *
     * Le immagini profilo sono master 2000×2000 (~80-200 KB l'una) perché servono al
     * renderer headless dei kit (canvas 2000). In gallery però la miniatura è 116 px CSS
     * — con `transform: scale(2)` e retina servono ~470 px reali: scaricare 2000 px
     * significa buttare ~18× i pixel utili, moltiplicati per ~500 profili in pagina.
     *
     * NON usarla per la texture del render (`_loadFrameImg`/`_loadPass*Img`): lì il file
     * intero serve davvero.
     */
    /* Delega alla SSOT in configuratore-core.js (`FOTORECORD.core.thumbUrl`), caricato
       prima di questo file da tutti e tre i consumatori: tema, harness di parita'
       (parity-configuratori.node.js) e app preventivi (configura_preventivo.html).
       Se mancasse, l'URL torna intatto: immagine pesante, mai un crash — e NON una
       seconda copia della formula, che e' esattamente cio' che il guardrail #13 vieta. */
    _thumbUrl(url, w = 500) {
      const core = window.FOTORECORD && window.FOTORECORD.core;
      return core && core.thumbUrl ? core.thumbUrl(url, w) : String(url ?? '');
    }
    /**
     * Adegua il BUFFER del canvas alla dimensione reale a schermo (HiDPI).
     *
     * Il markup dichiara `width="400"` ma il CSS lo stira a ~580 px (e il doppio su
     * retina): il browser ingrandisce 400 px su 580+, quindi l'anteprima è sfocata su
     * ogni dispositivo. Qui il buffer diventa 400×k e `_canvasCtx` scala il contesto di
     * k, così il sistema di coordinate LOGICO resta 400: la composizione non cambia di
     * un pixel, cambia solo la densità.
     *
     * Va chiamata solo dalla live-view. Il renderer headless dei kit imposta da sé
     * `canvas.width` (canvasSize 2000) e non passa di qui: senza `dataset.dpr`,
     * `_canvasCtx` usa k=1 e il disegno resta identico a prima — i 163 kit già online
     * non cambiano.
     */
    _fitCanvas(canvas) {
      if (!canvas) return;
      const dprDisp = window.devicePixelRatio || 1;
      const cssW    = canvas.clientWidth || CVS_W;
      // cap a 3: oltre, il peso del buffer cresce senza differenza visibile
      const k = Math.max(1, Math.min((cssW * dprDisp) / CVS_W, 3));
      const w = Math.round(CVS_W * k), h = Math.round(CVS_H * k);
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
      canvas.dataset.dpr = String(k);
    }
    /**
     * Contesto 2D + dimensione LOGICA del canvas (vedi _fitCanvas).
     * Senza `dataset.dpr` (headless kit, preventivi) → k=1 e W/H = canvas.width/height:
     * comportamento storico invariato.
     */
    _canvasCtx(canvas) {
      const ctx = canvas.getContext('2d');
      const k   = parseFloat(canvas.dataset?.dpr) || 1;
      ctx.setTransform(k, 0, 0, k, 0, 0);
      return { ctx, W: (canvas.width || CVS_W) / k, H: (canvas.height || CVS_H) / k };
    }
    /* L'ombra della cornice posata sul fondo: la stessa delle immagini del sito
       (FOTORECORD.core.ombraCornice, owner composito_parete.OMBRA_STRATI). Disegna solo
       l'ombra del rettangolo; il riempimento sotto il pezzo resta a chi chiama. Senza il
       core (caricato prima di questo file da tema, banco e totem) l'anteprima resta senza
       ombra e lo dice in console: mai un'ombra di riserva diversa da quella vera. */
    _ombraCornice(ctx, x, y, w, h) {
      const core = window.FOTORECORD && window.FOTORECORD.core;
      if (core && core.ombraCornice) return core.ombraCornice(ctx, x, y, w, h);
      if (!ConfiguratoreBase._avvisoOmbra) {
        ConfiguratoreBase._avvisoOmbra = true;
        console.warn('[configuratore] configuratore-core.js assente: anteprima senza ombra');
      }
    }
    _debounce(key, fn, delay = 300) {
      clearTimeout(this._debounceTimers[key]);
      this._debounceTimers[key] = setTimeout(fn, delay);
    }
    _round(v, d) { return +parseFloat(v).toFixed(d); }
    _fmt(n) { return FR.fmtNumero(n); }      // la parte numerica: chi chiama mette «€ »

    /* Riga «Spedizione — calcolata al checkout · Italia € 12,90» sotto il prezzo. Dal
       12/09/2026 il box mostra il prezzo del PRODOTTO, come ogni altra scheda e come il
       carrello: la spedizione la aggiunge Shopify al checkout, sull'indirizzo vero (tariffa
       piatta Italia = PRICING_SSOT.SPEDIZIONE; l'UE ha la sua). Prima il totale la sommava
       in pagina — giusto solo per l'Italia, e in fascia extra-large il «+12,90» era proprio
       sbagliato (la regola in price_logic dice 150, e finisce nel preventivo). La riga si
       nasconde dove non c'e' un prezzo (configurazione incompleta, profilo esaurito) e in
       solo-preventivo, dove la spedizione la scrive il preventivo. */
    _rigaSpedizione(visibile) {
      const row = document.getElementById(this.p + '-inline-recap-shipping');
      if (!row) return;
      const amount = document.getElementById(this.p + '-shipping-amount');
      /* montature e pannelli hanno un _fmt che mette gia' il simbolo */
      const euro = (n) => { const t = this._fmt(n); return t.startsWith('€') ? t : `€ ${t}`; };
      /* dal 19/10/2026 anche la soglia della spedizione gratuita (PRICING_SSOT, ADR 2026-10-19) */
      const soglia = this.PRICING.SOGLIA_SPEDIZIONE_GRATUITA;
      if (amount) amount.textContent = euro(this.PRICING.SPEDIZIONE) +
        (soglia ? ` · gratuita da ${euro(soglia).replace(/,00$/, '')}` : '');
      row.style.display = visibile ? '' : 'none';
    }
    /* La nota dell'imballo rinforzato, una per tutti i configuratori con una cornice: dice
       quanto e perche' — il formato inserito (l'opera) oltre la soglia (ADR 2026-10-30). */
    _notaImballo(lordo) {
      const t = this._fmt(lordo);
      const euro = t.startsWith('€') ? t : `€ ${t}`;
      return `Imballo rinforzato, formato oltre ${this.PRICING.SOGLIA_SUPPLEMENTO_CM} cm: + ${euro}`;
    }
    /**
     * Codice prodotto mostrato al cliente = id_db (CLAUDE.md § Identificatore unico, 19/07/2026).
     * Nei dati profilo (configuratore-aste-data.js) l'id_db E' gia' il campo sku; il suffisso
     * "| 152311-ppo-legno-anticato" del campo nome e' l'id semantico, chiave interna dei CSV, e
     * non va esposto — per questo qui NON si estrae piu' dal nome. toIdDb e' idempotente su un
     * id_db e normalizza le sorgenti ancora trattinate (metaobject non migrati).
     * ATTENZIONE montature: il loro id_db pubblico e' 'montatura' + toIdDb(sku), non toIdDb(sku)
     * — se un giorno la classe Montature mostra il codice, deve fare override, non ereditare.
     */
    _profiloCodice(p) {
      if (!p || !p.sku) return '—';
      const core = window.FOTORECORD && window.FOTORECORD.core;
      return (core && core.toIdDb) ? core.toIdDb(p.sku) : p.sku;
    }

    /**
     * Quanto pesa ogni componente nel prezzo, IVA inclusa — per i summary degli accordion
     * (detail-accordion.js), che sono il riepilogo della configurazione da quando la tabella
     * sopra il prezzo non c'e' piu' (11/09/2026). Chiavi: profilo, fronte, retro, passCartone,
     * passLegno, stampa, accessori. Un valore null = componente compreso nel prezzo o non
     * prezzato a parte (le clip delle montature); `netti` null = nessun prezzo da mostrare
     * (dati incompleti, fascia solo-preventivo).
     * Si PASSANO gli addendi gia' calcolati dal motore, non se ne ricalcola nessuno
     * (guardrail §13): il totale resta l'unica somma. Evento e variabile globale senza
     * prefisso: in pagina c'e' un solo configuratore, e la sezione `opera-cornice-detail`
     * ascolta `caoa:cornice-selected` anche sulle pagine aste e montature.
     */
    _publishPrezziComponenti(netti) {
      const iva = 1 + this.PRICING.IVA;
      let lordi = null;
      if (netti) {
        lordi = {};
        Object.keys(netti).forEach(k => { lordi[k] = (netti[k] == null) ? null : netti[k] * iva; });
      }
      window._FOTORECORD_PREZZI_COMPONENTI = lordi;
      document.dispatchEvent(new CustomEvent('fotorecord:prezzi-componenti', { detail: lordi }));
    }

    /**
     * Profilo su cui APRIRE il configuratore: il primo DISPONIBILE della lista.
     *
     * Aprendo sul primo e basta, se quello è esaurito il configuratore nasce con
     * "Profilo non disponibile" e la CTA già su "Richiedi disponibilità": sembra rotto
     * prima ancora che il cliente tocchi qualcosa. Se sono esauriti TUTTI si ripiega sul
     * primo assoluto — lì il badge e l'avviso spiegano da soli cosa sta succedendo.
     *
     * Sta in base perché il difetto era lo stesso in quattro punti (aste, montature,
     * opere init, opere cambio-modo): quattro copie della stessa riga sono quattro
     * occasioni di correggerne tre.
     *
     * NON passa di qui il profilo BLOCCATO (pagina prodotto) né il deep-link da kit/hub,
     * ed è corretto: lì il profilo lo ha scelto il cliente o il link, esaurito o no.
     */
    _primoProfiloUtile(list) {
      const l = list || this.state.profili || [];
      if (!l.length) return null;
      return l.find(p => p.disponibile !== false) || l[0];
    }

    /**
     * Codice id_db di un COMPONENTE (stampa, fronte, retro, passepartout, accessorio).
     * I componenti non hanno un id_db memorizzato: e' derivato dal loro sku, che e'
     * l'handle del metaobject (a sua volta l'id semantico del record nel DB di origine).
     * Vale la stessa regola dei profili — al cliente non esce mai un id trattinato.
     */
    _componenteCodice(c) {
      if (!c || !c.sku) return '—';
      const core = window.FOTORECORD && window.FOTORECORD.core;
      return (core && core.toIdDb) ? core.toIdDb(c.sku) : c.sku;
    }

    /**
     * Lookup per sku TOLLERANTE alle due scritture (id_db vs handle trattinato),
     * normalizzando ENTRAMBI i lati con core.toIdDb. Da usare SOLO sul confine URL
     * (_applyUrlParams): lì lo sku arriva da un altro produttore (kit, quiz, campagna,
     * link scritto a mano) e un confronto grezzo fallisce IN SILENZIO — il parametro
     * viene ignorato e il configuratore resta sul default, senza errori (visto il
     * 18/07/2026 sul deep-link vetro dei kit pannelli). I find interni agli event
     * handler restano grezzi: confrontano valori emessi dalla stessa lista.
     * Chi usa l'item trovato deve salvare in config il suo sku CANONICO (item.sku),
     * mai il parametro raw.
     */
    _findBySku(list, sku) {
      if (!sku) return null;
      const core = window.FOTORECORD && window.FOTORECORD.core;
      const norm = (s) => (core && core.toIdDb) ? core.toIdDb(String(s || '')) : String(s || '');
      const target = norm(sku);
      /* target vuoto DOPO la normalizzazione (parametro di soli caratteri non alfanumerici,
         es. '%20', '--'): rifiuta. Senza questo guard matcherebbe la voce "Nessuno"
         (sku '' → norm '') deselezionando fronte/retro in silenzio, con effetto sul prezzo. */
      if (!target) return null;
      return (list || []).find(x => x && norm(x.sku) === target) || null;
    }

    /**
     * Fronte di DEFAULT = il VETRO. La copy di tutte e 4 le section dice "il vetro è la
     * scelta standard", ma fino al 18/07/2026 il default reale era il CRILEX: cablato
     * nel costruttore dei pannelli e, negli altri tre, primo in lista per puro ordine
     * alfabetico degli handle metaobject ('crilex-…' < 'vetro-…'). Selezione per `tipo`
     * (materiale), NON per handle: le chiavi cablate invecchiano in silenzio — è lo
     * stesso modo in cui si è rotto il deep-link vetro dei kit.
     */
    /* WCAG 2.1.1 + 4.1.2 — le griglie profilo sono dichiarate role="listbox" nel
       Liquid, ma il JS le riempie di <div> nudi: senza role="option" uno screen reader
       annuncia la listbox VUOTA (peggio che non dichiararla), e senza tabindex la
       scelta del profilo — l'azione centrale d'acquisto su ~530 pagine — si fa solo
       col mouse. Si normalizza il CONTENITORE dopo ogni render, un punto solo per
       tutti e quattro i configuratori, invece di toccare le cinque stringhe template
       che lo riempiono. Roving tabindex: un solo item tabbabile, le frecce spostano. */
    /* Immagine della card opzione, o un segnaposto della STESSA misura quando manca
       (l'opzione «Nessuno/Nessuna», «Solo stampa», un materiale senza miniatura): senza,
       la card era piu' bassa delle altre e pareva un'opzione di serie B. */
    _optionImg(url, lazy) {
      if (url) return `<img class="${this.p}-option-img" src="${this._esc(this._thumbUrl(url))}" alt=""${lazy ? ' loading="lazy"' : ''}>`;
      return `<span class="${this.p}-option-img ${this.p}-option-img--vuota" aria-hidden="true"></span>`;
    }

    /* Riclic sull'opzione scelta = la toglie. Dal 12/09/2026 sostituisce la card
       «Nessuno/Nessuna» su stampa, fronte e retro: stesso gesto dei passepartout
       («Riclicca … per rimuoverlo»). Un radio nativo non si deseleziona: si guarda com'era
       PRIMA del clic (pointerdown sulla label, che poi attiva l'input) e, se era gia'
       scelto, al click si spegne e si chiama `onDeseleziona`. Da tastiera: Spazio o
       Invio sull'opzione scelta. Il `change` normale resta per la selezione. */
    _abilitaDeseleziona(root, name, onDeseleziona) {
      const input = (el) => el.closest && el.closest('label') && el.closest('label').querySelector(`input[name="${name}"]`);
      root.addEventListener('pointerdown', e => {
        const inp = input(e.target);
        if (inp) inp._eraScelto = inp.checked;
      });
      root.addEventListener('click', e => {
        if (e.target.name !== name || !e.target._eraScelto) return;
        e.target._eraScelto = false;
        e.target.checked = false;
        onDeseleziona();
      });
      root.addEventListener('keydown', e => {
        if (e.target.name !== name || !e.target.checked) return;
        if (e.key !== ' ' && e.key !== 'Enter') return;
        e.preventDefault();
        e.target.checked = false;
        onDeseleziona();
      });
    }

    /* max-height = N righe di celle, misurate (cella quadrata + row-gap + padding:
       box-sizing border-box nei root). N lo dice il CSS della griglia (`--gallery-righe`,
       che cambia col breakpoint insieme alle colonne), GALLERY_RIGHE se non lo dichiara.
       Legato una volta per griglia; il ResizeObserver ricalcola quando la colonna cambia
       larghezza — e quindi anche quando il breakpoint cambia colonne e righe. Griglia
       vuota (solo il messaggio «nessun profilo»): nessun limite. */
    _limitaRighe(grid) {
      const applica = () => {
        const cella = Array.from(grid.children).find(el =>
          Array.from(el.classList).some(c => c.endsWith('-gallery-item')));
        if (!cella || !cella.offsetHeight) { grid.style.maxHeight = ''; return; }
        const cs  = getComputedStyle(grid);
        const righe = parseInt(cs.getPropertyValue('--gallery-righe'), 10) || GALLERY_RIGHE;
        const gap = parseFloat(cs.rowGap) || 0;
        const pad = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
        grid.style.maxHeight = Math.round(righe * cella.offsetHeight + (righe - 1) * gap + pad) + 'px';
      };
      applica();
      if (!grid._limitaRigheRO && window.ResizeObserver) {
        grid._limitaRigheRO = new ResizeObserver(applica);
        grid._limitaRigheRO.observe(grid);
      }
    }

    _abilitaListbox(grid) {
      if (!grid) return;
      this._limitaRighe(grid);
      /* Solo i figli DIRETTI della griglia, e solo con una classe che FINISCE per
         `-gallery-item`: `[class*="-gallery-item"]` matcherebbe per sottostringa e
         prenderebbe anche `casm-gallery-item-img` / `-placeholder`, che stanno DENTRO
         la cella — 553 celle diventavano 1106 option, annidate l'una nell'altra. */
      const items = Array.from(grid.children).filter(el =>
        Array.from(el.classList).some(c => c.endsWith('-gallery-item')));
      if (!items.length) return;

      let attivo = items.findIndex(el => el.classList.contains('selected'));
      if (attivo < 0) attivo = 0;
      items.forEach((el, i) => {
        el.setAttribute('role', 'option');
        el.setAttribute('aria-selected', el.classList.contains('selected') ? 'true' : 'false');
        el.tabIndex = (i === attivo) ? 0 : -1;
        /* il title c'e' gia' su ogni cella e porta nome + misura: e' il nome
           accessibile migliore disponibile senza toccare i template. */
        if (!el.getAttribute('aria-label') && el.title) el.setAttribute('aria-label', el.title);
      });

      /* La selezione ridisegna la griglia: i nodi di prima non esistono piu' e il
         focus tornerebbe al body, sbalzando in cima chi naviga da tastiera. Se la
         scelta e' arrivata da tastiera, il focus torna sulla cella scelta. */
      if (grid.dataset.frRifocalizza) {
        delete grid.dataset.frRifocalizza;
        if (items[attivo]) items[attivo].focus();
      }

      if (grid.dataset.frListbox) return;   /* handler agganciato una volta sola */
      grid.dataset.frListbox = '1';
      grid.addEventListener('keydown', e => {
        const opzioni = Array.from(grid.querySelectorAll(':scope > [role="option"]'));
        if (!opzioni.length) return;
        const corrente = document.activeElement && document.activeElement.closest('[role="option"]');
        const qui = opzioni.indexOf(corrente);
        if (qui < 0) return;
        let dest = null;
        switch (e.key) {
          case 'ArrowRight': case 'ArrowDown': dest = Math.min(qui + 1, opzioni.length - 1); break;
          case 'ArrowLeft':  case 'ArrowUp':   dest = Math.max(qui - 1, 0); break;
          case 'Home':                         dest = 0; break;
          case 'End':                          dest = opzioni.length - 1; break;
          case 'Enter': case ' ': case 'Spacebar':
            e.preventDefault();
            grid.dataset.frRifocalizza = '1';   /* il render che segue rimette il focus qui */
            opzioni[qui].click();   /* stessa via del mouse: i listener sono delegati */
            return;
          default: return;
        }
        e.preventDefault();
        opzioni.forEach((el, i) => { el.tabIndex = (i === dest) ? 0 : -1; });
        opzioni[dest].focus();
      });
    }

    /* Vincolo Crilex: la regola vive in FR.vincoloCrilex (configuratore-pricing.js),
       condivisa col quiz dal 21/09/2026. Qui si passa solo il fronte dello stato. */
    _vincoloCrilex(base, altezza) {
      const fronte = this.state && this.state.config && this.state.config.fronte;
      return FR.vincoloCrilex(fronte, base, altezza);
    }

    _defaultFronte() {
      const reali = (this.state.fronti || []).filter(f => f && f.sku);
      return reali.find(f => String(f.tipo || '').toLowerCase().includes('vetro')) || reali[0] || null;
    }
    /* Retro di default: il primo SENZA limite di formato — cioè "da parete", non "da tavolo".
       Non si può prendere `retri[0]`: quell'ordine arriva da `shop.metaobjects…values` e non è
       garantito. Bastava che uscisse prima il "da tavolo" perché ogni montatura e ogni pannello
       nascessero col piedistallo, e `updateRetroAvailability` non se ne accorge: sui formati
       piccoli (il default è 20×20) il tavolo ci sta, quindi non c'è niente da riparare.
       Il limite lo dichiara il dato (maxBase/maxAltezza dal metaobject), non il nome dello sku. */
    _defaultRetro() {
      const reali = (this.state.retri || []).filter(r => r && r.sku);
      return reali.find(r => (r.maxBase || 999) >= 999 && (r.maxAltezza || 999) >= 999) || reali[0] || null;
    }
    _shadeColor(hex, amt) {
      const n = parseInt(hex.replace('#', ''), 16);
      const r = Math.min(255, Math.max(0, (n >> 16) + amt));
      const g = Math.min(255, Math.max(0, ((n >> 8) & 0xff) + amt));
      const b = Math.min(255, Math.max(0, (n & 0xff) + amt));
      return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
    }
    _fallbackColor(colore) {
      return FR.coloreHex(colore, '#8B7355');
    }

    /* ── avvisi artwork ── */
    _artworkNotice(msg, tipo) {
      const el = this.$('artwork-notice');
      if (!el) return;
      if (!msg || tipo === 'clear') { el.style.display = 'none'; el.textContent = ''; el.removeAttribute('data-tipo'); return; }
      el.textContent = msg;
      el.setAttribute('data-tipo', tipo || 'warn');
      el.style.display = 'block';
    }
    /* Avviso (non bloccante) se la risoluzione è bassa per il formato scelto.
       Soglia: 150 ppi sul lato lungo. */
    _checkArtworkResolution(img) {
      const { base, altezza } = this.state.config;
      if (!(base > 0) || !(altezza > 0)) return;
      const longCm     = Math.max(base, altezza);
      const neededLong = Math.round((longCm / 2.54) * 150);
      const haveLong   = Math.max(img.naturalWidth, img.naturalHeight);
      if (haveLong < neededLong * 0.85) {
        this._artworkNotice(`Risoluzione bassa per ${base}×${altezza} cm: la stampa potrebbe risultare poco nitida. Consigliata una foto più grande.`, 'warn');
      } else {
        this._artworkNotice('', 'clear');
      }
    }
    /* Stato UI upload foto: bottone overlay visibile solo con canvas visibile e
       nessuna foto; footer (hint + ✕) solo a foto caricata. Null-safe (renderer headless). */
    _syncArtworkUI() {
      const btn    = this.$('artwork-btn');
      const footer = this.$('artwork-footer');
      const canvas = this.$('live-canvas');
      const has    = !!this._artworkImg;
      const canvasVisible = !!canvas && canvas.style.display !== 'none';
      if (btn)    btn.style.display    = (!has && canvasVisible) ? '' : 'none';
      if (footer) footer.style.display = has ? '' : 'none';
    }
    /* Validazione + caricamento della foto del cliente (JPG/PNG, max 20 MB).
       File ORIGINALE conservato per la stampa; l'immagine per l'anteprima. */
    _onArtworkFile(file, inputEl) {
      if (!file) return;
      if (!/^image\/(jpeg|png)$/.test(file.type)) {
        this._artworkNotice('Carica la foto in formato JPG o PNG.', 'error');
        if (inputEl) inputEl.value = '';
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        this._artworkNotice('La foto supera i 20 MB: invia il file originale via email a info@fotorecord.it indicando i dati dell’ordine.', 'error');
        if (inputEl) inputEl.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onload = ev => {
        const img = new Image();
        img.onload = () => {
          this._artworkImg  = img;
          this._artworkFile = file;
          this._checkArtworkResolution(img);
          this.updateLiveView();
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    }
    /* Cablaggio standard upload foto: input + bottone overlay + tap sul canvas + ✕. */
    _wireArtworkUpload() {
      const artworkInput = this.$('artwork-input');
      if (artworkInput) {
        artworkInput.addEventListener('change', e => this._onArtworkFile(e.target.files?.[0], artworkInput));
      }
      this.$('artwork-btn')?.addEventListener('click', () => artworkInput?.click());
      this.$('live-canvas')?.addEventListener('click', () => artworkInput?.click());
      this.$('artwork-clear')?.addEventListener('click', () => {
        this._artworkImg  = null;
        this._artworkFile = null;
        if (artworkInput) artworkInput.value = '';
        this._artworkNotice('', 'clear');
        this.updateLiveView();
      });
    }
    /* Cattura l'anteprima canvas come PNG (property "_anteprima"). I loader CDN
       usano crossOrigin → canvas non tainted; se tainted, toBlob lancia → null. */
    _capturePreviewBlob() {
      return new Promise(resolve => {
        const canvas = this.$('live-canvas');
        if (!canvas) return resolve(null);
        try {
          canvas.toBlob(b => resolve(b), 'image/png');
        } catch (err) {
          console.warn(`[${this.logTag}] anteprima non catturabile (CORS?)`, err);
          resolve(null);
        }
      });
    }

    /* ── quantità ── */
    changeQty(delta) {
      const input = this.$('quantita');
      if (!input) return;
      const val = Math.max(1, Math.min(99, (parseInt(input.value) || 1) + delta));
      input.value = val;
      this.state.config.quantita = val;
      this.calculateLive();
    }
    onQtyChange() {
      const input = this.$('quantita');
      if (!input) return;
      let val = parseInt(input.value) || 1;
      val = Math.max(1, Math.min(99, val));
      input.value = val;
      this.state.config.quantita = val;
      this.calculateLive();
    }

    /* ── preset dimensioni ── */
    _syncPresets(base, altezza) {
      document.querySelectorAll('.' + this.p + '-preset').forEach(btn => {
        const match = parseFloat(btn.dataset.base) === base
                      && parseFloat(btn.dataset.alt) === altezza;
        btn.classList.toggle('is-active', match);
      });
    }

    /* ── STAMPA (macro-tipi 'foto'/'tela'; record risolto per dimensioni) ── */
    _stampaSupportsTela() { return false; }   // solo aste (battente)
    _stampaTelaDisabled() {
      const battente = this.state.config.profilo?.battenteMm || 0;
      return battente > 0 && battente < BATTENTE_MIN_TELA_MM;
    }
    _resolveStampa(tipo) {
      if (!tipo) return null;
      const list = this.state.stampe.filter(s => s.tipo === tipo);
      if (!list.length) return null;
      if (tipo === 'tela') return list[0];
      // foto: small se entrambi i lati rientrano nei max, altrimenti pro
      const { base, altezza } = this.state.config;
      const small = list.find(s => (s.maxBase || 999) < 999);
      const pro   = list.find(s => (s.maxBase || 999) >= 999) || list[list.length - 1];
      if (!small) return pro;
      const isSmall = base && altezza
        && ((base <= small.maxBase && altezza <= small.maxAlt) ||
            (altezza <= small.maxBase && base <= small.maxAlt));
      return isSmall ? small : pro;
    }
    renderStampaOptions() {
      const el = this.$('stampa-options');
      if (!el) return;
      const p = this.p;
      const hasFoto = this.state.stampe.some(s => s.tipo === 'foto');
      const hasTela = this._stampaSupportsTela() && this.state.stampe.some(s => s.tipo === 'tela');
      if (!hasFoto && !hasTela) {
        el.innerHTML = `<p class="${p}-hint">Stampa non disponibile al momento.</p>`;
        return;
      }
      const selected = this.state.config.stampaTipo;
      const fotoInst = this.state.stampe.find(s => s.tipo === 'foto');
      const telaInst = this.state.stampe.find(s => s.tipo === 'tela');
      /* Niente card «Nessuna» (12/09/2026): la stampa e' opzionale, si aggiunge cliccando
         e si toglie ricliccando (_abilitaDeseleziona). */
      const opts = [];
      if (hasFoto) opts.push({ tipo: 'foto', label: 'Stampa fotografica', subtitle: (fotoInst && fotoInst.descrizioneBreve) || '', imgUrl: (fotoInst && fotoInst.immagineUrl) || '' });
      if (hasTela) opts.push({ tipo: 'tela', label: 'Stampa su tela',     subtitle: (telaInst && telaInst.descrizioneBreve) || '', imgUrl: (telaInst && telaInst.immagineUrl) || '' });

      const telaLocked = hasTela && this._stampaTelaDisabled();

      el.innerHTML = opts.map(o => {
        const sel    = o.tipo === selected;
        const isTela = o.tipo === 'tela';
        const dis    = isTela && telaLocked;
        const value  = o.tipo;
        const cls    = `${p}-option${sel ? ' selected' : ''}${dis ? ` ${p}-option--disabled` : ''}`;
        const img    = this._optionImg(o.imgUrl, true);
        return `<label class="${cls}">
          <input type="radio" name="${p}-stampa" value="${this._esc(value)}"${sel ? ' checked' : ''}${dis ? ' disabled' : ''}>
          <span class="${p}-option-card">
            ${img}
            <span class="${p}-option-dot"></span>
            <span class="${p}-option-text">
              <strong>${this._esc(o.label)}</strong>
              <span>${this._esc(o.subtitle)}</span>
            </span>
          </span>
        </label>`;
      }).join('');
    }
    selectStampaTipo(tipo) {
      if (tipo === 'tela' && this._stampaTelaDisabled()) {
        this.state.config.stampaTipo = null;
        this.state.config.stampa     = null;
      } else {
        this.state.config.stampaTipo = tipo;
        this.state.config.stampa     = this._resolveStampa(tipo);
      }
      this._updateStampaLegend();
      this.renderStampaOptions();
      this.calculateLive();
      this.updateLiveView();   // la stampa (lucida/tela) incide sull'anteprima: ridisegna al toggle
    }
    _updateStampaLegend() {
      this._dispatchStampaEvent();
      const el = this.$('legend-stampa');
      if (!el) return;
      const s = this.state.config.stampa;
      el.textContent = s ? `— ${s.nome}` : '';
    }
    /* La stampa ha il suo accordion (sezione `material-detail` con evento `<p>:stampa-changed`):
       il record `stampa_configuratore` porta gia' sku, nome, tipo (foto|tela), descrizione e
       miniatura, cioe' lo stesso payload dei materiali. null = nessuna stampa scelta. */
    _dispatchStampaEvent() {
      const s = this.state.config.stampa || null;
      window['_' + this.logTag + '_STAMPA'] = s;
      document.dispatchEvent(new CustomEvent(this.p + ':stampa-changed', { detail: s }));
    }
    _updateStampaState() {
      const tipo = this.state.config.stampaTipo;
      if (tipo === 'tela' && this._stampaTelaDisabled()) {
        this.state.config.stampaTipo = null;
        this.state.config.stampa     = null;
      } else if (tipo) {
        this.state.config.stampa = this._resolveStampa(tipo);
      }
      this._updateStampaLegend();
      this.renderStampaOptions();
    }

    /* ── disegno condiviso ── */
    _draw9Slice(ctx, img, slicePx, dx, dy, dw, dh, destSlice, tileImg) {
      const sw = img.naturalWidth, sh = img.naturalHeight;
      const s = slicePx, d = destSlice;
      /* overlap di 1px: dx+d/dx+dw-d ecc. sono coordinate frazionarie, e due
         drawImage adiacenti che si toccano esattamente lasciano una fessura
         (anti-aliasing calcolato indipendentemente per ciascuna chiamata).
         I lati vengono allargati di 1px verso gli angoli, poi gli angoli sono
         ridisegnati sopra per coprire l'eventuale sovrapposizione. */
      const o = 1;
      if (tileImg && tileImg.naturalWidth > 0) {
        /* Lati da una porzione REALE del profilo, ripetuta a scala fisica.
           Il master 20×20 ha una banda utile di (200 − 2×sezione) mm: su un profilo
           largo la banda è corta e i lati vengono stirati fino a 8× — su un motivo
           ornamentale lo si vede tutto. La striscia è verticale e larga quanto la
           faccia del profilo: la scala si ricava da lei, nessun dato da tenere
           allineato altrove. Gli angoli restano dal master (il giunto a 45° con la
           luce corretta esiste solo lì). */
        this._drawTiledBand(ctx, tileImg, dx+d-o,   dy,        dw-2*d+2*o, d,          'top');
        this._drawTiledBand(ctx, tileImg, dx+d-o,   dy+dh-d,   dw-2*d+2*o, d,          'bottom');
        this._drawTiledBand(ctx, tileImg, dx,       dy+d-o,    d,          dh-2*d+2*o, 'left');
        this._drawTiledBand(ctx, tileImg, dx+dw-d,  dy+d-o,    d,          dh-2*d+2*o, 'right');
      } else {
        ctx.drawImage(img, s,    0,    sw-2*s, s,      dx+d-o,  dy,       dw-2*d+2*o, d);
        ctx.drawImage(img, s,    sh-s, sw-2*s, s,      dx+d-o,  dy+dh-d,  dw-2*d+2*o, d);
        ctx.drawImage(img, 0,    s,    s,      sh-2*s, dx,      dy+d-o,   d,          dh-2*d+2*o);
        ctx.drawImage(img, sw-s, s,    s,      sh-2*s, dx+dw-d, dy+d-o,   d,          dh-2*d+2*o);
      }
      ctx.drawImage(img, 0,    0,    s, s, dx,       dy,       d, d);
      ctx.drawImage(img, sw-s, 0,    s, s, dx+dw-d,  dy,       d, d);
      ctx.drawImage(img, sw-s, sh-s, s, s, dx+dw-d,  dy+dh-d,  d, d);
      ctx.drawImage(img, 0,    sh-s, s, s, dx,       dy+dh-d,  d, d);
    }
    /* Una fascia laterale coperta ripetendo la striscia del profilo.
       La striscia ha la larghezza = faccia del profilo (asse u, da ESTERNO a INTERNO)
       e l'altezza = porzione di barra (asse v, lungo il lato). Ogni lato applica la
       trasformazione che porta (u,v) al proprio orientamento, così l'ombreggiatura
       trasversale non viene mai ribaltata. `ctx.transform` moltiplica (non azzera) la
       matrice corrente: il fattore dpr impostato da _fitCanvas resta valido. */
    _drawTiledBand(ctx, tile, x, y, w, h, side) {
      if (w <= 0 || h <= 0) return;
      const tw = tile.naturalWidth, th = tile.naturalHeight;
      if (!tw || !th) return;
      const vert = (side === 'left' || side === 'right');
      const k    = (vert ? w : h) / tw;          // px destinazione per px striscia
      if (!(k > 0)) return;
      ctx.save();
      ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
      if      (side === 'top')    ctx.transform( 0,  k, k, 0, x,     y    );
      else if (side === 'bottom') ctx.transform( 0, -k, k, 0, x,     y + h);
      else if (side === 'left')   ctx.transform( k,  0, 0, k, x,     y    );
      else                        ctx.transform(-k,  0, 0, k, x + w, y    );
      /* Fase centrata: la porzione di barra usata è quella centrale della striscia,
         così i due angoli dello stesso lato sono simmetrici invece che uno intero e
         uno tronco. Overlap di 1px destinazione tra copie adiacenti: stessa fessura
         da anti-aliasing del 9-slice. */
      const lung  = (vert ? h : w) / k;          // lunghezza da coprire, in unità striscia
      /* n = copie STRETTAMENTE necessarie: con la fase centrata n·th ≥ lung basta a
         coprire il lato. Una copia in più aggiungerebbe una giunzione dentro la fascia
         anche quando la striscia da sola bastava — ed è la giunzione che si vede. */
      const n     = Math.max(1, Math.ceil(lung / th));
      const start = (lung - n * th) / 2;
      const ov    = 1 / k;
      /* Copie alterne SPECCHIATE. Una striscia è una foto di barra reale, non un motivo
         perfettamente periodico: tagliarla su un periodo non basta (misurato sul primo
         profilo: la giunzione migliore resta 11,6× la variazione tra righe adiacenti,
         cioè visibile). A specchio ogni giunzione combacia per costruzione — copia pari
         e copia dispari si toccano sulla STESSA riga dell'immagine — al prezzo di un
         motivo ribaltato ogni due passate, che su un ornamento legge come simmetria.
         Con n = 1 (lato più corto della striscia) non si specchia niente. */
      for (let i = 0; i < n; i++) {
        const v0 = start + i * th;
        if (i % 2) {
          ctx.save();
          ctx.translate(0, v0 + th); ctx.scale(1, -1);
          ctx.drawImage(tile, 0, 0, tw, th, 0, 0, tw, th + ov);
          ctx.restore();
        } else {
          ctx.drawImage(tile, 0, 0, tw, th, 0, v0, tw, th + ov);
        }
      }
      ctx.restore();
    }
    /* La cornice disegnata col solo colore (gradiente per lato) finche' l'immagine del profilo
       non e' arrivata, o quando non c'e'. Metodo a parte dal 10/10/2026: lo riusa il disegno
       delle cornici multiple (assets/prodotto-multipla-stampe.js), una cornice per pezzo.
       (x, y, w, h) = esterno della cornice, p = larghezza della fascia, in px. */
    _drawCorniceFallback(ctx, frameColor, x, y, w, h, p) {
      const light = this._shadeColor(frameColor, 40);
      const dark  = this._shadeColor(frameColor, -35);
      ctx.save();
      const gTop = ctx.createLinearGradient(0, y, 0, y + p);
      gTop.addColorStop(0, light); gTop.addColorStop(1, frameColor);
      ctx.fillStyle = gTop;
      ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x+w, y); ctx.lineTo(x+w-p, y+p); ctx.lineTo(x+p, y+p); ctx.closePath(); ctx.fill();
      const gBot = ctx.createLinearGradient(0, y+h-p, 0, y+h);
      gBot.addColorStop(0, frameColor); gBot.addColorStop(1, dark);
      ctx.fillStyle = gBot;
      ctx.beginPath(); ctx.moveTo(x, y+h); ctx.lineTo(x+w, y+h); ctx.lineTo(x+w-p, y+h-p); ctx.lineTo(x+p, y+h-p); ctx.closePath(); ctx.fill();
      const gLeft = ctx.createLinearGradient(x, 0, x+p, 0);
      gLeft.addColorStop(0, light); gLeft.addColorStop(1, frameColor);
      ctx.fillStyle = gLeft;
      ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x+p, y+p); ctx.lineTo(x+p, y+h-p); ctx.lineTo(x, y+h); ctx.closePath(); ctx.fill();
      const gRight = ctx.createLinearGradient(x+w-p, 0, x+w, 0);
      gRight.addColorStop(0, frameColor); gRight.addColorStop(1, dark);
      ctx.fillStyle = gRight;
      ctx.beginPath(); ctx.moveTo(x+w, y); ctx.lineTo(x+w-p, y+p); ctx.lineTo(x+w-p, y+h-p); ctx.lineTo(x+w, y+h); ctx.closePath(); ctx.fill();
      ctx.restore();
    }
    _drawImgCover(ctx, img, x, y, w, h) {
      if (w <= 0 || h <= 0) return;
      const iw = img.naturalWidth, ih = img.naturalHeight;
      const scale = Math.max(w / iw, h / ih);
      const sw = iw * scale, sh = ih * scale;
      ctx.save();
      ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
      ctx.drawImage(img, x + (w - sw) / 2, y + (h - sh) / 2, sw, sh);
      ctx.restore();
    }

    /* ── loaders immagini (CDN Shopify ACAO:* → crossOrigin: canvas non tainted).
          url vuoto = svuota cache; il check url dentro onload/onerror scarta le
          risposte arrivate DOPO un cambio selezione (race su click rapidi). ── */
    _loadEffettoImg(url) {
      url = url || '';
      if (url === this._effettoImgUrl) return;
      this._effettoImgUrl = url;
      this._effettoImg    = null;
      if (!url) { this.updateLiveView(); return; }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload  = () => { if (url !== this._effettoImgUrl) return; this._effettoImg = img;  this.updateLiveView(); };
      img.onerror = () => { if (url !== this._effettoImgUrl) return; this._effettoImg = null; this.updateLiveView(); };
      img.src = url;
    }

    /* ── Effetti superficie (calibrazione FX in cima al modulo). Regione = area
       opera già disegnata. Ordine fisico: stampa (lucida/tela) SOTTO, fronte
       (vetro/crilex) SOPRA — la lastra sta davanti alla stampa. ── */
    _stampaTipoCorrente() {
      const cfg = this.state && this.state.config;
      if (!cfg || !cfg.stampa) return null;
      if (cfg.stampaTipo === 'foto' || cfg.stampaTipo === 'tela') return cfg.stampaTipo;
      return cfg.stampa.conTelaio ? 'tela' : 'foto';
    }
    _drawStampaEffetto(ctx, x, y, w, h, baseCm) {
      const tipo = this._stampaTipoCorrente();
      if (!tipo || !(w > 0) || !(h > 0)) return;
      if (!USE_PROCEDURAL_FX) {
        const url = tipo === 'tela' ? (window.__EFFETTO_TELA_URL || '') : (window.__EFFETTO_LUCIDA_URL || '');
        const img = fxFallbackImg(url, () => this.updateLiveView());
        if (img) { ctx.save(); ctx.globalAlpha = 0.35; ctx.drawImage(img, x, y, w, h); ctx.restore(); }
        return;
      }
      if (tipo === 'tela') fxWeave(ctx, x, y, w, h, baseCm);
      else                 fxReflect(ctx, x, y, w, h, FX.lucida.int, FX.lucida.spr, 0);
    }
    _drawFronteEffetto(ctx, x, y, w, h) {
      if (!this._effettoImg || !(w > 0) || !(h > 0)) return;   // stessa gate del vecchio overlay
      if (!USE_PROCEDURAL_FX) {
        ctx.save(); ctx.globalAlpha = 0.35; ctx.drawImage(this._effettoImg, x, y, w, h); ctx.restore();
        return;
      }
      const crilex = !!(this.state && this.state.config && this.state.config.fronte
                        && this.state.config.fronte.tipo === 'crilex');
      if (crilex) {
        fxReflect(ctx, x, y, w, h, FX.crilex.int, FX.crilex.spr, FX.crilex.streak);
        fxTint(ctx, x, y, w, h, FX.crilex.blu);
      } else {
        fxReflect(ctx, x, y, w, h, FX.vetro.int, FX.vetro.spr, FX.vetro.streak);
      }
    }
    _loadFrameImg(url) {
      url = url || '';
      if (url === this._frameImgUrl) { this.updateLiveView(); return; }
      this._frameImgUrl = url; this._frameImg = null;
      if (!url) { this.updateLiveView(); return; }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload  = () => { if (url !== this._frameImgUrl) return; this._frameImg = img;  this.updateLiveView(); };
      img.onerror = () => { if (url !== this._frameImgUrl) return; this._frameImg = null; this.updateLiveView(); };
      img.src = url;
    }
    /* Striscia del profilo per i lati (vedi _drawTiledBand). Assente sulla quasi
       totalità dei profili: senza, il 9-slice resta quello di sempre. */
    _loadPatternImg(url) {
      url = url || '';
      if (url === this._patternImgUrl) { this.updateLiveView(); return; }
      this._patternImgUrl = url; this._patternImg = null;
      if (!url) { this.updateLiveView(); return; }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload  = () => { if (url !== this._patternImgUrl) return; this._patternImg = img;  this.updateLiveView(); };
      img.onerror = () => { if (url !== this._patternImgUrl) return; this._patternImg = null; this.updateLiveView(); };
      img.src = url;
    }
    _loadPassLegnoImg(url) {
      url = url || '';
      if (url === this._passLegnoImgUrl) { this.updateLiveView(); return; }
      this._passLegnoImgUrl = url; this._passLegnoImg = null;
      if (!url) { this.updateLiveView(); return; }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload  = () => { if (url !== this._passLegnoImgUrl) return; this._passLegnoImg = img;  this.updateLiveView(); };
      img.onerror = () => { if (url !== this._passLegnoImgUrl) return; this._passLegnoImg = null; this.updateLiveView(); };
      img.src = url;
    }
    /* Striscia del passepartout in legno: stessa cura dell'asta, stesso difetto di
       partenza — anche qui il 9-slice stira la banda del master sulla lunghezza del lato. */
    _loadPassLegnoPatternImg(url) {
      url = url || '';
      if (url === this._passLegnoPatternUrl) { this.updateLiveView(); return; }
      this._passLegnoPatternUrl = url; this._passLegnoPatternImg = null;
      if (!url) { this.updateLiveView(); return; }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload  = () => { if (url !== this._passLegnoPatternUrl) return; this._passLegnoPatternImg = img;  this.updateLiveView(); };
      img.onerror = () => { if (url !== this._passLegnoPatternUrl) return; this._passLegnoPatternImg = null; this.updateLiveView(); };
      img.src = url;
    }
    _loadPassCartoneImg(url) {
      url = url || '';
      if (url === this._passCartoneImgUrl) { this.updateLiveView(); return; }
      this._passCartoneImgUrl = url; this._passCartoneImg = null;
      if (!url) { this.updateLiveView(); return; }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload  = () => { if (url !== this._passCartoneImgUrl) return; this._passCartoneImg = img;  this.updateLiveView(); };
      img.onerror = () => { if (url !== this._passCartoneImgUrl) return; this._passCartoneImg = null; this.updateLiveView(); };
      img.src = url;
    }
    _loadClipImg(url) {
      if (!url || url === this._clipImgUrl) return;
      this._clipImgUrl = url;
      this._clipImg    = null;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { this._clipImg = img; this.updateLiveView(); };
      img.src = url;
    }

    /* ── Smusso a 45° con anima a vista (cartoni spessi, es. WhiteCore/Blackcore 1,4 mm) ──
       Frontalmente lo smusso appare come un bordino largo quanto lo spessore del
       cartone, sul filo interno del foro; il suo colore è quello dell'ANIMA:
       bianca (WhiteCore) o nera (Blackcore). I cartoncini sottili (< 1 mm, es.
       Fedrigoni-Fabriano) hanno taglio netto in tinta piena: nessuno smusso.
       Il bordino vive DENTRO la larghezza nominale del passepartout: occupa
       l'ultimo tratto della banda a ridosso del foro, sovrapposto alla tinta.
       Non allarga l'ingombro né restringe l'area opera. */
    _drawPassCartoneBevel(ctx, opX, opY, opW, opH, cartPx, scale) {
      const pc = this.state.config.passCartone;
      const spessMm = pc && pc.spessoreMm ? parseFloat(pc.spessoreMm) : 0;
      if (!(spessMm >= 1) || cartPx <= 0) return;
      const anima = (pc && pc.anima || '').toLowerCase();
      const nera = anima.indexOf('ner') !== -1;   // "Nera"/"nero"
      const coreColor  = nera ? '#1f1e1c' : '#fbfaf6';
      const edgeStroke = nera ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)';
      const bevelPx = Math.min(Math.max(spessMm * 0.1 * scale, 1.25), cartPx * 0.5);
      const bx = opX + cartPx - bevelPx, by = opY + cartPx - bevelPx;
      const bw = opW - 2 * (cartPx - bevelPx), bh = opH - 2 * (cartPx - bevelPx);
      ctx.fillStyle = coreColor;
      ctx.fillRect(bx, by, bw, bevelPx);
      ctx.fillRect(bx, by + bh - bevelPx, bw, bevelPx);
      ctx.fillRect(bx, by + bevelPx, bevelPx, bh - 2 * bevelPx);
      ctx.fillRect(bx + bw - bevelPx, by + bevelPx, bevelPx, bh - 2 * bevelPx);
      ctx.strokeStyle = edgeStroke; ctx.lineWidth = 1;
      ctx.strokeRect(bx + bevelPx, by + bevelPx, bw - 2 * bevelPx, bh - 2 * bevelPx);
    }

    /* ── clip minime per formato (montature/pannelli) ── */
    _updateClipMin() {
      const P = this.PRICING;
      const { base, altezza } = this.state.config;
      const hasDims  = !!(base && altezza);
      const smallFmt = hasDims &&
        Math.min(base, altezza) <= P.SOGLIA_CLIP_LATO_MIN &&
        Math.max(base, altezza) <= P.SOGLIA_CLIP_LATO_MAX;
      const minClips = hasDims ? (smallFmt ? P.N_CLIPS_STD : P.N_CLIPS_GRANDE) : P.N_CLIPS_STD;
      this.state.config.clipMinQty = minClips;
      if (!this.state.config.clipQty || this.state.config.clipQty < minClips) {
        this.state.config.clipQty = minClips;
      }
      const clipItem = (this.state.accessori || []).find(a => (a.categoria || '').toLowerCase().includes('attaccaglie'));
      const accdDetail = { tipo: 'clip', sku: clipItem?.sku || '', nome: clipItem?.nome || 'Clip di fissaggio', nomeBreve: clipItem?.nomeBreve || '', imgUrl: clipItem?.imgUrl || '', materiale: clipItem?.materiale || '', colore: clipItem?.colore || '', descrizioneBreve: clipItem?.descrizioneBreve || '', note: clipItem?.note || '', clipQty: this.state.config.clipQty };
      window['_' + this.logTag + '_ACCESSORI'] = accdDetail;
      document.dispatchEvent(new CustomEvent(this.p + ':clip-changed', { detail: accdDetail }));
    }

    /* ── LOCK PROFILO FALLITO ──
       Il metafield della pagina prodotto punta a un profilo che il configuratore non
       trova (dato disallineato, profilo archiviato, id storico mai tradotto). Non è un
       caso da `warn` e prosegui: si rende visibile lo step profilo, si dice al cliente
       che deve scegliere, e si lascia la CTA disabilitata finché non lo fa — la macchina
       a stati la riabilita da sola alla prima selezione. */
    _segnalaLockFallito(sku) {
      console.error(`[${this.logTag}] lockedProfileSku non trovato tra i profili:`, sku,
                    '— nessuna preselezione: la scelta resta al cliente.');
      const step = document.getElementById(`${this.p}-step-profilo`);
      if (step) step.style.display = '';
      const avviso = document.getElementById(`${this.p}-profilo-lock-error`);
      if (avviso) avviso.style.display = '';
    }

    /* ── CTA via macchina a stati unica (core.ctaState) ── */
    _setCta(opts) {
      const btn = document.getElementById(this.ids.cta);
      if (!btn) return;
      const s = FR.core.ctaState(opts);
      btn.disabled        = s.disabled;
      btn.textContent     = s.text;
      btn.dataset.ctaMode = s.mode;
      if (s.esaurito) btn.dataset.esaurito = '1'; else delete btn.dataset.esaurito;
    }

    /* ── carrello nativo (variante fascia + properties + file) ── */
    _pickBucket(euro) {
      return FR.core.pickBucket(euro, window.SU_MISURA_BUCKETS);
    }
    async _addToCart() {
      const bucket = this._bucket || (this._bucketFallbackEuro ? this._pickBucket(this._bucketFallbackEuro()) : null);
      const pezzi  = this.state.config.quantita || 1;
      if (!bucket || !(bucket.id > 0)) {
        console.error(`[${this.logTag}] add to cart: nessuna fascia di prezzo`);
        return;
      }
      // Step Stampa: se "stampate voi" la foto da stampare è obbligatoria
      if (this.state.config.stampa && !this._artworkFile) {
        this._artworkNotice('Hai scelto la stampa: carica la foto da stampare (JPG/PNG) o, se supera i 20 MB, inviala a info@fotorecord.it.', 'error');
        return;
      }
      const btn = document.getElementById(this.ids.cta);
      if (btn) { btn.disabled = true; btn.textContent = this.ctaBusyText || 'Aggiungendo…'; }
      try {
        const props = this._buildCartProperties();
        // Il file originale (master di stampa) va nell'ordine SOLO se la stampa è scelta:
        // la foto caricata per la sola anteprima, senza stampa, finiva nel carrello come
        // «Foto da stampare» e l'ordine sembrava chiedere una stampa mai pagata (#1372,
        // #1384, #1385 — 08/10/2026). Stessa condizione del quiz (quiz-aste-su-misura.js).
        if (this.state.config.stampa && this._artworkFile) props['Foto da stampare'] = this._artworkFile;
        const anteprima = await this._capturePreviewBlob();
        if (anteprima) props['_anteprima'] = anteprima;                          // mockup di conferma
        await FR.core.addToCart({ items: [{ id: bucket.id, quantity: pezzi, properties: props }] });
      } catch (e) {
        console.error(`[${this.logTag}] add to cart error`, e);
        if (btn) { btn.disabled = false; btn.textContent = 'Aggiungi al carrello'; }
      }
    }

    /* ── form preventivo (montature/pannelli: gdpr + corpo + lead) ── */
    handleFormSubmit(e) {
      const gdprCheck = this.$('check-gdpr');
      if (gdprCheck && !gdprCheck.checked) {
        e.preventDefault();
        gdprCheck.focus();
        return;
      }
      this._buildBodyField();
      if (typeof window.fotorecordLeadConversion === 'function') {
        window.fotorecordLeadConversion(this.leadKey || this.p);
      }
    }

    /* ── banner successo post-submit ── */
    _checkSuccessQuery() {
      if (window.location.search.includes('contact%5Bposted_successfully%5D=true') ||
          window.location.search.includes('contact[posted_successfully]=true')) {
        const banner = this.$('success-banner');
        if (banner) {
          banner.style.display = 'block';
          banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
    _checkSuccessSession(key) {
      if (sessionStorage.getItem(key) !== '1') return;
      sessionStorage.removeItem(key);
      const banner = this.$('success-banner');
      if (banner) { banner.style.display = ''; banner.scrollIntoView({ behavior: 'smooth' }); }
    }

    /* ── MODI DI MONTAGGIO: chip che cambiano modo restando nella pagina ──
       Esiste solo nel configuratore OPERE (l'unico che monta la stessa opera in tre
       modi diversi). Nei tre configuratori pubblici il chip è stato tolto il
       29/07/2026: là era una NAVIGAZIONE verso un'altra pagina prodotto, e con essa
       se ne è andato l'handoff sessionStorage che serviva a non perdere dimensioni e
       foto nel salto. Chi apre "cornici su misura" ha già scelto il tipo. */
    _renderModiMontaggio(currentKey, onPick) {
      const el = this.$('tipo-switch');
      if (!el) return;
      el.innerHTML = MODI_MONTAGGIO.map(t => {
        const cur = t.key === currentKey;
        return `<button type="button" class="cfr-tipo-pill${cur ? ' active' : ''}"
                 data-modo="${this._esc(t.key)}"${cur ? ' aria-current="true"' : ''}>${this._esc(t.label)}</button>`;
      }).join('');
      if (this._modiWired) return;
      this._modiWired = true;
      el.addEventListener('click', e => {
        const btn = e.target.closest('[data-modo]');
        if (!btn) return;
        const modo = btn.dataset.modo;
        if (modo && modo !== this.state.config.tipo) onPick(modo);
      });
    }

    /* Il retro "da tavolo" regge solo formati FINITI (opera + passepartout, misura
       battente — SSOT FR.dimsBattente ↔ price_logic.dims_battente) entro maxBase×
       maxAltezza, anche ruotati. NON sulla sola opera: un 20×25 con pass 3cm
       diventa 26×31 e non sta in piedi (fix 28/08/2026; stessa regola lato server
       in quote_engine._guard_retro_tavolo). Nelle classi senza passepartout
       (montature, pannelli) i campi pass sono undefined → conta la sola opera. */
    _isRetroDisabled(r) {
      const { base, altezza, hasPassCartone, passCartoneLargh, hasPassLegno, passLegno } = this.state.config;
      const ppC = (hasPassCartone && passCartoneLargh) ? passCartoneLargh : 0;
      const ppL = (hasPassLegno && passLegno && passLegno.larghezzaMm) ? passLegno.larghezzaMm : 0;
      /* la regola vive in FR.retroConsentito (configuratore-pricing.js), condivisa col quiz */
      return !FR.retroConsentito(r, base, altezza, ppC, ppL);
    }
    /* Il limite lo dice il dato del retro, non un numero scritto qui. */
    _hintRetroLimite(r) {
      return `Max ${r.maxBase}×${r.maxAltezza} cm finiti (opera + passepartout)`;
    }
  }

  /* ══════════════════════════════════════════════════════════════════════════
     ASTE SU MISURA (casm) — superset: profilo + passepartout + stampa tela +
     attaccaglie. Espone la stessa API del vecchio ConfiguratoreAsteSuMisura
     (renderer headless + configuratore preventivi interno).
     ══════════════════════════════════════════════════════════════════════════ */
  const DEFAULT_BASE = 20, DEFAULT_ALTEZZA = 20;

  class ConfiguratoreAste extends ConfiguratoreBase {

    constructor() {
      super('casm', { logTag: 'CASM', ids: { cta: 'casm-cta' } });
      this.ctaBusyText         = 'Aggiunta al carrello…';
      this._bucketFallbackEuro = () => this._lastCalcResult?.prezzoUnitario;
      this.state = {
        profili:     [],
        fronti:      [],
        retri:       [],
        passLegno:   [],
        passCartone: [],
        accessori:   [],
        stampe:      [],
        filters: { battente: '', colore: '', finitura: '' },
        config: {
          profiloSku:         null,
          profilo:            null,
          attaccaglieQtys:    {},
          base:               null,
          altezza:            null,
          fronteSku:          null,
          fronte:             null,
          retroSku:           null,
          retro:              null,
          hasPassLegno:       false,
          passLegnoSku:       null,
          passLegno:          null,
          hasPassCartone:     false,
          passCartoneSku:     null,
          passCartone:        null,
          passCartoneLargh:   PASS_CARTONE_LARGH_DEFAULT_CM,   // cm
          stampaTipo:         null,     // null | 'foto' | 'tela'
          stampa:             null,
          quantita:           1,
          // true = l'operatore ha scelto lui il numero di attaccaglie: stop ai default
          // automatici (vedi _maybeInitAttaccaglie).
          attTouched:         false,
        },
      };
      /* canvas image cache */
      this._frameImg          = null;
      this._frameImgUrl       = '';
      this._patternImg        = null;
      this._patternImgUrl     = '';
      this._passLegnoImg      = null;
      this._passLegnoImgUrl   = '';
      this._passLegnoPatternImg = null;
      this._passLegnoPatternUrl = '';
      this._passCartoneImg    = null;
      this._passCartoneImgUrl = '';
      this._passCartoneTile    = null;
      this._passCartoneTileKey = '';
    }

    _dataGlobal() { return window.SHOPIFY_ASTE_SU_MISURA; }
    _stampaSupportsTela() { return true; }

    init() {
      const data = window.SHOPIFY_ASTE_SU_MISURA;
      if (!data) {
        console.error('[CASM] Dati non disponibili');
        return;
      }
      /* Live view: canvas trasparente (il fondo è quello della card). Il default
         '#f0ede8' resta per il renderer headless, che istanzia SENZA init(). */
      this.__bgColor = 'transparent';
      this.loadData(data);
      this.renderFilters();
      this.renderGallery();
      this.setupEventListeners();
      this.syncFronte();
      this.syncRetro();
      this.renderStampaOptions();
      this.renderPassCartoneGallery();
      this.renderPassLegnoGallery();
      this._updatePassCartoneUI();
      this._updatePassLegnoState();
      this.renderAttaccaglieStep();
      this._checkSuccessSession('casm_success');
      this.setDefaultDim(DEFAULT_BASE, DEFAULT_ALTEZZA);
      const locked = this._applyLockedProfile(data);
      // Default = primo profilo DISPONIBILE, non il primo assoluto (vedi _primoProfiloUtile).
      const primo = locked ? null : this._primoProfiloUtile();
      if (primo) this.selectProfilo(primo.sku);
      this._applyUrlParams();
      this._mountMobileBar();
    }

    /* ── PROFILO BLOCCATO (configuratore dentro la pagina prodotto asta) ──
       Tre esiti, non due: `false` = nessun lock richiesto (default normale),
       `'ok'` = lock applicato, `'fallito'` = lock richiesto ma irrisolvibile.
       Il terzo caso NON deve ricadere sul default: preselezionare un altro profilo
       su una pagina prodotto significa vendere una cornice diversa da quella che il
       cliente sta guardando. Entrambi i valori non-false bloccano la preselezione. */
    _applyLockedProfile(data) {
      const sku = data && data.lockedProfileSku;
      if (!sku) return false;
      const prof = this.state.profili.find(x => x.sku === sku);
      if (!prof) { this._segnalaLockFallito(sku); return 'fallito'; }
      this.selectProfilo(sku);
      const step = document.getElementById('casm-step-profilo');
      if (step) step.style.display = 'none';
      const root = document.getElementById('configuratore-aste-su-misura');
      if (root) root.classList.add('casm--locked');
      return true;
    }

    /* ── PRE-FILL DA QUIZ / DEEP-LINK KIT (URL params) ── */
    _applyUrlParams() {
      const p = new URLSearchParams(window.location.search);
      if (!p.get('sku') && !p.get('base')) return;

      /* Confine URL: lookup via _findBySku (id_db-normalizzato) e in config SEMPRE lo
         sku canonico dell'item trovato, mai il parametro raw — un raw in config non
         matcherebbe più i confronti interni (gallery, select). */
      const sku = p.get('sku');
      if (sku) {
        const profilo = this._findBySku(this.state.profili, sku);
        if (profilo) this.selectProfilo(profilo.sku);
      }

      const base = parseFloat(p.get('base'));
      const alt  = parseFloat(p.get('alt'));
      if (!isNaN(base) && !isNaN(alt)) {
        this.setDefaultDim(base, alt);
      }

      /* `?fronte=` VUOTO (param presente, valore '') = scelta deliberata "Nessuno" — è il
         motivo del guard `!== null` (a differenza dei truthy-check di passC/passL). Va
         gestito prima di _findBySku, che rifiuta i target vuoti. */
      /* Si passa dai setter (_setFronte/_setRetro) e non da un assegnamento diretto allo
         stato: sono loro a emettere `casm:fronte-selected` / `casm:retro-selected`, e senza
         quegli eventi gli accordion del riepilogo restavano su vetro e cartone di default
         mentre stato, radio e prezzo dicevano crilex e schienale — visto sul deep-link dei
         kit combo (controllo-sito 18/09, N1). */
      const fronteSku = p.get('fronte');
      if (fronteSku !== null) {
        const fronte = fronteSku === '' ? null : this._findBySku(this.state.fronti, fronteSku);
        if (fronteSku === '' || fronte) {
          this._setFronte(fronte ? fronte.sku : NESSUNO_SKU);
          this.syncFronte();
        }
      }

      const retroSku = p.get('retro');
      if (retroSku !== null) {
        const retro = retroSku === '' ? null : this._findBySku(this.state.retri, retroSku);
        if (retroSku === '' || (retro && !this._isRetroDisabled(retro))) {
          this._setRetro(retro ? retro.sku : NESSUNO_SKU);
          this.syncRetro();
        }
      }

      const passCsku = p.get('passC');
      if (passCsku) {
        const item  = this._findBySku(this.state.passCartone, passCsku);
        const largh = parseFloat(p.get('passCL'));
        if (item) {
          this.state.config.hasPassCartone   = true;
          this.state.config.passCartoneSku   = item.sku;
          this.state.config.passCartone      = item;
          this.state.config.passCartoneLargh = !isNaN(largh) ? largh : PASS_CARTONE_LARGH_DEFAULT_CM;
          this.renderPassCartoneGallery();
          this._updatePassCartoneUI();
          if (item.imgUrl) this._loadPassCartoneImg(item.imgUrl);
        }
      }

      const passLsku = p.get('passL');
      if (passLsku) {
        const item = this._findBySku(this.state.passLegno, passLsku);
        if (item) {
          this.state.config.hasPassLegno = true;
          this.state.config.passLegnoSku = item.sku;
          this.state.config.passLegno    = item;
          this.renderPassLegnoGallery();
          if (item.imgUrl) this._loadPassLegnoImg(item.imgUrl);
          this._loadPassLegnoPatternImg(item.patternUrl || '');
        }
      }

      const stampaParam = p.get('stampa');
      if (stampaParam) {
        const tipo = ['foto', 'tela'].includes(stampaParam)
          ? stampaParam
          : (this._findBySku(this.state.stampe, stampaParam)?.tipo || null);
        if (tipo) this.selectStampaTipo(tipo);
      }

      // Stesso motivo dei setter qui sopra: il riepilogo dei passepartout ascolta gli eventi.
      if (p.get('passC') || p.get('passL')) this._dispatchPassEvents();
      this._applyHandoffArtwork();
      this.calculateLive();

      const target = document.getElementById('casm-profilo-info') || document.getElementById('casm-gallery');
      if (target) setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    }

    /* ── FOTO DAL QUIZ — handoff via sessionStorage ── */
    _applyHandoffArtwork() {
      let dataUrl = null;
      try { dataUrl = sessionStorage.getItem('qasm_artwork_dataurl'); } catch (e) {}
      if (!dataUrl) return;
      const img = new Image();
      img.onload = () => {
        this._artworkImg = img;
        this.updateLiveView();
      };
      img.src = dataUrl;
    }

    /* ── CARICAMENTO DATI ── */
    loadData(data) {
      // Profili: usa ASTE_CORNICI_FULL (generato da script, tutti i record)
      // se disponibile e non vuoto, altrimenti fallback ai dati liquid (max 250)
      const full = window.ASTE_CORNICI_FULL;
      if (full && full.length > 0) {
        this.state.profili = full
          .filter(r => (r.configuratore || '').includes('aste_su_misura'))
          .map(r => ({
            sku:           r.sku        || '',
            nome:          r.nome       || '',
            colore:        r.colore     || '',
            finitura:      r.finitura   || '',
            stile:         r.stile      || '',
            larghezzaMm:   r.larghezza_sezione_mm || 0,
            altezzaSezione:r.altezza_sezione_mm   || 0,
            battenteMm:    r.altezza_battente_mm  || 0,
            costoMl:       r.costo_fornitore_1    || 0,
            markup:        r.markup               || 0,
            markupGrande:  r.markup_grande        || 0,
            imgUrl:        r.immagine_url         || '',
            patternUrl:    r.pattern_url          || '',
            sezioneUrl:    r.sezione_profilo_url  || '',
            /* Foto reali del profilo (barra + campione d'angolo). Assenti su ~200 profili
               non ancora fotografati: il consumatore (opera-cornice-detail) non disegna il
               blocco invece di mostrare un buco. */
            fotoCampioneUrl: r.foto_campione_url  || '',
            fotoAngoloUrl:   r.foto_angolo_url    || '',
            fotoFallback:    r.foto_fallback === true,
            disponibile:   r.disponibile !== false,
          }));
      } else {
        this.state.profili = data.profili || [];
      }

      this.state.fronti      = data.fronti      || [];
      this.state.retri       = data.retri       || [];
      this.state.passLegno   = data.passLegno   || [];
      this.state.passCartone = data.passCartone || [];
      this.state.accessori   = data.accessori   || [];
      this.state.stampe      = data.stampe      || [];

      // default fronte = VETRO, retro = il primo SENZA limite di formato (_defaultFronte
      // / _defaultRetro): entrambi dal dato, non dalla posizione nell'elenco.
      // (Calcolati prima di aggiungere "Nessuno" alle liste.)
      const dfFronte = this._defaultFronte();
      const dfRetro  = this._defaultRetro();
      this.state.config.fronteSku = dfFronte?.sku || NESSUNO_SKU;
      this.state.config.fronte    = dfFronte      || null;
      this.state.config.retroSku  = dfRetro?.sku  || NESSUNO_SKU;
      this.state.config.retro     = dfRetro       || null;

      window._CASM_FRONTE = this.state.config.fronte;
      window._CASM_RETRO  = this.state.config.retro;
      document.dispatchEvent(new CustomEvent('casm:fronte-selected', { detail: this.state.config.fronte }));
      document.dispatchEvent(new CustomEvent('casm:retro-selected',  { detail: this.state.config.retro  }));
      this._dispatchPassEvents();
      this._loadEffettoImg(this.state.config.fronte?.effettoUrl || '');
    }

    /* ── FILTRI PROFILI ── */
    renderFilters() {
      this._renderBattenteFilter();
      this._renderColoreFilter();
      this._renderFinituraFilter();
    }

    _renderBattenteFilter() {
      const el = document.getElementById('casm-filter-battente');
      if (!el) return;
      const opts = [
        { label: 'Tutti',             value: ''      },
        { label: 'Normale',           value: 'basso' },
        { label: 'Alto almeno 20 mm', value: 'alto'  },
      ];
      el.innerHTML = opts.map(o =>
        `<button type="button"
           class="casm-pill${o.value === this.state.filters.battente ? ' active' : ''}"
           data-battente="${this._esc(o.value)}">${this._esc(o.label)}</button>`
      ).join('');
    }

    _renderColoreFilter() {
      const el = document.getElementById('casm-filter-colore');
      if (!el) return;
      const colori = [...new Set(
        this.state.profili.map(p => (p.colore || '').trim()).filter(Boolean)
      )].sort();
      const opts = [{ label: 'Tutti', value: '' }, ...colori.map(c => ({ label: c, value: c }))];
      el.innerHTML = opts.map(o =>
        `<button type="button"
           class="casm-pill${o.value === this.state.filters.colore ? ' active' : ''}"
           data-colore="${this._esc(o.value)}">${this._esc(o.label)}</button>`
      ).join('');
    }

    _renderFinituraFilter() {
      const el = document.getElementById('casm-filter-finitura');
      if (!el) return;
      const finiture = [...new Set(
        this.state.profili.map(p => (p.finitura || '').trim()).filter(Boolean)
      )].sort();
      const opts = [{ label: 'Tutti', value: '' }, ...finiture.map(f => ({ label: f, value: f }))];
      el.innerHTML = opts.map(o =>
        `<button type="button"
           class="casm-pill${o.value === this.state.filters.finitura ? ' active' : ''}"
           data-finitura="${this._esc(o.value)}">${this._esc(o.label)}</button>`
      ).join('');
    }

    _filteredProfili() {
      const { battente, colore, finitura } = this.state.filters;
      return this.state.profili.filter(p => {
        if (battente === 'alto'  && (p.battenteMm || 0) < BATTENTE_ALTO_MM) return false;
        if (battente === 'basso' && (p.battenteMm || 0) >= BATTENTE_ALTO_MM) return false;
        if (colore   && (p.colore   || '').toLowerCase() !== colore.toLowerCase())   return false;
        if (finitura && (p.finitura || '').toLowerCase() !== finitura.toLowerCase()) return false;
        return true;
      });
    }

    /* ── GALLERY PROFILI ── */
    renderGallery() {
      const grid    = document.getElementById('casm-gallery-grid');
      const countEl = document.getElementById('casm-gallery-count');
      if (!grid) return;

      const list = this._filteredProfili();
      if (countEl) countEl.textContent = `${list.length} ${list.length === 1 ? 'profilo' : 'profili'}`;

      if (!list.length) {
        grid.innerHTML = '<div class="casm-gallery-empty">Nessun profilo con questi filtri</div>';
        return;
      }

      grid.innerHTML = list.map(p => {
        const sel        = p.sku === this.state.config.profiloSku ? ' selected' : '';
        const esaurito   = p.disponibile === false ? ' casm-gallery-item--esaurito' : '';
        const imgHtml    = p.imgUrl
          ? `<img class="casm-gallery-item-img" src="${this._esc(this._thumbUrl(p.imgUrl))}" alt="" loading="lazy">`
          : `<div class="casm-gallery-item-placeholder">${this._esc(p.nome.split(' ').slice(0,2).join(' '))}</div>`;
        const badge      = p.disponibile === false
          ? `<span class="casm-badge-esaurito">Esaurito</span>` : '';
        return `<div class="casm-gallery-item${sel}${esaurito}" data-sku="${this._esc(p.sku)}"
                     title="${this._esc(p.nome)} — ${p.larghezzaMm || '?'}mm">${imgHtml}${badge}</div>`;
      }).join('');
      this._abilitaListbox(grid);
    }

    selectProfilo(sku) {
      const p = this.state.profili.find(x => x.sku === sku);
      if (!p) return;
      this.state.config.profiloSku = sku;
      this.state.config.profilo    = p;
      this.renderGallery();
      this._updateEsauritoWarning();
      this._loadFrameImg(p.imgUrl || '');
      this._loadPatternImg(p.patternUrl || '');
      this._updatePassLegnoState();
      this._updateStampaState();
      this.calculateLive();
      window._CAOA_PROFILO = p;
      document.dispatchEvent(new CustomEvent('caoa:cornice-selected', { detail: p }));
    }

    _updateEsauritoWarning() {
      const esauritoEl = document.getElementById('casm-esaurito-warning');
      if (esauritoEl) esauritoEl.style.display = this.state.config.profilo?.disponibile === false ? '' : 'none';
    }

    /* ── GALLERY PASSEPARTOUT CARTONE (toggle su riclick, niente tile "Nessuno") ── */
    renderPassCartoneGallery() {
      const { base, altezza } = this.state.config;
      const grid = document.getElementById('casm-pass-cartone-gallery');
      if (!grid) return;
      const items = this.state.passCartone;
      if (!items.length) { grid.innerHTML = '<p class="casm-hint">Nessun passepartout in cartone disponibile al momento.</p>'; return; }
      grid.innerHTML = items.map(p => {
        const sel      = (this.state.config.hasPassCartone && p.sku === this.state.config.passCartoneSku) ? ' selected' : '';
        const compat   = _passCartoneCompatibile(p, base, altezza);
        const disabled = compat ? '' : ' casm-gallery-item--disabled';
        const ariaDisabled = compat ? '' : ' aria-disabled="true"';
        const nome    = p.nome || '';
        const imgHtml = p.imgUrl
          ? `<img src="${this._esc(this._thumbUrl(p.imgUrl))}" alt="${this._esc(nome)}" loading="lazy">`
          : `<div class="casm-gallery-item-placeholder">${this._esc(nome.split(' ').slice(0,2).join(' '))}</div>`;
        return `<div class="casm-gallery-item${sel}${disabled}" data-pass-cartone="${this._esc(p.sku)}"${ariaDisabled}
                     title="${this._esc(nome)}${sel ? ' — riclicca per rimuovere' : ''}">${imgHtml}</div>`;
      }).join('');
      this._abilitaListbox(grid);
    }

    _updatePassCartoneUI() {
      const larghWrap = document.getElementById('casm-pass-cartone-largh-wrap');
      if (larghWrap) larghWrap.style.display = this.state.config.hasPassCartone ? '' : 'none';
    }

    /* ── GALLERY PASSEPARTOUT LEGNO ── */
    renderPassLegnoGallery() {
      const grid = document.getElementById('casm-pass-legno-gallery');
      if (!grid) return;
      const items = this.state.passLegno;
      if (!items.length) { grid.innerHTML = '<p class="casm-hint">Nessun passepartout in legno disponibile al momento.</p>'; return; }
      grid.innerHTML = items.map(p => {
        const sel     = (this.state.config.hasPassLegno && p.sku === this.state.config.passLegnoSku) ? ' selected' : '';
        const nome    = p.nome || '';
        const imgHtml = p.imgUrl
          ? `<img class="casm-gallery-item-img" src="${this._esc(this._thumbUrl(p.imgUrl))}" alt="${this._esc(nome)}" loading="lazy">`
          : `<div class="casm-gallery-item-placeholder">${this._esc(nome.split(' ').slice(0,2).join(' '))}</div>`;
        return `<div class="casm-gallery-item${sel}" data-pass-legno="${this._esc(p.sku)}"
                     title="${this._esc(nome)} — ${p.larghezzaMm || '?'}mm${sel ? ' — riclicca per rimuovere' : ''}">${imgHtml}</div>`;
      }).join('');
      this._abilitaListbox(grid);
    }

    /* ── blocca/sblocca legno in base al battente ── */
    _updatePassLegnoState() {
      const profilo  = this.state.config.profilo;
      const battente = profilo?.battenteMm || 0;
      const locked   = profilo && battente > 0 && battente < BATTENTE_ALTO_MM;

      const step  = document.getElementById('casm-pass-legno-step');
      const badge = document.getElementById('casm-pass-legno-locked-badge');
      if (step)  step.classList.toggle('casm-step--locked', !!locked);
      if (badge) badge.style.display = locked ? '' : 'none';

      if (locked && this.state.config.hasPassLegno) {
        this.state.config.hasPassLegno   = false;
        this.state.config.passLegnoSku   = null;
        this.state.config.passLegno      = null;
        this._passLegnoImg               = null;
        this._passLegnoImgUrl            = '';
        this._passLegnoPatternImg        = null;
        this._passLegnoPatternUrl        = '';
        this.renderPassLegnoGallery();
      }
    }

    /* ── CANVAS PREVIEW ── */
    updateLiveView() {
      const { base, altezza } = this.state.config;
      const canvas = document.getElementById('casm-live-canvas');
      const ph     = document.getElementById('casm-live-placeholder');
      if (!canvas || !ph) return;

      const hasData = base > 0 && altezza > 0 && !isNaN(base) && !isNaN(altezza);
      if (!hasData) {
        canvas.style.display = 'none';
        ph.style.display     = 'flex';
        this._syncArtworkUI();
        return;
      }
      canvas.style.display = 'block';
      ph.style.display     = 'none';
      this._fitCanvas(canvas);
      this._drawPreview(canvas);
      this._syncArtworkUI();
    }

    _drawPreview(canvas) {
      const { ctx, W, H } = this._canvasCtx(canvas);
      const {
        base, altezza, profilo,
        hasPassLegno, passLegno,
        hasPassCartone, passCartone, passCartoneLargh,
      } = this.state.config;

      const sezioneMm  = profilo?.larghezzaMm || 30;
      const sezioneCm  = sezioneMm / 10.0;

      const passLegnoCm   = (hasPassLegno   && passLegno)   ? (passLegno.larghezzaMm || 0) / 10.0 : 0;
      const passCartoneCm = (hasPassCartone && passCartone)  ? (passCartoneLargh || 0)             : 0;

      /* Catena sormonti (SSOT: price_logic → PRICING_SSOT.SORMONTO_CM): ogni strato
         copre SORMONTO_CM per lato di quello interno → si disegnano le fasce VISIBILI:
         legno = larghezza − sormonto, cartone = fascia inserita (il suo sormonto
         esterno è compensato nel taglio), opera = opera − 2×sormonto. */
      const sormCm     = this.PRICING.SORMONTO_CM || 0;
      const legnoVisCm = passLegnoCm > 0 ? Math.max(passLegnoCm - sormCm, 0) : 0;
      const operaVisW  = Math.max(base    - 2 * sormCm, 0.1);
      const operaVisH  = Math.max(altezza - 2 * sormCm, 0.1);

      const totW  = operaVisW + 2 * (legnoVisCm + passCartoneCm) + 2 * sezioneCm;
      const totH  = operaVisH + 2 * (legnoVisCm + passCartoneCm) + 2 * sezioneCm;
      const pad   = this.__margineCatalogo ? Math.round(Math.min(W, H) * MARGINE_CATALOGO) : CVS_PAD;
      const scale = Math.min((W - pad * 2) / totW, (H - pad * 2) / totH);
      const drawW = totW * scale;
      const drawH = totH * scale;
      const fx    = (W - drawW) / 2;
      const fy    = (H - drawH) / 2;
      const profPx  = sezioneCm * scale;
      const legnoPx = legnoVisCm * scale;
      const cartPx  = passCartoneCm * scale;

      ctx.clearRect(0, 0, W, H);
      // Sfondo configurabile: '__bgColor === "transparent"' salta il fill →
      // usato dal render headless delle opere incorniciate (landing autori).
      if (this.__bgColor !== 'transparent') {
        ctx.fillStyle = this.__bgColor || '#f0ede8';
        ctx.fillRect(0, 0, W, H);
      }

      /* ombra (saltata se __noShadow — es. render opere landing autori) */
      if (!this.__noShadow) this._ombraCornice(ctx, fx, fy, drawW, drawH);
      ctx.fillStyle = '#ccc';
      ctx.fillRect(fx, fy, drawW, drawH);
      ctx.shadowColor = 'transparent';

      /* ── LAYER 1: cornice ── */
      if (this._frameImg && profilo) {
        const slicePx = this._frameImg.naturalWidth * (sezioneMm / 200);
        this._draw9Slice(ctx, this._frameImg, slicePx, fx, fy, drawW, drawH, profPx, this._patternImg);
      } else {
        /* fallback gradiente quando l'immagine non è ancora caricata */
        this._drawCorniceFallback(ctx, this._fallbackColor(profilo?.colore), fx, fy, drawW, drawH, profPx);
      }

      let opX = fx + profPx, opY = fy + profPx;
      let opW = drawW - 2 * profPx, opH = drawH - 2 * profPx;

      /* ── LAYER 2: passepartout legno ── */
      if (legnoPx > 0) {
        if (this._passLegnoImg) {
          const slicePx = this._passLegnoImg.naturalWidth * ((passLegno.larghezzaMm || 30) / 200);
          this._draw9Slice(ctx, this._passLegnoImg, slicePx, opX, opY, opW, opH, legnoPx, this._passLegnoPatternImg);
        } else {
          ctx.fillStyle = PASS_LEGNO_COLOR;
          ctx.fillRect(opX, opY, opW, legnoPx);
          ctx.fillRect(opX, opY + opH - legnoPx, opW, legnoPx);
          ctx.fillRect(opX, opY + legnoPx, legnoPx, opH - 2 * legnoPx);
          ctx.fillRect(opX + opW - legnoPx, opY + legnoPx, legnoPx, opH - 2 * legnoPx);
        }
        opX += legnoPx; opY += legnoPx; opW -= 2 * legnoPx; opH -= 2 * legnoPx;
      }

      /* ── LAYER 3: passepartout cartone ── */
      if (cartPx > 0) {
        if (this._passCartoneImg) {
          const tileKey = `${this.state.config.passCartoneSku}_${Math.round(cartPx)}`;
          if (this._passCartoneTileKey !== tileKey) {
            const ts  = Math.max(1, Math.round(cartPx));
            const off = document.createElement('canvas');
            off.width = ts; off.height = ts;
            off.getContext('2d').drawImage(this._passCartoneImg, 0, 0, ts, ts);
            this._passCartoneTile    = off;
            this._passCartoneTileKey = tileKey;
          }
          const pattern = ctx.createPattern(this._passCartoneTile, 'repeat');
          /* overlap di 1px sui lati verticali: stessa fessura da anti-aliasing
             del 9-slice (vedi _draw9Slice) tra bande adiacenti con coordinate
             frazionarie. I lati sono disegnati dopo alto/basso e allargati
             per coprire la giunzione. */
          const oc = 1;
          [
            { x: opX,               y: opY,               w: opW,    h: cartPx },
            { x: opX,               y: opY + opH - cartPx, w: opW,   h: cartPx },
            { x: opX,               y: opY + cartPx - oc,       w: cartPx, h: opH - 2 * cartPx + 2 * oc },
            { x: opX + opW - cartPx, y: opY + cartPx - oc,      w: cartPx, h: opH - 2 * cartPx + 2 * oc },
          ].forEach(b => {
            ctx.save();
            ctx.beginPath(); ctx.rect(b.x, b.y, b.w, b.h); ctx.clip();
            pattern.setTransform(new DOMMatrix().translate(b.x, b.y));
            ctx.fillStyle = pattern; ctx.fillRect(b.x, b.y, b.w, b.h);
            ctx.restore();
          });
          ctx.strokeStyle = 'rgba(0,0,0,0.12)'; ctx.lineWidth = 1;
          ctx.strokeRect(opX + cartPx, opY + cartPx, opW - 2 * cartPx, opH - 2 * cartPx);
        } else {
          ctx.fillStyle = PASS_CARTONE_COLOR;
          ctx.fillRect(opX, opY, opW, cartPx);
          ctx.fillRect(opX, opY + opH - cartPx, opW, cartPx);
          ctx.fillRect(opX, opY + cartPx, cartPx, opH - 2 * cartPx);
          ctx.fillRect(opX + opW - cartPx, opY + cartPx, cartPx, opH - 2 * cartPx);
        }
        this._drawPassCartoneBevel(ctx, opX, opY, opW, opH, cartPx, scale);
        opX += cartPx; opY += cartPx;
        opW -= 2 * cartPx; opH -= 2 * cartPx;
      }

      /* ── LAYER 4: area opera ── */
      this._drawApertura(ctx, opX, opY, opW, opH, scale);

      ctx.strokeStyle = 'rgba(0,0,0,0.08)'; ctx.lineWidth = 1;
      ctx.strokeRect(fx, fy, drawW, drawH);

      /* Le quote sono le dimensioni del disegno: totW/totH, la catena dei sormonti
         (= price_logic.dims_esterno_cornice). Fino al 17/10/2026 si sommavano qui sezione e
         passepartout interi all'opera, senza i sormonti: il render scriveva 56,3 × 77,3 cm
         dove la cornice ne misura 55,7 × 76,7 (ordine #1392), e il foglio di produzione
         diceva il vero. */
      if (!this.__noShadow) this._drawDimLabels(ctx, W, H, fx, fy, drawW, drawH, totW, totH);
    }

    /* L'area dentro cornice e passepartout (la parte a vista dell'opera, in px). Metodo a
       parte dal 10/10/2026 perche' il passepartout multifoto disegna qui il suo cartone
       con le finestre (ConfiguratoreMultifoto._drawApertura); il resto del disegno e'
       lo stesso. `scale` = px per cm. */
    _drawApertura(ctx, opX, opY, opW, opH, scale) {
      // __aperturaVuota: la cornice SENZA fondo (kit grezzi, render headless): dentro non
      // c'e' niente da disegnare e l'apertura resta trasparente — appoggiata su una
      // parete si vede la parete. Nel configuratore live il flag non esiste (undefined)
      // e il comportamento e' quello di sempre: pannello bianco, opera, effetto fronte.
      if (this.__aperturaVuota) {
        ctx.clearRect(opX, opY, opW, opH);
        if (this.__bgColor !== 'transparent') {
          ctx.fillStyle = this.__bgColor || '#f0ede8';
          ctx.fillRect(opX, opY, opW, opH);
        }
        return;
      }
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(opX, opY, opW, opH);

      if (this._artworkImg) {
        this._drawImgCover(ctx, this._artworkImg, opX, opY, opW, opH);
      }

      /* ── LAYER 5: effetto stampa (lucida/tela) poi fronte (vetro/crilex) ── */
      this._drawStampaEffetto(ctx, opX, opY, opW, opH, this.state.config.base);
      this._drawFronteEffetto(ctx, opX, opY, opW, opH);
    }

    _drawDimLabels(ctx, W, H, fx, fy, drawW, drawH, extW, extH) {
      const off = 14, tick = 5;
      ctx.strokeStyle = '#8b2635'; ctx.lineWidth = 1.2; ctx.fillStyle = '#8b2635';
      ctx.font = '600 13px Helvetica,Arial,sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'top';
      const by = fy + drawH + off;
      ctx.beginPath(); ctx.moveTo(fx, by); ctx.lineTo(fx + drawW, by); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(fx, by - tick); ctx.lineTo(fx, by + tick); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(fx + drawW, by - tick); ctx.lineTo(fx + drawW, by + tick); ctx.stroke();
      ctx.fillText(`${this._round(extW, 1)} cm`, fx + drawW / 2, by + 3);
      const ax = fx + drawW + off;
      ctx.beginPath(); ctx.moveTo(ax, fy); ctx.lineTo(ax, fy + drawH); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(ax - tick, fy); ctx.lineTo(ax + tick, fy); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(ax - tick, fy + drawH); ctx.lineTo(ax + tick, fy + drawH); ctx.stroke();
      ctx.save();
      ctx.translate(ax + 14, fy + drawH / 2); ctx.rotate(Math.PI / 2);
      ctx.textBaseline = 'middle'; ctx.fillText(`${this._round(extH, 1)} cm`, 0, 0);
      ctx.restore();
    }

    /* ── SYNC FRONTE / RETRO ── */
    _setFronte(sku) {
      const f = this.state.fronti.find(x => x.sku === sku) || null;
      this.state.config.fronteSku = f ? f.sku : NESSUNO_SKU;
      this.state.config.fronte    = f;
      this._loadEffettoImg(f?.effettoUrl || '');
      window._CASM_FRONTE = f;
      document.dispatchEvent(new CustomEvent('casm:fronte-selected', { detail: f }));
      this.calculateLive();
    }
    _setRetro(sku) {
      const r = this.state.retri.find(x => x.sku === sku) || null;
      this.state.config.retroSku = r ? r.sku : NESSUNO_SKU;
      this.state.config.retro    = r;
      window._CASM_RETRO = r;
      document.dispatchEvent(new CustomEvent('casm:retro-selected', { detail: r }));
      this.calculateLive();
    }

    syncFronte() {
      const el = document.getElementById('casm-fronte-options');
      if (!el) return;
      el.innerHTML = this.state.fronti.map(f => {
        const ch      = f.sku === this.state.config.fronteSku ? ' checked' : '';
        const img     = this._optionImg(f.immagineUrl);
        const desc    = f.descrizioneBreve || '';
        return `<label class="casm-option">
          <input type="radio" name="casm-fronte" value="${this._esc(f.sku)}"${ch}>
          <span class="casm-option-card">
            ${img}
            <span class="casm-option-dot"></span>
            <span class="casm-option-text">
              <strong>${this._esc(f.nome)}</strong>
              <span>${this._esc(desc)}</span>
            </span>
          </span>
        </label>`;
      }).join('');
    }

    syncRetro() {
      const el = document.getElementById('casm-retro-options');
      if (!el) return;
      el.innerHTML = this.state.retri.map(r => {
        const ch      = r.sku === this.state.config.retroSku ? ' checked' : '';
        const dis     = this._isRetroDisabled(r) ? ' disabled' : '';
        const img     = this._optionImg(r.immagineUrl);
        const hint    = this._isRetroDisabled(r) ? this._hintRetroLimite(r) : '';
        const desc    = hint ? '' : (r.descrizioneBreve || '');
        return `<label class="casm-option">
          <input type="radio" name="casm-retro" value="${this._esc(r.sku)}"${ch}${dis}>
          <span class="casm-option-card">
            ${img}
            <span class="casm-option-dot"></span>
            <span class="casm-option-text">
              <strong>${this._esc(r.nome)}</strong>
              ${(hint || desc) ? `<span>${hint || this._esc(desc)}</span>` : ''}
            </span>
          </span>
        </label>`;
      }).join('');
    }

    /* _isRetroDisabled: ereditato da ConfiguratoreBase (formato FINITO, fix 28/08/2026). */

    updateRetroAvailability() {
      document.querySelectorAll('#casm-retro-options input[type="radio"]').forEach(inp => {
        const r = this.state.retri.find(x => x.sku === inp.value);
        if (!r) return;
        const dis = this._isRetroDisabled(r);
        inp.disabled = dis;
        if (dis && inp.checked) {
          const fallback = this.state.retri.find(x => !this._isRetroDisabled(x));
          if (fallback) {
            this.state.config.retroSku = fallback.sku;
            this.state.config.retro    = fallback;
            this.syncRetro();
          }
        }
      });
    }

    /* ── PASSEPARTOUT EVENTS ── */
    _dispatchPassEvents() {
      const { hasPassCartone, passCartone, passCartoneLargh, hasPassLegno, passLegno } = this.state.config;
      const cartoneDetail = (hasPassCartone && passCartone)
        ? { tipo: 'cartone', sku: passCartone.sku || '', nome: passCartone.nome, nomeBreve: passCartone.nomeBreve || '', imgUrl: passCartone.imgUrl || '', larghezza: passCartoneLargh,
            spessoreMm: passCartone.spessoreMm || 0, anima: passCartone.anima || '',
            colore: passCartone.colore || '', tinta: passCartone.tinta || '', note: passCartone.note || '' }
        : null;
      const legnoDetail = (hasPassLegno && passLegno)
        ? { tipo: 'legno', sku: passLegno.sku || '', nome: passLegno.nome, nomeBreve: passLegno.nomeBreve || '', imgUrl: passLegno.imgUrl || '', larghezza: (passLegno.larghezzaMm || 0) / 10,
            colore: passLegno.colore || '',
            altezzaSezione: passLegno.altezzaSezione || 0, battenteMm: passLegno.battenteMm || 0,
            fotoCampioneUrl: passLegno.fotoCampioneUrl || '', fotoAngoloUrl: passLegno.fotoAngoloUrl || '' }
        : null;
      window._CASM_PASS_CARTONE = cartoneDetail;
      window._CASM_PASS_LEGNO   = legnoDetail;
      document.dispatchEvent(new CustomEvent('casm:pass-cartone-changed', { detail: cartoneDetail }));
      document.dispatchEvent(new CustomEvent('casm:pass-legno-changed',   { detail: legnoDetail   }));
    }

    /* ── ATTACCAGLIE ── */
    /* Soglia e conteggi arrivano da PRICING (SSOT: price_logic.get_hangers_count, via
       generate_pricing_constants_js.py): erano cablati qui come 40/1/2, cioè la stessa
       regola scritta due volte.

       Si misura il BATTENTE, non l'opera: le attaccaglie si avvitano sul pezzo FISICO e
       con un passepartout la cornice è più grande dell'opera. Fino al 28/07/2026 qui si
       usavano base/altezza nudi mentre price_engine usava il battente, quindi un'opera
       38×38 con passepartout da 5 cm (battente 48×48) dava 2 attaccaglie al banco e 1
       sul sito. Stesso helper del calcolo prezzo, così le due misure non possono più
       divergere. */
    _nAttaccaglie() {
      const P = this.PRICING;
      const { base, altezza, hasPassCartone, passCartone, passCartoneLargh,
              hasPassLegno, passLegno } = this.state.config;
      const ppcLargh = (hasPassCartone && passCartone && passCartoneLargh > 0) ? passCartoneLargh : 0;
      const pplMm    = (hasPassLegno && passLegno) ? (passLegno.larghezzaMm || 0) : 0;
      return FR.nAttaccaglie(base, altezza, ppcLargh, pplMm, P);
    }

    /* Ricalcola la quantità AUTOMATICA di attaccaglie, finché l'operatore non l'ha
       toccata a mano. Prima usciva appena `attaccaglieQtys` era popolato: dopo le prime
       misure il numero restava congelato, quindi aggiungere un passepartout (che ingrandisce
       il battente) non lo aggiornava. price_engine invece lo ricalcola a ogni preventivo —
       ed è l'altra metà della divergenza battente/opera. Stesso schema del flag
       `_accTouched` del configuratore preventivi. */
    _maybeInitAttaccaglie() {
      if (this.state.config.attTouched) return false;
      const att = this.state.accessori.filter(x => (x.categoria || '').includes('attaccaglie'));
      if (!att.length) return false;
      /* Dice se il numero è cambiato: calculateLive ridisegna lo step solo allora.
         Fino al 16/09/2026 non lo ridisegnava mai — aggiungendo un passepartout il
         prezzo e il carrello passavano a 2 attaccaglie, la tile restava a 1 (audit
         configuratore 16/09, rilievo 1). */
      const n    = this._nAttaccaglie();
      const prev = this.state.config.attaccaglieQtys || {};
      const same = Object.keys(prev).length === 1 && prev[att[0].sku] === n;
      this.state.config.attaccaglieQtys = { [att[0].sku]: n };
      return !same;
    }

    renderAttaccaglieStep() {
      const el    = document.getElementById('casm-attacc-step');
      const legEl = document.getElementById('casm-legend-attacc');
      const items = this.state.accessori.filter(x => (x.categoria || '').includes('attaccaglie'));
      const qtys  = this.state.config.attaccaglieQtys || {};
      const totQty = Object.values(qtys).reduce((s, q) => s + (q || 0), 0);
      if (legEl) legEl.textContent = totQty > 0 ? ` — ${totQty} pz` : '';
      const firstItem = items.find(a => (qtys[a.sku] || 0) > 0) || items[0] || null;
      const accdDetail = { tipo: 'attaccaglia', sku: firstItem?.sku || '', nome: firstItem?.nome || '', nomeBreve: firstItem?.nomeBreve || '', imgUrl: firstItem?.imgUrl || '', materiale: firstItem?.materiale || '', colore: firstItem?.colore || '', descrizioneBreve: firstItem?.descrizioneBreve || '', note: firstItem?.note || '', totQty, items: items.map(a => ({ sku: a.sku, nome: a.nome, qty: qtys[a.sku] || 0 })) };
      window._CASM_ACCESSORI = accdDetail;
      document.dispatchEvent(new CustomEvent('casm:accessori-changed', { detail: accdDetail }));
      if (!el) return;
      if (!items.length) {
        el.innerHTML = '<p class="casm-hint">Nessuna attaccaglia disponibile.</p>';
        return;
      }
      /* Tile a 6 colonne come profili e passepartout (16/09/2026): immagine quadrata,
         didascalia «Attaccaglie Oro FRAC0030» (core.didascaliaAccessorio) e stepper
         compatto dentro la cella — la quantità resta PER TIPO, si possono mischiare.
         Nome completo e descrizione stanno nel tooltip e nella scheda «Accessori». */
      const core = window.FOTORECORD.core;
      el.innerHTML =
        `<div class="casm-attacc-gallery">` +
        items.map(a => {
          const qty   = qtys[a.sku] || 0;
          const label = core.didascaliaAccessorio(a.colore, a.sku);
          const img   = a.imgUrl
            ? `<img class="casm-attacc-img" src="${this._esc(this._thumbUrl(a.imgUrl, 200))}" alt="" loading="lazy">`
            : '<div class="casm-attacc-img-placeholder"></div>';
          return `<div class="casm-attacc-card${qty > 0 ? ' active' : ''}" title="${this._esc(a.nome)}${a.descrizioneBreve ? ' — ' + this._esc(a.descrizioneBreve) : ''}">
            ${img}
            <div class="casm-attacc-name">${this._esc(label)}</div>
            <div class="casm-qty-stepper">
              <button type="button" class="casm-qty-btn" data-attacc-delta="-1" data-attacc-sku="${this._esc(a.sku)}" aria-label="Diminuisci ${this._esc(label)}">−</button>
              <input type="number" class="casm-qty-input casm-attacc-qty-input" data-attacc-sku="${this._esc(a.sku)}"
                     value="${qty}" min="0" max="20" step="1" aria-label="Quantità ${this._esc(a.nome)}">
              <button type="button" class="casm-qty-btn" data-attacc-delta="1" data-attacc-sku="${this._esc(a.sku)}" aria-label="Aumenta ${this._esc(label)}">+</button>
            </div>
          </div>`;
        }).join('') +
        `</div>`;
    }

    /* ── DIMENSIONI ── */
    setDefaultDim(base, altezza) {
      const inputB = document.getElementById('casm-base');
      const inputA = document.getElementById('casm-altezza');
      if (inputB) inputB.value = base;
      if (inputA) inputA.value = altezza;
      this.state.config.base    = base;
      this.state.config.altezza = altezza;
      this._syncPresets(base, altezza);
      this.updateLiveView();
      this.updateRetroAvailability();
      this.syncRetro();
      this._updatePassLegnoState();
      this._updateStampaState();
      this._maybeInitAttaccaglie();
      this.renderAttaccaglieStep();
      this.calculateLive();
    }

    /* ── CALCOLO PREZZO ── */
    calculateLive() {
      // gallery compat + auto-deselect del cartone incompatibile con le dimensioni
      this.renderPassCartoneGallery();
      if (this.state.config.hasPassCartone) {
        const _pc = this.state.config.passCartone;
        const _b  = this.state.config.base, _a = this.state.config.altezza;
        if (_pc && !_passCartoneCompatibile(_pc, _b, _a)) {
          this.state.config.hasPassCartone = false;
          this.state.config.passCartoneSku = null;
          this.state.config.passCartone    = null;
          this._passCartoneImg = null; this._passCartoneImgUrl = '';
          this.renderPassCartoneGallery();
          this._updatePassCartoneUI();
        }
      }
      /* Attaccaglie automatiche: si ricalcolano QUI, perché il loro numero dipende dal
         battente e quindi cambia anche quando si aggiunge o toglie un passepartout, non
         solo quando cambiano le misure. Esce da sé se l'operatore le ha già scelte a mano.
         Se il numero cambia, lo step si ridisegna: tile, legenda, scheda e carrello
         devono dire lo stesso numero. */
      if (this._maybeInitAttaccaglie()) this.renderAttaccaglieStep();

      const validation = this.validateConfig();
      this._renderValidation(validation);
      if (validation.some(v => v.type === 'error')) { this._lastCalcResult = null; this._renderPrice(null); this._publishPrezziComponenti(null); return; }

      const {
        base, altezza, profilo, fronte, retro,
        hasPassLegno, passLegno,
        hasPassCartone, passCartone, passCartoneLargh,
        stampa, stampaTipo,
      } = this.state.config;

      const fascia         = this._getFascia(base, altezza);
      const soloPreventivo = fascia.soloPreventivo;
      /* flag esplicito della fascia (30/07/2026): prima si deduceva da
         `supplementoGrande > 0`, cioè dall'importo dell'imballo — vedi FR.getFascia. */
      const isGrande       = fascia.isGrande;
      /* markup PER-COMPONENTE: ogni voce porta markup (std) + markupGrande. */
      const mk = this._makeMk(isGrande);

      /* LA FORMULA E' UNA: FR.corniceNetto (configuratore-pricing.js), la stessa del quiz.
         Qui si preparano solo gli input nella forma normalizzata. */
      const attItemsCalc = this.state.accessori.filter(x => (x.categoria || '').includes('attaccaglie'));
      const attaccaglie  = attItemsCalc.map(a => ({ prezzo: a.prezzo, qty: (this.state.config.attaccaglieQtys || {})[a.sku] || 0 }));
      const voci = window.FOTORECORD.corniceNetto({
        base, altezza, profilo, fronte, retro,
        passLegno:   (hasPassLegno && passLegno) ? passLegno : null,
        passCartone: (hasPassCartone && passCartone) ? passCartone : null,
        passCartoneLargh, stampa, stampaTipo, attaccaglie, isGrande, mk, P: this.PRICING,
      });
      const { asta: costoAsta, fronte: costoFronte, retro: costoRetro, passLegno: costoPassLegno,
              passCartone: costoPassCartone, stampa: costoStampa, accessori: costoAtt, batt } = voci;

      /* Dato incompleto → nessun prezzo, non un prezzo approssimato. */
      if (this._markupMancante(mk)) { this._lastCalcResult = null; this._renderPrice(null); this._publishPrezziComponenti(null); return; }

      const netto = voci.netto;
      const imponibile     = netto + window.FOTORECORD.imballoNetto(fascia);
      const prezzoUnitario = imponibile * (1 + this.PRICING.IVA);
      const quantita       = this.state.config.quantita || 1;
      const tot            = prezzoUnitario * quantita;

      this._lastCalcResult = { tot, prezzoUnitario, fascia };
      this._renderPrice(tot, fascia);
      /* In fascia solo-preventivo il totale e' una stima: niente importi per componente. */
      this._publishPrezziComponenti(soloPreventivo ? null : {
        profilo: costoAsta, fronte: costoFronte, retro: costoRetro,
        passCartone: costoPassCartone, passLegno: costoPassLegno,
        stampa: costoStampa, accessori: costoAtt,
      });

      /* dimensioni totali esterne = luce (battente − 2×sormonto) + 2×sezione profilo */
      const sezCm       = (profilo?.larghezzaMm || 0) / 10;
      const _sorm       = this.PRICING.SORMONTO_CM || 0;
      const extTotB     = this._round(batt.b - 2 * _sorm + 2 * sezCm, 1);
      const extTotA     = this._round(batt.h - 2 * _sorm + 2 * sezCm, 1);
      const hasDims     = sezCm > 0;
      const dimText     = hasDims ? `${extTotB} × ${extTotA} cm` : '';

      const dimEstEl = document.getElementById('casm-dim-esterne');
      if (dimEstEl) {
        dimEstEl.textContent = hasDims ? `Dimensioni esterne: ${dimText}` : '';
        dimEstEl.style.display = hasDims ? '' : 'none';
      }
      document.querySelectorAll('.casm-price-dims').forEach(el => {
        el.textContent = dimText;
        el.style.display = hasDims ? '' : 'none';
      });
    }

    validateConfig() {
      const msgs = [];
      const { base, altezza, profilo, fronte, retro } = this.state.config;
      if (!base || !altezza) {
        msgs.push({ type: 'error', text: 'Inserisci le dimensioni (base e altezza).' });
        return msgs;
      }
      const minBase = (profilo && profilo.dimMinBase > 0) ? profilo.dimMinBase : 9;
      const minAlt  = (profilo && profilo.dimMinAlt > 0) ? profilo.dimMinAlt : 9;
      if (base < minBase || altezza < minAlt) {
        msgs.push({ type: 'error', text: `Dimensioni minime: ${minBase} × ${minAlt} cm.` });
      }
      if (!profilo) msgs.push({ type: 'error', text: 'Seleziona un profilo dalla gallery.' });
      const vincoloCrilex = this._vincoloCrilex(base, altezza);
      if (vincoloCrilex) msgs.push({ type: 'error', text: vincoloCrilex });
      return msgs;
    }

    _renderPrice(tot, fascia) {
      const amountEls = document.querySelectorAll('.casm-price-amount');
      const noteEls   = document.querySelectorAll('.casm-price-extra');
      if (!amountEls.length) return;
      this._rigaSpedizione(false);

      const esaurito = this.state.config.profilo?.disponibile === false;

      const forcePreventivo = !!(window.SHOPIFY_ASTE_SU_MISURA?.forcePreventivo);
      if (forcePreventivo || fascia?.soloPreventivo) {
        amountEls.forEach(el => { el.textContent = `€ ${this._fmt(tot)}`; el.dataset.placeholder = 'false'; });
        this._setCta({ forcePreventivo: true });
        noteEls.forEach(el => { el.textContent = 'Stima indicativa — il prezzo definitivo, spedizione compresa, viene confermato via email'; el.style.display = ''; });
        return;
      }

      if (tot === null) {
        amountEls.forEach(el => { el.textContent = 'Completa la configurazione'; el.dataset.placeholder = 'true'; });
        this._setCta({ result: null, incompleteText: 'Configura la cornice' });
        noteEls.forEach(el => el.style.display = 'none');
        return;
      }

      if (esaurito) {
        amountEls.forEach(el => { el.textContent = 'Profilo non disponibile'; el.dataset.placeholder = 'true'; });
        this._setCta({ esaurito: true });
        noteEls.forEach(el => el.style.display = 'none');
        return;
      }

      const quantita       = this.state.config.quantita || 1;
      const prezzoUnitario = this._lastCalcResult?.prezzoUnitario;
      // Carrello nativo: scelgo la "fascia di prezzo" (variante reale) col prezzo ≥ a quello calcolato.
      // Mostro il prezzo arrotondato della fascia → quello che il cliente vede è quello che paga.
      const bucket = this._pickBucket(prezzoUnitario);
      this._bucket = bucket;

      if (!bucket) {
        // Prezzo fuori dalle fasce disponibili → preventivo.
        amountEls.forEach(el => { el.textContent = `€ ${this._fmt(tot)}`; el.dataset.placeholder = 'false'; });
        this._setCta({ result: { soloPreventivo: false }, bucket: null });
        document.querySelectorAll('.casm-price-unit').forEach(el => { el.style.display = 'none'; });
        this._rigaSpedizione(true);
        noteEls.forEach(el => { el.textContent = 'Stima indicativa — prezzo confermato via email'; el.style.display = ''; });
        return;
      }

      const bucketUnit = bucket.c / 100;
      const bucketTot  = bucketUnit * quantita;
      amountEls.forEach(el => { el.textContent = `€ ${this._fmt(bucketTot)}`; el.dataset.placeholder = 'false'; });
      this._setCta({ result: { soloPreventivo: false }, bucket: bucket });
      document.querySelectorAll('.casm-price-unit').forEach(el => {
        if (quantita > 1) { el.textContent = `${quantita} × € ${this._fmt(bucketUnit)} cad.`; el.style.display = ''; }
        else { el.style.display = 'none'; }
      });
      this._rigaSpedizione(true);
      const imballo = fascia.supplementoGrande > 0;
      noteEls.forEach(el => {
        el.textContent = imballo ? this._notaImballo(fascia.supplementoGrande) : '';
        el.style.display = imballo ? '' : 'none';
      });
    }

    _renderValidation(msgs) {
      const el = document.getElementById('casm-validation');
      if (!el) return;
      el.innerHTML = msgs.map(m =>
        `<div class="casm-msg ${m.type}"><span>${this._esc(m.text)}</span></div>`
      ).join('');
    }

    /* ── MODAL ── */
    openModal() {
      const overlay = document.getElementById('casm-modal-overlay');
      if (!overlay) return;
      this._buildRecap();
      this._buildModalText();
      overlay.classList.add('open');
      document.body.style.overflow = 'hidden';
    }

    _buildRecap() {
      const el = document.getElementById('casm-modal-recap-rows');
      if (!el) return;
      const {
        base, altezza, profilo, fronte, retro, stampa, stampaTipo,
        hasPassLegno, passLegno,
        hasPassCartone, passCartone, passCartoneLargh,
      } = this.state.config;
      const rows = [
        ['Dimensioni interne',  base && altezza ? `${base} × ${altezza} cm` : '—'],
        ['Profilo asta',        nb(profilo) || '—'],
        ['Fronte',              nb(fronte)  || 'Nessuno'],
        ['Retro',               nb(retro)   || 'Nessuno'],
      ];
      if (stampa && stampaTipo) {
        rows.push(['Stampa', `${nb(stampa)}${stampaTipo === 'tela' ? ' - telaio incluso' : ''}`]);
      }
      if (hasPassCartone) rows.push(['Passepartout in cartone', `${nb(passCartone) || '—'} - bordo ${passCartoneLargh} cm`]);
      if (hasPassLegno)   rows.push(['Passepartout in legno',   nb(passLegno) || '—']);
      const attItems  = this.state.accessori.filter(x => (x.categoria || '').includes('attaccaglie'));
      const attQtysR  = this.state.config.attaccaglieQtys || {};
      const attPartsR = attItems.filter(a => (attQtysR[a.sku] || 0) > 0).map(a => `${attQtysR[a.sku]} × ${nb(a)}`);
      if (attPartsR.length) rows.push(['Accessori', attPartsR.join(', ')]);
      el.innerHTML = rows.map(([l, v]) =>
        `<div class="casm-modal-recap-row"><span>${l}</span><span>${v}</span></div>`
      ).join('');
      const priceEl = document.getElementById('casm-modal-price-value');
      const srcEl   = document.querySelector('.casm-price-amount');
      if (priceEl && srcEl) priceEl.textContent = srcEl.textContent;
    }

    _buildModalText() {
      const ta = document.getElementById('casm-modal-body');
      if (!ta) return;
      const {
        base, altezza, profilo, fronte, retro,
        hasPassLegno, passLegno,
        hasPassCartone, passCartone, passCartoneLargh,
      } = this.state.config;

      if (profilo?.disponibile === false) {
        ta.value = `Richiesta disponibilità profilo\nProfilo: ${this._profiloCodice(profilo)}\n\nVorrei sapere quando questo profilo tornerà disponibile.`;
        return;
      }

      const lines = [
        `Configurazione Cornice su Misura`,
        `Dimensioni opera: ${base} × ${altezza} cm`,
        `Profilo asta: ${nb(profilo) || '—'}`,
        `Fronte: ${nb(fronte) || 'Nessuno'}`,
        `Retro: ${nb(retro) || 'Nessuno'}`,
      ];
      const stampa     = this.state.config.stampa;
      const stampaTipo = this.state.config.stampaTipo;
      if (stampa && stampaTipo) {
        lines.push(`Stampa: ${nb(stampa)} (${this._componenteCodice(stampa)})${stampaTipo === 'tela' ? ' - montaggio su telaio' : ''}`);
      }
      if (hasPassLegno)   lines.push(`Passepartout in legno: ${nb(passLegno) || '—'}`);
      if (hasPassCartone) lines.push(`Passepartout in cartone: ${nb(passCartone) || '—'} - bordo ${passCartoneLargh} cm`);
      const attItems2  = this.state.accessori.filter(x => (x.categoria || '').includes('attaccaglie'));
      const attQtys2   = this.state.config.attaccaglieQtys || {};
      const attParts2  = attItems2.filter(a => (attQtys2[a.sku] || 0) > 0).map(a => `${attQtys2[a.sku]} × ${nb(a)}`);
      if (attParts2.length) lines.push(`Accessori: ${attParts2.join(', ')}`);
      const qty = this.state.config.quantita || 1;
      if (qty > 1) lines.push(`Quantità: ${qty}`);
      const srcEl = document.querySelector('.casm-price-amount');
      lines.push(`Stima prezzo: ${srcEl?.textContent || '—'}`);
      ta.value = lines.join('\n');
    }

    /* ── EVENT LISTENERS ── */
    setupEventListeners() {
      if (this._listenersAttached) return;
      this._listenersAttached = true;
      const root = document.getElementById('configuratore-aste-su-misura');
      if (!root) return;

      /* filtro battente (come colore/finitura: "Tutti" = '') */
      root.addEventListener('click', e => {
        const btn = e.target.closest('[data-battente]');
        if (!btn) return;
        this.state.filters.battente = btn.dataset.battente;
        this._renderBattenteFilter();
        this.renderGallery();
      });

      /* filtro colore */
      root.addEventListener('click', e => {
        const btn = e.target.closest('[data-colore]');
        if (!btn) return;
        this.state.filters.colore = btn.dataset.colore;
        root.querySelectorAll('[data-colore]').forEach(b => b.classList.toggle('active', b === btn));
        this.renderGallery();
      });

      /* filtro finitura */
      root.addEventListener('click', e => {
        const btn = e.target.closest('[data-finitura]');
        if (!btn) return;
        this.state.filters.finitura = btn.dataset.finitura;
        root.querySelectorAll('[data-finitura]').forEach(b => b.classList.toggle('active', b === btn));
        this.renderGallery();
      });

      /* selezione profilo */
      root.addEventListener('click', e => {
        const item = e.target.closest('.casm-gallery-item[data-sku]');
        if (!item) return;
        this.selectProfilo(item.dataset.sku);
      });

      /* preset dimensioni */
      root.addEventListener('click', e => {
        const btn = e.target.closest('.casm-preset');
        if (!btn) return;
        this.setDefaultDim(parseFloat(btn.dataset.base), parseFloat(btn.dataset.alt));
      });

      /* input dimensioni */
      const inputB = document.getElementById('casm-base');
      const inputA = document.getElementById('casm-altezza');
      const onDimChange = () => {
        const b = parseFloat(inputB?.value) || null;
        const a = parseFloat(inputA?.value) || null;
        this.state.config.base    = b;
        this.state.config.altezza = a;
        if (b && a) this._syncPresets(b, a);
        this.updateLiveView();
        this.updateRetroAvailability();
        this.syncRetro();
        this._updatePassLegnoState();
        this._updateStampaState();
        if (b && a) this._maybeInitAttaccaglie();
        this.renderAttaccaglieStep();
        this.calculateLive();
      };
      if (inputB) inputB.addEventListener('input', onDimChange);
      if (inputA) inputA.addEventListener('input', onDimChange);

      /* stampa (radio: foto / tela; riclic = nessuna) */
      root.addEventListener('change', e => {
        if (e.target.name !== 'casm-stampa') return;
        this.selectStampaTipo(e.target.value || null);
      });
      this._abilitaDeseleziona(root, 'casm-stampa', () => this.selectStampaTipo(null));

      /* fronte / retro (riclic = senza lastra / senza fondo) */
      root.addEventListener('change', e => {
        if (e.target.name === 'casm-fronte') this._setFronte(e.target.value);
        if (e.target.name === 'casm-retro')  this._setRetro(e.target.value);
      });
      this._abilitaDeseleziona(root, 'casm-fronte', () => this._setFronte(NESSUNO_SKU));
      this._abilitaDeseleziona(root, 'casm-retro',  () => this._setRetro(NESSUNO_SKU));

      /* ── toggle passepartout CARTONE ── */
      root.addEventListener('click', e => {
        const item = e.target.closest('[data-pass-cartone]');
        if (!item) return;
        if (item.classList.contains('casm-gallery-item--disabled')) return;
        const sku = item.dataset.passCartone;
        if (this.state.config.hasPassCartone && this.state.config.passCartoneSku === sku) {
          this.state.config.hasPassCartone = false;
          this.state.config.passCartoneSku = null;
          this.state.config.passCartone    = null;
          this._passCartoneImg = null; this._passCartoneImgUrl = '';
        } else {
          const p = this.state.passCartone.find(x => x.sku === sku);
          this.state.config.hasPassCartone = true;
          this.state.config.passCartoneSku = sku;
          this.state.config.passCartone    = p || null;
          this._loadPassCartoneImg(p?.imgUrl || '');
          // Aggiorna min/max/value dell'input larghezza
          const pcWidth = document.getElementById('casm-pass-cartone-largh');
          if (pcWidth && p) {
            const { base, altezza } = this.state.config;
            const minL = (p.larghezzaMinimaPass > 0) ? p.larghezzaMinimaPass : PASS_CARTONE_LARGH_MIN_CM;
            const maxL = Math.min(PASS_CARTONE_LARGH_MAX_CM,
              Math.floor(Math.min(p.lastraBase - base, p.lastraAlt - altezza) / 2 / 0.5) * 0.5);
            pcWidth.min   = minL;
            pcWidth.max   = maxL > minL ? maxL : minL;
            pcWidth.value = Math.min(maxL > minL ? maxL : minL, Math.max(minL, parseFloat(pcWidth.value) || minL));
            this.state.config.passCartoneLargh = parseFloat(pcWidth.value);
          }
        }
        this.renderPassCartoneGallery();
        this._updatePassCartoneUI();
        this._dispatchPassEvents();
        this.updateLiveView();
        this.updateRetroAvailability();  // il pass cambia il formato finito: il retro tavolo va rivalutato
        this.calculateLive();
      });

      /* larghezza bordo cartone */
      const pcWidth = document.getElementById('casm-pass-cartone-largh');
      if (pcWidth) {
        pcWidth.addEventListener('input', () => {
          const minL = (this.state.config.passCartone?.larghezzaMinimaPass > 0)
            ? this.state.config.passCartone.larghezzaMinimaPass : PASS_CARTONE_LARGH_MIN_CM;
          const val = parseFloat(pcWidth.value);
          // tetto = max dell'input (lastra e PASS_CARTONE_LARGH_MAX_CM, messo alla selezione)
          const maxL = parseFloat(pcWidth.max) > 0 ? parseFloat(pcWidth.max) : PASS_CARTONE_LARGH_MAX_CM;
          this.state.config.passCartoneLargh = isNaN(val) ? minL : Math.min(maxL, Math.max(minL, val));
          this._dispatchPassEvents();
          this.updateLiveView();
          this.updateRetroAvailability();  // il pass cambia il formato finito
          this.calculateLive();
        });
      }

      /* ── toggle passepartout LEGNO ── */
      root.addEventListener('click', e => {
        const item = e.target.closest('[data-pass-legno]');
        if (!item) return;
        const step = document.getElementById('casm-pass-legno-step');
        if (step?.classList.contains('casm-step--locked')) return;
        const sku = item.dataset.passLegno;
        if (this.state.config.hasPassLegno && this.state.config.passLegnoSku === sku) {
          this.state.config.hasPassLegno = false;
          this.state.config.passLegnoSku = null;
          this.state.config.passLegno    = null;
          this._passLegnoImg = null; this._passLegnoImgUrl = '';
          this._passLegnoPatternImg = null; this._passLegnoPatternUrl = '';
        } else {
          const p = this.state.passLegno.find(x => x.sku === sku);
          this.state.config.hasPassLegno = true;
          this.state.config.passLegnoSku = sku;
          this.state.config.passLegno    = p || null;
          this._loadPassLegnoImg(p?.imgUrl || '');
          this._loadPassLegnoPatternImg(p?.patternUrl || '');
        }
        this.renderPassLegnoGallery();
        this._dispatchPassEvents();
        this.updateLiveView();
        this.updateRetroAvailability();  // il pass cambia il formato finito
        this.calculateLive();
      });

      /* attaccaglie — qty stepper (per card) */
      root.addEventListener('click', e => {
        const btn = e.target.closest('[data-attacc-delta]');
        if (!btn) return;
        const sku   = btn.dataset.attaccSku;
        const delta = parseInt(btn.dataset.attaccDelta, 10);
        if (!sku || isNaN(delta)) return;
        const qtys = this.state.config.attaccaglieQtys;
        qtys[sku] = Math.max(0, (qtys[sku] || 0) + delta);
        this.state.config.attTouched = true;   // stop al ricalcolo automatico
        this.renderAttaccaglieStep();
        this.calculateLive();
      });

      /* attaccaglie — qty input diretto (per card) */
      root.addEventListener('input', e => {
        const sku = e.target.dataset.attaccSku;
        if (!sku || e.target.type !== 'number') return;
        const v = parseInt(e.target.value, 10);
        this.state.config.attaccaglieQtys[sku] = isNaN(v) || v < 0 ? 0 : v;
        this.state.config.attTouched = true;   // stop al ricalcolo automatico
        this.renderAttaccaglieStep();
        this.calculateLive();
      });

      /* artwork upload (input + overlay + tap canvas + ✕: cablaggio base) */
      this._wireArtworkUpload();

      /* quantità */
      const qtyInput = document.getElementById('casm-quantita');
      const qtyMinus = document.getElementById('casm-qty-minus');
      const qtyPlus  = document.getElementById('casm-qty-plus');
      if (qtyMinus) qtyMinus.addEventListener('click', () => this.changeQty(-1));
      if (qtyPlus)  qtyPlus.addEventListener('click',  () => this.changeQty(+1));
      if (qtyInput) qtyInput.addEventListener('change', () => this.onQtyChange());

      /* CTA */
      const ctaBtn = document.getElementById('casm-cta');
      if (ctaBtn) ctaBtn.addEventListener('click', () => {
        if (ctaBtn.dataset.ctaMode === 'cart') this._addToCart();
        else this.openModal();
      });

      /* modal chiudi */
      document.getElementById('casm-modal-close')?.addEventListener('click', () => this._closeModal());
      document.getElementById('casm-modal-cancel')?.addEventListener('click', () => this._closeModal());
      document.getElementById('casm-modal-overlay')?.addEventListener('click', e => {
        if (e.target.id === 'casm-modal-overlay') this._closeModal();
      });

      /* form submit */
      const form = document.getElementById('casm-modal-form');
      if (form) form.addEventListener('submit', () => {
        sessionStorage.setItem('casm_success', '1');
        const noteEl = document.getElementById('casm-modal-note');
        const bodyEl = document.getElementById('casm-modal-body');
        if (noteEl && bodyEl && noteEl.value.trim()) {
          bodyEl.value += '\n\nNote aggiuntive:\n' + noteEl.value.trim();
        }
        if (typeof window.fotorecordLeadConversion === 'function') {
          window.fotorecordLeadConversion('aste');
        }
      });

      /* reset */
      const resetBtn = document.getElementById('casm-reset');
      if (resetBtn) {
        resetBtn.addEventListener('click', () => {
          this.state.filters = { battente: '', colore: '', finitura: '' };
          this.state.config.profiloSku      = null;
          this.state.config.profilo         = null;
          this.state.config.hasPassLegno    = false;
          this.state.config.passLegnoSku    = null;
          this.state.config.passLegno       = null;
          this.state.config.hasPassCartone  = false;
          this.state.config.passCartoneSku  = null;
          this.state.config.passCartone     = null;
          this.state.config.attaccaglieQtys = {};
          this.state.config.attTouched      = false;   // reset → torna al conteggio automatico
          this.state.config.stampaTipo      = null;
          this.state.config.quantita        = 1;
          const qtyEl = document.getElementById('casm-quantita');
          if (qtyEl) qtyEl.value = '1';
          this.state.config.stampa          = null;
          this._frameImg        = null; this._frameImgUrl       = '';
          this._patternImg      = null; this._patternImgUrl     = '';
          this._passLegnoImg    = null; this._passLegnoImgUrl   = '';
          this._passLegnoPatternImg = null; this._passLegnoPatternUrl = '';
          this._passCartoneImg  = null; this._passCartoneImgUrl = '';
          this._artworkImg      = null; this._artworkFile       = null;
          this._effettoImg      = null; this._effettoImgUrl     = '';
          const ai = document.getElementById('casm-artwork-input');
          if (ai) ai.value = '';
          this._renderBattenteFilter();
          this._renderColoreFilter();
          this._renderFinituraFilter();
          this.renderPassCartoneGallery();
          this.renderPassLegnoGallery();
          this._updatePassCartoneUI();
          this._updatePassLegnoState();
          this.renderStampaOptions();
          this._updateStampaLegend();
          this.renderAttaccaglieStep();
          this.setDefaultDim(DEFAULT_BASE, DEFAULT_ALTEZZA);
          this.renderGallery();
          /* NB: la vecchia chiamata a _updateProfiloInfo() (metodo mai esistito →
             TypeError silenzioso a fine reset) è stata rimossa. */
        });
      }
    }

    _closeModal() {
      const overlay = document.getElementById('casm-modal-overlay');
      if (overlay) overlay.classList.remove('open');
      document.body.style.overflow = '';
    }

    _buildCartProperties() {
      const {
        base, altezza, profilo, fronte, retro,
        hasPassLegno, passLegno,
        hasPassCartone, passCartone, passCartoneLargh,
        stampa, stampaTipo,
      } = this.state.config;
      const attItems = this.state.accessori.filter(x => (x.categoria || '').includes('attaccaglie'));
      const attQtys  = this.state.config.attaccaglieQtys || {};
      const attParts = attItems.filter(a => (attQtys[a.sku] || 0) > 0).map(a => `${attQtys[a.sku]} × ${nb(a)}`);
      const props = {
        'Tipologia':         'Cornice su misura',
        'Dimensioni':        `${base} × ${altezza} cm`,
        'Profilo asta':      nb(profilo),
        'Fronte':            nb(fronte) || 'Nessuno',
        'Retro':             nb(retro) || 'Nessuno',
        '_prezzo_calcolato_pezzo': (this._lastCalcResult?.prezzoUnitario || 0).toFixed(2),
      };
      // Niente 'Quantità' fra le property (vale per le 4 classi e per il quiz): i pezzi
      // sono la `quantity` della riga carrello, che il cliente può cambiare dopo. Fino
      // al 16/09/2026 qui c'era una copia, ferma a ciò che era stato scelto prima di
      // ritoccare il carrello: al checkout diceva «Quantità: 1» accanto a «× 2», e in
      // produzione, finché vinceva lei, #1370 ha perso 3 pezzi pagati (08/09/2026).
      // Per la stessa ragione `_prezzo_calcolato_pezzo` è il prezzo calcolato di UN
      // pezzo (IVA inclusa, prima della fascia), invariante ai ritocchi del carrello:
      // fino al 16/09 la chiave era `_prezzo_calcolato` e portava il totale × pezzi.
      // È solo un cross-check del motore JS contro quello Python su ordini reali
      // (`ordini_articoli.estrai_articolo`): il prezzo di riga resta quello di Shopify.
      if (stampa && stampaTipo)          props['Stampa']                  = `${nb(stampa)}${stampaTipo === 'tela' ? ' - su telaio' : ''}`;
      if (hasPassLegno && passLegno)     props['Passepartout in legno']   = nb(passLegno);
      if (hasPassCartone && passCartone) props['Passepartout in cartone'] = `${nb(passCartone)} - bordo ${passCartoneLargh} cm`;
      if (attParts.length)               props['Accessori']               = attParts.join(', ');
      // Proprietà NASCOSTE (prefisso '_', Shopify le nasconde da carrello/checkout/email
      // cliente ma restano leggibili via Admin API — stessa convenzione di
      // '_prezzo_calcolato_pezzo'/'_anteprima'): portano id_db/sku grezzi così un ordine può
      // essere riletto in un preventivo senza indovinare il materiale da un nome in
      // chiaro (vedi core/fotorecord/shopify_ordini.py, _estrai_config_riga).
      props['_tipo_prodotto'] = 'aste';
      props['_profilo_id_db'] = profilo?.sku ? this._profiloCodice(profilo) : '';
      props['_fronte_sku']    = fronte?.sku || '';
      props['_retro_sku']     = retro?.sku  || '';
      if (stampa && stampaTipo) {
        props['_stampa_tipo'] = stampaTipo;
        props['_stampa_sku']  = stampa.sku || '';
      }
      if (hasPassCartone && passCartone) {
        props['_pp_cartone_sku']        = passCartone.sku || '';
        props['_pp_cartone_largh_cm']   = String(passCartoneLargh || '');
      }
      if (hasPassLegno && passLegno) props['_pp_legno_sku'] = passLegno.sku || '';
      if (attParts.length) {
        const attList = attItems.filter(a => (attQtys[a.sku] || 0) > 0).map(a => ({ sku: a.sku, qty: attQtys[a.sku] }));
        props['_accessori_json'] = JSON.stringify(attList);
      }
      return props;
    }
  }

  /* ══════════════════════════════════════════════════════════════════════════
     MONTATURE A GIORNO (cmg) — profilo sottile 9-slice + clip; niente passepartout.
     ══════════════════════════════════════════════════════════════════════════ */
  class ConfiguratoreMontature extends ConfiguratoreBase {
    constructor() {
      super('cmg', { logTag: 'CMG', ids: { cta: 'cmg-btn-preventivo' } });
      this.ctaIncompleteText  = 'Configura la montatura';
      this.leadKey            = 'montature';
      this.state = {
        profili:   [],
        fronti:    [],
        retri:     [],
        accessori: [],
        stampe:    [],
        filters:   { colore: '' },   // '' = tutti i colori
        config: {
          profiloSku: null,
          profilo:    null,
          base:       null,
          altezza:    null,
          fronteSku:  null,
          fronte:     null,
          retroSku:   null,
          retro:      null,
          quantita:   1,
          stampaTipo: null,    // null | 'foto'
          stampa:     null,
          clipQty:    null,
          clipMinQty: 4,
        },
        calcResult: null,
      };
      this._previewImg    = null;
      this._previewImgUrl = '';
      this.init();
    }

    _dataGlobal() { return window.SHOPIFY_MONTATURE_GIORNO; }

    /**
     * Override obbligatorio: una montatura e' un prodotto Shopify DISTINTO dall'asta pur
     * condividendo il profilo 142010, quindi ha un id_db suo col prefisso 'montatura'
     * (= il valore del metafield custom.id_db, vedi _applyLockedProfile). Ereditare la
     * versione base darebbe l'id_db dell'ASTA: stesso codice per due prodotti diversi.
     * Forma dichiarata in codice_prodotto.PREFISSO_MONTATURE (ADR 05/09/2026): non e'
     * un residuo da migrare, e' l'identita' della famiglia.
     * Lo sku arriva dal metaobject profili_montature_a_giorno, migrato a `frasNNNN` il
     * 18/08/2026; toIdDb resta a normalizzare, non a tradurre.
     */
    _profiloCodice(p) {
      const sku = p && p.sku ? p.sku : this.state.config.profiloSku;
      if (!sku) return '—';
      const core = window.FOTORECORD && window.FOTORECORD.core;
      return 'montatura' + ((core && core.toIdDb) ? core.toIdDb(sku) : sku);
    }

    /* ── INIT ── */
    init() {
      if (!window.SHOPIFY_MONTATURE_GIORNO) {
        console.error('[CMG] Dati non disponibili (SHOPIFY_MONTATURE_GIORNO undefined)');
        return;
      }
      this.loadData(window.SHOPIFY_MONTATURE_GIORNO);
      this.renderFilters();
      this.renderGallery();
      this.setupEventListeners();
      this.syncFronte();
      this.syncRetro();
      this.renderStampaOptions();
      this._checkSuccessQuery();
      this.setDefaultDim(20, 20);
      const locked = this._applyLockedProfile(window.SHOPIFY_MONTATURE_GIORNO);
      // Default = primo profilo DISPONIBILE, non il primo assoluto (vedi _primoProfiloUtile).
      const primo = locked ? null : this._primoProfiloUtile();
      if (primo) this.selectProfilo(primo.sku);
      if (!locked) this._applyUrlParams();
      this._mountMobileBar();
    }

    /* ── PROFILO BLOCCATO (pagina prodotto montatura, via metafield custom.id_db) ── */
    _applyLockedProfile(data) {
      const locked = data && data.lockedProfileSku;
      if (!locked) return false;
      // lockedProfileSku = id_db del prodotto = "montatura"+toIdDb(sku profilo).
      const core = window.FOTORECORD && window.FOTORECORD.core;
      const prof = this.state.profili.find(x => ('montatura' + (core ? core.toIdDb(x.sku) : '')) === locked);
      /* 'fallito' e non `false`: vedi _applyLockedProfile delle aste — un lock che non
         risolve non deve far ricadere la pagina sul primo profilo utile. */
      if (!prof) { this._segnalaLockFallito(locked); return 'fallito'; }
      this.selectProfilo(prof.sku);
      const step = document.getElementById('cmg-step-profilo');
      if (step) step.style.display = 'none';
      const root = document.getElementById('configuratore-montature-giorno');
      if (root) root.classList.add('cmg--locked');
      return true;
    }

    /* ── DEEP-LINK (kit / hub): ?sku=<sku|id_db>&base=&alt= ── */
    _applyUrlParams() {
      const p = new URLSearchParams(window.location.search);
      const sku = p.get('sku');
      if (sku) {
        const core = window.FOTORECORD && window.FOTORECORD.core;
        const prof = this.state.profili.find(x =>
          x.sku === sku || (core && ('montatura' + core.toIdDb(x.sku)) === sku) || (core && core.toIdDb(x.sku) === core.toIdDb(sku)));
        if (prof) this.selectProfilo(prof.sku);
      }
      const base = parseFloat(p.get('base'));
      const alt  = parseFloat(p.get('alt'));
      if (!isNaN(base) && !isNaN(alt)) this.setDefaultDim(base, alt);
    }

    /* ── CARICAMENTO DATI ── */
    loadData(data) {
      this.state.profili   = data.profili   || [];
      this.state.fronti    = data.fronti    || [];
      this.state.retri     = data.retri     || [];
      this.state.accessori = data.accessori || [];
      this.state.stampe    = (data.stampe   || []).filter(s => s.tipo === 'foto');

      // default fronte = VETRO (_defaultFronte, come la copy); retro = prima opzione reale.
      const dfFronte = this._defaultFronte();
      this.state.config.fronteSku = dfFronte?.sku || null;
      this.state.config.fronte    = dfFronte      || null;
      const dfRetro = this._defaultRetro();
      this.state.config.retroSku  = dfRetro?.sku || null;
      this.state.config.retro     = dfRetro      || null;

      window._CMG_FRONTE = this.state.config.fronte;
      window._CMG_RETRO  = this.state.config.retro;
      document.dispatchEvent(new CustomEvent('cmg:fronte-selected', { detail: this.state.config.fronte }));
      document.dispatchEvent(new CustomEvent('cmg:retro-selected',  { detail: this.state.config.retro  }));
      this._loadClipImg(data.clipUrl || '');
    }

    /* ── FILTRI COLORE ── */
    renderFilters() {
      const container = document.getElementById('cmg-filter-colore');
      if (!container) return;

      const colori = [...new Set(
        this.state.profili
          .map(p => (p.colore || '').trim())
          .filter(c => c !== '')
      )].sort();

      const pills = [
        { label: 'Tutti', value: '' },
        ...colori.map(c => ({ label: c, value: c })),
      ];

      container.innerHTML = pills.map(({ label, value }) =>
        `<button type="button" class="cmg-pill${value === this.state.filters.colore ? ' active' : ''}"
                 data-colore="${this._esc(value)}">${this._esc(label)}</button>`
      ).join('');
    }

    /* ── GALLERIA PROFILI ── */
    renderGallery() {
      const grid    = document.getElementById('cmg-gallery-profili');
      const counter = document.getElementById('cmg-gallery-count');
      if (!grid) return;

      const { colore } = this.state.filters;
      const filtered = colore
        ? this.state.profili.filter(p => (p.colore || '').trim() === colore)
        : this.state.profili;

      if (filtered.length === 0) {
        grid.innerHTML = '<div class="cmg-gallery-empty">Nessun profilo trovato per questo colore.</div>';
        if (counter) counter.textContent = '';
        return;
      }

      if (counter) counter.textContent = `${filtered.length} ${filtered.length === 1 ? 'profilo' : 'profili'}`;

      grid.innerHTML = filtered.map(p => {
        const selected = p.sku === this.state.config.profiloSku;
        // niente SKU grezzo come UI cliente: dal codice profilo si ricava
        // l'etichetta leggibile della tinta (es. "radica miele laccato")
        const tinta = (p.sku || '').replace(/^\d+-p[a-z]-/, '').replace(/-/g, ' ');
        /* La griglia usa `imgThumbUrl`, NON `imgUrl`: quest'ultima e' il master 1080
           che serve alla live view per il 9-slice, e a 36 celle peserebbe 5,9 MB. */
        const thumb = p.imgThumbUrl || p.imgUrl;
        const imgEl = thumb
          ? `<img class="cmg-gallery-item-img" src="${this._esc(this._thumbUrl(thumb))}" alt="" loading="lazy">`
          : `<div class="cmg-gallery-item-placeholder">${this._esc(tinta || p.nome || 'anteprima non disponibile')}</div>`;
        // Esaurito: stessa resa del configuratore aste — resta selezionabile, si marca.
        const esaurito = p.disponibile === false ? ' cmg-gallery-item--esaurito' : '';
        const badge    = p.disponibile === false
          ? `<span class="cmg-badge-esaurito">Esaurito</span>` : '';
        return `<div class="cmg-gallery-item${selected ? ' selected' : ''}${esaurito}"
                     role="option" aria-selected="${selected}"
                     data-sku="${this._esc(p.sku)}"
                     title="${this._esc(p.nome || p.sku)}">${imgEl}${badge}</div>`;
      }).join('');
      this._abilitaListbox(grid);
    }

    /* ── EVENT LISTENERS ── */
    setupEventListeners() {
      if (this._listenersAttached) return;
      this._listenersAttached = true;
      // Pill filtro colore
      const filterBar = document.getElementById('cmg-filter-colore');
      if (filterBar) {
        filterBar.addEventListener('click', e => {
          const pill = e.target.closest('.cmg-pill');
          if (!pill) return;
          this.state.filters.colore = pill.dataset.colore || '';
          filterBar.querySelectorAll('.cmg-pill').forEach(p =>
            p.classList.toggle('active', p.dataset.colore === this.state.filters.colore)
          );
          this.renderGallery();
        });
      }

      // Galleria profili
      const gallery = document.getElementById('cmg-gallery-profili');
      if (gallery) {
        gallery.addEventListener('click', e => {
          const item = e.target.closest('.cmg-gallery-item');
          if (!item) return;
          this.selectProfilo(item.dataset.sku);
        });
      }

      // Preset dimensioni
      const presetsEl = document.getElementById('cmg-presets');
      if (presetsEl) {
        presetsEl.addEventListener('click', e => {
          const btn = e.target.closest('.cmg-preset');
          if (!btn) return;
          const base    = parseFloat(btn.dataset.base);
          const altezza = parseFloat(btn.dataset.alt);
          const inputB  = document.getElementById('cmg-base');
          const inputA  = document.getElementById('cmg-altezza');
          if (inputB) inputB.value = base;
          if (inputA) inputA.value = altezza;
          this._syncPresets(base, altezza);
          this.state.config.base    = base;
          this.state.config.altezza = altezza;
          this.updateLiveView();
          this.updateRetroAvailability();
          this.calculateLive();
        });
      }

      // Input dimensioni (con debounce)
      const inputBase    = document.getElementById('cmg-base');
      const inputAltezza = document.getElementById('cmg-altezza');
      if (inputBase)    inputBase.addEventListener('input',    () => this._debounce('dim', () => this.onDimChange()));
      if (inputAltezza) inputAltezza.addEventListener('input', () => this._debounce('dim', () => this.onDimChange()));

      // Stampa (radio: nessuna / foto)
      document.addEventListener('change', e => {
        if (e.target.name !== 'cmg-stampa') return;
        this.selectStampaTipo(e.target.value || null);
      });
      this._abilitaDeseleziona(document, 'cmg-stampa', () => this.selectStampaTipo(null));

      // Fronte
      document.querySelectorAll('input[name="cmg-fronte"]').forEach(input =>
        input.addEventListener('change', e => this.selectFronte(e.target.value))
      );

      // Retro
      document.querySelectorAll('input[name="cmg-retro"]').forEach(input =>
        input.addEventListener('change', e => this.selectRetro(e.target.value))
      );

      // Quantità
      const qtyInput = document.getElementById('cmg-quantita');
      const qtyMinus = document.getElementById('cmg-qty-minus');
      const qtyPlus  = document.getElementById('cmg-qty-plus');
      if (qtyInput) qtyInput.addEventListener('input', () => this._debounce('qty', () => this.onQtyChange()));
      if (qtyMinus) qtyMinus.addEventListener('click', () => this.changeQty(-1));
      if (qtyPlus)  qtyPlus.addEventListener('click',  () => this.changeQty(+1));

      // Artwork upload (cablaggio base: input + overlay + tap canvas + ✕)
      this._wireArtworkUpload();

      // Reset
      const btnReset = document.getElementById('cmg-btn-reset');
      if (btnReset) btnReset.addEventListener('click', () => this.resetConfig());

      // CTA Preventivo
      const btnPreventivo = document.getElementById('cmg-btn-preventivo');
      if (btnPreventivo) btnPreventivo.addEventListener('click', () => {
        if (btnPreventivo.dataset.ctaMode === 'cart') this._addToCart();
        else this.openModal();
      });

      // Modal: chiudi
      const overlay    = document.getElementById('cmg-modal-overlay');
      const btnClose   = document.getElementById('cmg-modal-close');
      const btnAnnulla = document.getElementById('cmg-modal-cancel');
      if (overlay)    overlay.addEventListener('click',   e => { if (e.target === overlay) this.closeModal(); });
      if (btnClose)   btnClose.addEventListener('click',  () => this.closeModal());
      if (btnAnnulla) btnAnnulla.addEventListener('click', () => this.closeModal());

      // Form submit
      const form = document.getElementById('cmg-modal-form');
      if (form) form.addEventListener('submit', e => this.handleFormSubmit(e));

      // ESC chiude modal
      document.addEventListener('keydown', e => { if (e.key === 'Escape') this.closeModal(); });
    }

    /* ── SELEZIONE PROFILO ── */
    selectProfilo(sku) {
      const profilo = this.state.profili.find(p => p.sku === sku);
      if (!profilo) return;
      this.state.config.profiloSku = sku;
      this.state.config.profilo    = profilo;

      // Aggiorna selezione nella gallery
      document.querySelectorAll('#cmg-gallery-profili .cmg-gallery-item').forEach(item => {
        const sel = item.dataset.sku === sku;
        item.classList.toggle('selected', sel);
        item.setAttribute('aria-selected', sel);
      });

      this._updateEsauritoWarning();
      this.updateLiveView();
      this.calculateLive();
      window._CAOA_PROFILO = profilo;
      document.dispatchEvent(new CustomEvent('caoa:cornice-selected', { detail: profilo }));
    }

    _updateEsauritoWarning() {
      const el = document.getElementById('cmg-esaurito-warning');
      if (el) el.style.display = this.state.config.profilo?.disponibile === false ? '' : 'none';
    }

    /* ── LIVE VIEW CANVAS — 9-slice frame (profilo visivo 4mm) ── */
    updateLiveView() {
      const { base, altezza, profilo } = this.state.config;
      const canvas      = document.getElementById('cmg-live-canvas');
      const placeholder = document.getElementById('cmg-live-placeholder');
      if (!canvas || !placeholder) return;

      const hasData = base !== null && altezza !== null
                      && !isNaN(base) && !isNaN(altezza)
                      && base > 0 && altezza > 0;

      if (!hasData) {
        canvas.style.display      = 'none';
        placeholder.style.display = 'flex';
        this._syncArtworkUI();
        return;
      }

      canvas.style.display      = 'block';
      placeholder.style.display = 'none';
      this._syncArtworkUI();

      const imgUrl = profilo?.imgUrl || '';

      if (imgUrl && imgUrl !== this._previewImgUrl) {
        this._previewImgUrl = imgUrl;
        this._previewImg    = null;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        // scarta le risposte arrivate dopo un cambio profilo (race su click rapidi)
        img.onload = () => {
          if (imgUrl !== this._previewImgUrl) return;
          this._previewImg = img;
          this.updateLiveView();
        };
        img.onerror = () => { if (imgUrl !== this._previewImgUrl) return; this._previewImg = null; this.updateLiveView(); };
        img.src = imgUrl;
      } else if (!imgUrl) {
        this._previewImgUrl = '';
        this._previewImg    = null;
      }

      this._fitCanvas(canvas);
      this._drawPreview(canvas, base, altezza, profilo);
    }

    _drawPreview(canvas, base, altezza, profilo) {
      const { ctx, W, H } = this._canvasCtx(canvas);

      ctx.clearRect(0, 0, W, H);

      /* Catena sormonti: il profilo copre SORMONTO_CM per lato dell'opera →
         si disegna l'opera VISIBILE (opera − 2×sormonto). */
      const sormCm    = this.PRICING.SORMONTO_CM || 0;
      const operaVisW = Math.max(base    - 2 * sormCm, 0.1);
      const operaVisH = Math.max(altezza - 2 * sormCm, 0.1);
      const outerW_cm = operaVisW + 2 * PROF_VISIBILE_CM;
      const outerH_cm = operaVisH + 2 * PROF_VISIBILE_CM;

      const maxW  = W - SVG_PAD * 2;
      const maxH  = H - SVG_PAD * 2;
      const scale = Math.min(maxW / outerW_cm, maxH / outerH_cm);

      const rW = outerW_cm * scale;
      const rH = outerH_cm * scale;
      const fx = (W - rW) / 2;
      const fy = (H - rH) / 2;
      const profPx = PROF_VISIBILE_CM * scale;

      const ix = fx + profPx;
      const iy = fy + profPx;
      const iw = rW - 2 * profPx;
      const ih = rH - 2 * profPx;

      // Ombra
      this._ombraCornice(ctx, fx, fy, rW, rH);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(fx, fy, rW, rH);

      // Interno bianco
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(ix, iy, iw, ih);

      if (this._artworkImg) {
        this._drawImgCover(ctx, this._artworkImg, ix, iy, iw, ih);
      }

      const img = this._previewImg;
      if (img) {
        // 9-slice: immagine 20cm×20cm, bordo = 4mm = 4/200 della larghezza
        const slicePx = img.naturalWidth * (4 / 200);
        this._draw9Slice(ctx, img, slicePx, fx, fy, rW, rH, profPx);
      } else {
        // Fallback colore
        const fillColor = window.FOTORECORD.getProfiloColor(profilo);
        ctx.fillStyle = fillColor;
        ctx.fillRect(fx, fy, rW, profPx);
        ctx.fillRect(fx, fy + rH - profPx, rW, profPx);
        ctx.fillRect(fx, fy + profPx, profPx, rH - 2 * profPx);
        ctx.fillRect(fx + rW - profPx, fy + profPx, profPx, rH - 2 * profPx);
      }

      // Effetto stampa (lucida/tela) poi fronte (vetro/crilex)
      this._drawStampaEffetto(ctx, ix, iy, iw, ih, base);
      this._drawFronteEffetto(ctx, ix, iy, iw, ih);

      // Bordo interno
      ctx.strokeStyle = 'rgba(176,160,144,0.6)';
      ctx.lineWidth   = 0.8;
      ctx.strokeRect(ix, iy, iw, ih);

      // Clip metalliche sul bordo esterno del profilo
      if (this._clipImg) {
        this._drawClips(ctx, fx, fy, rW, rH, base, altezza);
      }

      // Le label mostrano le dimensioni ESTERNE (artwork + 4mm profilo per lato)
      const labelBase = this._round(outerW_cm, 1);
      const labelAlt  = this._round(outerH_cm, 1);
      this._drawLabels(ctx, W, H, fx, fy, rW, rH, labelBase, labelAlt);
    }

    _drawLabels(ctx, W, H, fx, fy, rW, rH, base, altezza) {
      const labelOffset = 14;
      const tickSize    = 5;
      ctx.strokeStyle = '#8b2635';
      ctx.lineWidth   = 1.2;
      ctx.fillStyle   = '#8b2635';
      ctx.font        = '600 13px Helvetica,Arial,sans-serif';
      ctx.textAlign   = 'center';
      ctx.textBaseline = 'top';

      // Base (sotto)
      const baseLineY = fy + rH + labelOffset;
      ctx.beginPath(); ctx.moveTo(fx, baseLineY); ctx.lineTo(fx + rW, baseLineY); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(fx, baseLineY - tickSize); ctx.lineTo(fx, baseLineY + tickSize); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(fx + rW, baseLineY - tickSize); ctx.lineTo(fx + rW, baseLineY + tickSize); ctx.stroke();
      ctx.fillText(`${base} cm`, fx + rW / 2, baseLineY + 3);

      // Altezza (destra)
      const altLineX = fx + rW + labelOffset;
      ctx.beginPath(); ctx.moveTo(altLineX, fy); ctx.lineTo(altLineX, fy + rH); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(altLineX - tickSize, fy); ctx.lineTo(altLineX + tickSize, fy); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(altLineX - tickSize, fy + rH); ctx.lineTo(altLineX + tickSize, fy + rH); ctx.stroke();
      ctx.save();
      ctx.translate(altLineX + 14, fy + rH / 2);
      ctx.rotate(Math.PI / 2);
      ctx.textAlign   = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${altezza} cm`, 0, 0);
      ctx.restore();
    }

    _drawClips(ctx, fx, fy, rW, rH, base, altezza) {
      const P         = this.PRICING;
      const img       = this._clipImg;
      /* stessa scala del _drawPreview (opera visibile = opera − 2×sormonto) */
      const outerW_cm = Math.max(base - 2 * (P.SORMONTO_CM || 0), 0.1) + 2 * PROF_VISIBILE_CM;
      const scale     = rW / outerW_cm;       // px per cm (scale uniforme)
      const clipW     = 0.4 * scale;           // 4mm — lato corto, lungo il bordo
      const clipH     = 0.5 * scale;           // 5mm — verso l'esterno

      const smallFmt  = Math.min(base, altezza) <= P.SOGLIA_CLIP_LATO_MIN
                        && Math.max(base, altezza) <= P.SOGLIA_CLIP_LATO_MAX;
      const nSide     = smallFmt ? P.N_CLIPS_PER_SIDE_STD : P.N_CLIPS_PER_SIDE_GR;
      const positions = nSide >= 2 ? [0.25, 0.75] : [0.5];

      const drawTop = cx =>
        ctx.drawImage(img, cx - clipW / 2, fy, clipW, clipH);
      const drawBottom = cx =>
        ctx.drawImage(img, cx - clipW / 2, fy + rH - clipH, clipW, clipH);
      const drawLeft = cy => {
        ctx.save();
        ctx.translate(fx, cy);
        ctx.rotate(-Math.PI / 2);
        ctx.drawImage(img, -clipW / 2, 0, clipW, clipH);
        ctx.restore();
      };
      const drawRight = cy => {
        ctx.save();
        ctx.translate(fx + rW, cy);
        ctx.rotate(Math.PI / 2);
        ctx.drawImage(img, -clipW / 2, 0, clipW, clipH);
        ctx.restore();
      };

      positions.forEach(t => {
        drawTop(fx + rW * t);
        drawBottom(fx + rW * t);
        drawLeft(fy + rH * t);
        drawRight(fy + rH * t);
      });
    }

    /* ── DIMENSIONI ── */
    setDefaultDim(base, altezza) {
      const inputB = document.getElementById('cmg-base');
      const inputA = document.getElementById('cmg-altezza');
      if (inputB) inputB.value = base;
      if (inputA) inputA.value = altezza;
      this.state.config.base    = base;
      this.state.config.altezza = altezza;
      this._syncPresets(base, altezza);
      this.updateLiveView();
      this.updateRetroAvailability();
      this._updateStampaState();
      this.calculateLive();
    }

    onDimChange() {
      const base    = parseFloat(document.getElementById('cmg-base')?.value)    || null;
      const altezza = parseFloat(document.getElementById('cmg-altezza')?.value) || null;
      this.state.config.base    = base;
      this.state.config.altezza = altezza;
      this._syncPresets(base, altezza);
      this.updateLiveView();
      this.updateRetroAvailability();
      this._updateStampaState();
      this.calculateLive();
    }

    /* ── FRONTE ── */
    selectFronte(sku) {
      const fronte = this.state.fronti.find(f => f.sku === sku);
      if (!fronte) return;
      this.state.config.fronteSku = sku;
      this.state.config.fronte    = fronte;
      this.syncFronte();
      window._CMG_FRONTE = fronte;
      document.dispatchEvent(new CustomEvent('cmg:fronte-selected', { detail: fronte }));
      this.calculateLive();
    }

    syncFronte() {
      document.querySelectorAll('input[name="cmg-fronte"]').forEach(input => {
        input.checked = input.value === this.state.config.fronteSku;
      });
      const legend = document.getElementById('cmg-legend-fronte');
      if (legend && this.state.config.fronte) {
        legend.textContent = `— ${this.state.config.fronte.nome}`;
      }
      this._loadEffettoImg(this.state.config.fronte?.effettoUrl || '');
    }

    /* ── RETRO ── */
    selectRetro(sku) {
      const retro = this.state.retri.find(r => r.sku === sku);
      if (!retro) return;
      this.state.config.retroSku = sku;
      this.state.config.retro    = retro;
      this.syncRetro();
      window._CMG_RETRO = retro;
      document.dispatchEvent(new CustomEvent('cmg:retro-selected', { detail: retro }));
      this.calculateLive();
    }

    syncRetro() {
      document.querySelectorAll('input[name="cmg-retro"]').forEach(input => {
        input.checked = input.value === this.state.config.retroSku;
      });
      const legend = document.getElementById('cmg-legend-retro');
      if (legend && this.state.config.retro) {
        legend.textContent = `— ${this.state.config.retro.nome}`;
      }
    }

    updateRetroAvailability() {
      const { base, altezza } = this.state.config;
      const retroTavoloInput  = document.getElementById('cmg-retro-tavolo');
      const warning           = document.getElementById('cmg-tavolo-warning');
      if (!retroTavoloInput) return;

      const retro = this.state.retri.find(r => r.sku && isRetroTavolo(r));
      /* Il radio "da tavolo" è nel DOM ma nessun retro si dichiara tale: il dato manca e
         senza gate un formato non producibile arriverebbe al carrello. Si dice. */
      if (!retro) {
        console.error('[CMG] nessun retro con montaggio="tavolo" nel dataset: '
                    + 'gate sul formato non applicato', this.state.retri);
        return;
      }

      const { maxB, maxA } = limitiRetroTavolo(retro, 'CMG');
      const fits = (base !== null && altezza !== null) && (
        (base <= maxB && altezza <= maxA) ||
        (altezza <= maxB && base <= maxA)
      );

      retroTavoloInput.disabled = !fits;
      if (warning) warning.classList.toggle('is-visible', !fits && base !== null && altezza !== null);

      // Se il retro tavolo non è disponibile e era selezionato, passa al primo retro disponibile
      if (!fits && isRetroTavolo(this.state.config.retro)) {
        const parete = this.state.retri.find(r => r.sku && !isRetroTavolo(r));
        if (parete) this.selectRetro(parete.sku);
      }
    }

    /* ── RESET ── */
    resetConfig() {
      this.state.config = {
        profiloSku: null,
        profilo:    null,
        base:       null,
        altezza:    null,
        fronteSku:  this.state.fronti[0]?.sku || null,
        fronte:     this.state.fronti[0]      || null,
        retroSku:   this._defaultRetro()?.sku || null,
        retro:      this._defaultRetro()      || null,
        quantita:   1,
        stampaTipo: null,
        stampa:     null,
        clipQty:    null,
        clipMinQty: 4,
      };
      this.state.filters.colore = '';

      const qtyEl  = document.getElementById('cmg-quantita');
      if (qtyEl)  qtyEl.value  = '1';
      document.querySelectorAll('.cmg-preset').forEach(b => b.classList.remove('is-active'));

      // Riabilita MDF tavolo
      const tavoloInput = document.getElementById('cmg-retro-tavolo');
      if (tavoloInput) tavoloInput.disabled = false;
      const warning = document.getElementById('cmg-tavolo-warning');
      if (warning) warning.classList.remove('is-visible');

      this._artworkImg  = null;
      this._artworkFile = null;
      const ai = document.getElementById('cmg-artwork-input');
      if (ai) ai.value = '';
      this._artworkNotice('', 'clear');

      // Ridisegna filtri e gallery
      this.renderFilters();
      this.renderGallery();
      this.syncFronte();
      this.syncRetro();
      this.renderStampaOptions();
      this._updateStampaLegend();
      this.setDefaultDim(20, 20);
    }

    /* ── VALIDAZIONE ── */
    validateConfig() {
      const { base, altezza, profilo } = this.state.config;
      const errors   = [];
      const warnings = [];

      if (!profilo) {
        errors.push('Scegli un profilo montatura dalla galleria.');
      }

      const minBase = (profilo && profilo.dimMinBase > 0) ? profilo.dimMinBase : 9;
      const minAlt  = (profilo && profilo.dimMinAlt > 0) ? profilo.dimMinAlt : 9;

      if (base === null || isNaN(base)) {
        errors.push('Inserisci la misura base (larghezza).');
      } else if (base < minBase) {
        errors.push(`Base minima: ${minBase} cm.`);
      }

      if (altezza === null || isNaN(altezza)) {
        errors.push('Inserisci la misura altezza.');
      } else if (altezza < minAlt) {
        errors.push(`Altezza minima: ${minAlt} cm.`);
      }

      if (base !== null && altezza !== null && !isNaN(base) && !isNaN(altezza)) {
        const f = this._getFascia(base, altezza);
        if (f.soloPreventivo) {
          warnings.push('Formato grande: configurazione disponibile solo su preventivo.');
        } else if (f.supplementoGrande > 0) {
          warnings.push('Per formati con un lato oltre ' + this.PRICING.SOGLIA_SUPPLEMENTO_CM + ' cm, il trasporto avviene in imballaggio rinforzato.');
        }
      }

      const vincoloCrilex = this._vincoloCrilex(base, altezza);
      if (vincoloCrilex) errors.push(vincoloCrilex);

      return { valid: errors.length === 0, errors, warnings };
    }

    /* ── CALCOLO PREZZO ── */
    calcola() {
      const { config } = this.state;
      const { base, altezza, profilo, fronte, retro, quantita, stampa, stampaTipo } = config;
      if (!profilo || base === null || altezza === null) return null;

      // Markup per fascia formato
      const fascia         = this._getFascia(base, altezza);
      const soloPreventivo = fascia.soloPreventivo;
      /* flag esplicito della fascia (30/07/2026): prima si deduceva da
         `supplementoGrande > 0`, cioè dall'importo dell'imballo — vedi FR.getFascia. */
      const isGrande       = fascia.isGrande;
      /* markup PER-COMPONENTE: profilo/fronte/retro/stampa portano markup (std) +
         markupGrande. Il profilo montatura li prende dal metaobject
         `profili_montature_a_giorno`: fino al 18/07/2026 qui c'era un 2,9 cablato e in
         fascia grande si applicava lo standard invece del 3,5 del DB. */
      const mk = this._makeMk(isGrande);

      /* La formula vive in FOTORECORD.montaturaNetto: la usa anche il modo "montatura"
         del configuratore opere, e deve restare UNA. */
      const c = window.FOTORECORD.montaturaNetto({
        base, altezza, profilo, fronte, retro,
        stampa:   (stampa && stampaTipo === 'foto') ? stampa : null,
        profCm:   this.PRICING.PROF_CM,   // 1.4 cm — sezione 142010
        nClips:   this.state.config.clipQty || window.FOTORECORD.numeroClip(base, altezza, this.PRICING),
        clipUnit: this._clipCostoUnitario(),
        mk,
      });
      const { costoAsta, costoFronte, costoRetro, costoStampa, costoClip, nClips } = c;
      let imponibile = c.netto;
      /* Dato incompleto → niente prezzo: `calculateLive` tratta null come "non calcolabile". */
      if (this._markupMancante(mk)) return null;
      if (fascia.supplementoGrande > 0) imponibile += window.FOTORECORD.imballoNetto(fascia);

      const prezzoUnitario = this._round(imponibile * (1 + this.PRICING.IVA), 2);
      const prezzoTotale   = this._round(prezzoUnitario * quantita, 2);

      return {
        soloPreventivo,
        prezzoUnitario,
        prezzoTotale,
        breakdown: { costoAsta, costoFronte, costoRetro, costoClip, costoStampa, nClips, imponibile, isGrande, stampa, stampaTipo, vendita: c.vendita },
      };
    }

    _clipCostoUnitario() {
      // Prezzo clip = prezzo finale IVA incl./pz da accessori_configuratore, riportato a netto.
      const clip = this.state.accessori.find(a =>
        (a.categoria || '').toLowerCase().includes('attaccaglie')
      );
      const prezzo = (clip && clip.prezzo != null) ? clip.prezzo : 0.10;
      return prezzo / (1 + this.PRICING.IVA);
    }

    calculateLive() {
      const validation = this.validateConfig();
      this._renderValidation(validation);
      const result = validation.valid ? this.calcola() : null;
      this.state.calcResult = result;
      // Carrello nativo: fascia di prezzo (variante reale) col prezzo >= a quello calcolato.
      this._bucket = (result && !result.soloPreventivo) ? this._pickBucket(result.prezzoUnitario) : null;
      this._renderPrice(result);
      this._updateClipMin();
      this._updateCta(result);
      /* Clip comprese nel prezzo → nessun importo a parte (accessori: null). */
      const v = (result && !result.soloPreventivo) ? result.breakdown.vendita : null;
      this._publishPrezziComponenti(v ? { profilo: v.asta, fronte: v.fronte, retro: v.retro, stampa: v.stampa, accessori: null } : null);
    }

    _renderValidation({ errors, warnings }) {
      const area = document.getElementById('cmg-validation');
      if (!area) return;
      area.innerHTML = [
        ...errors.map(e   => `<div class="cmg-msg error"><span>✕</span><span>${this._esc(e)}</span></div>`),
        ...warnings.map(w => `<div class="cmg-msg warning"><span>Attenzione:</span><span>${this._esc(w)}</span></div>`),
      ].join('');
    }

    _renderPrice(result) {
      const priceEl    = document.getElementById('cmg-price-total');
      const unitNote   = document.getElementById('cmg-price-unit-note');
      const extraNote  = document.getElementById('cmg-extra-grande-note');
      const priceElM   = document.getElementById('cmg-price-total-m');
      const unitNoteM  = document.getElementById('cmg-price-unit-note-m');
      const extraNoteM = document.getElementById('cmg-extra-grande-note-m');
      const { config } = this.state;

      // Profilo esaurito: nessun prezzo acquistabile, come nel configuratore aste.
      // Non è un `return`: le righe informative in coda (legenda dimensioni, dimensioni
      // esterne) devono continuare ad aggiornarsi mentre il cliente compila la richiesta.
      const esaurito = config.profilo?.disponibile === false;
      this._rigaSpedizione(false);

      // Carrello nativo: se c'è una fascia, mostro il prezzo della fascia (quello che il cliente paga).
      const useBucket = !!(result && !result.soloPreventivo && this._bucket);
      const dispUnit  = useBucket ? this._bucket.c / 100 : (result ? result.prezzoUnitario : 0);
      const dispTotal = useBucket ? dispUnit * (config.quantita || 1) : (result ? result.prezzoTotale : 0);

      const _sync = (el, un, en) => {
        if (esaurito) {
          if (el) { el.textContent = 'Profilo non disponibile'; el.setAttribute('data-placeholder', 'true'); }
          if (un) un.style.display = 'none';
          if (en) en.style.display = 'none';
        } else if (result && result.soloPreventivo) {
          if (el) { el.textContent = this._fmt(result.prezzoTotale); el.removeAttribute('data-placeholder'); }
          if (un) un.style.display = 'none';
          if (en) { en.textContent = 'Stima indicativa — il prezzo definitivo, spedizione compresa, viene confermato via email'; en.style.display = 'inline'; }
        } else if (result) {
          if (el) { el.textContent = this._fmt(dispTotal); el.removeAttribute('data-placeholder'); }
          if (un) {
            if (config.quantita > 1) { un.textContent = ` — ${this._fmt(dispUnit)} cad.`; un.style.display = 'inline'; }
            else { un.style.display = 'none'; }
          }
          this._rigaSpedizione(true);
          // Nota extra: solo supplemento grande se applicabile
          if (en) {
            const f = this._getFascia(config.base, config.altezza);
            if (f.supplementoGrande > 0) { en.textContent = `Supplemento formato grande: +${this._fmt(f.supplementoGrande)}`; en.style.display = 'inline'; }
            else { en.style.display = 'none'; }
          }
        } else {
          if (el) { el.textContent = 'Scegli profilo e dimensioni'; el.setAttribute('data-placeholder', 'true'); }
          if (un) un.style.display = 'none';
          if (en) en.style.display = 'none';
        }
      };

      _sync(priceEl, unitNote, extraNote);
      _sync(priceElM, unitNoteM, extraNoteM);

      // Aggiorna legend dimensioni
      const legendDim = document.getElementById('cmg-legend-dim');
      if (legendDim) {
        legendDim.textContent = (config.base && config.altezza)
          ? `— ${config.base} × ${config.altezza} cm` : '';
      }

      // Dimensioni esterne = opera visibile (− 2×sormonto) + 4mm profilo per lato
      const dimEstEl = document.getElementById('cmg-dim-esterne');
      if (dimEstEl) {
        if (config.base && config.altezza) {
          const _s   = this.PRICING.SORMONTO_CM || 0;
          const extB = this._round(config.base    - 2 * _s + 2 * PROF_VISIBILE_CM, 1);
          const extA = this._round(config.altezza - 2 * _s + 2 * PROF_VISIBILE_CM, 1);
          dimEstEl.textContent = `Dimensioni esterne: ${extB} × ${extA} cm`;
          dimEstEl.style.display = '';
        } else {
          dimEstEl.style.display = 'none';
        }
      }
    }

    _updateCta(result) {
      // Esaurito prima di tutto: ctaState restituisce "Richiedi disponibilità" in modalità
      // preventivo e ATTIVA — il cliente deve poter mandare la richiesta, non trovarsi un
      // bottone spento. Passare `result` qui sotto lo porterebbe invece al carrello.
      if (this.state.config.profilo?.disponibile === false) { this._setCta({ esaurito: true }); return; }
      this._setCta({ result: result, bucket: this._bucket, incompleteText: this.ctaIncompleteText });
    }

    _buildCartProperties() {
      const { config, calcResult } = this.state;
      const props = {
        'Tipologia':         'Montatura a giorno',
        'Profilo montatura': nb(config.profilo),
        'Dimensioni':        `${config.base} × ${config.altezza} cm`,
        'Fronte':            nb(config.fronte)  || '—',
        'Retro':             nb(config.retro)   || '—',
        'Clip':              `${this.state.config.clipQty || 0} pz`,
        '_prezzo_calcolato_pezzo': (calcResult?.prezzoUnitario || 0).toFixed(2),
      };
      if (config.stampa && config.stampaTipo) props['Stampa'] = nb(config.stampa);
      // Proprietà nascoste per la ricostruzione lato preventivi — vedi il commento
      // gemello in ConfiguratoreCornice._buildCartProperties. Qui il profilo porta il
      // prefisso letterale 'montatura' (vedi _profiloCodice di questa classe): lo SKU
      // grezzo del profilo (senza prefisso) sta in config.profilo.sku.
      props['_tipo_prodotto'] = 'montatura';
      props['_profilo_id_db'] = config.profilo?.sku || '';
      props['_fronte_sku']    = config.fronte?.sku || '';
      props['_retro_sku']     = config.retro?.sku  || '';
      props['_clip_qty']      = String(this.state.config.clipQty || 0);
      if (config.stampa && config.stampaTipo) {
        props['_stampa_tipo'] = config.stampaTipo;
        props['_stampa_sku']  = config.stampa.sku || '';
      }
      return props;
    }

    /* ── MODAL ── */
    openModal() {
      if (!this.state.calcResult) return;
      this._populateRecap();
      const overlay = document.getElementById('cmg-modal-overlay');
      if (overlay) {
        overlay.classList.add('open');
        document.body.style.overflow = 'hidden';
        setTimeout(() => overlay.querySelector('input[type="text"]')?.focus(), 150);
      }
    }

    closeModal() {
      const overlay = document.getElementById('cmg-modal-overlay');
      if (overlay) {
        overlay.classList.remove('open');
        document.body.style.overflow = '';
      }
    }

    _populateRecap() {
      const { config, calcResult } = this.state;
      if (!calcResult) return;

      const rows = [
        ['Profilo montatura',  nb(config.profilo) || '—'],
        ['Dimensioni interne', `${config.base} × ${config.altezza} cm`],
        ['Fronte',             nb(config.fronte)  || '—'],
        ['Retro',              nb(config.retro)   || '—'],
        ['Accessori',          `${this.state.config.clipQty || 0} clip`],
        ['Quantità',           String(config.quantita)],
      ];
      if (config.stampa && config.stampaTipo) {
        rows.splice(4, 0, ['Stampa', nb(config.stampa)]);
      }

      const recapBody = document.getElementById('cmg-modal-recap-rows');
      if (recapBody) {
        recapBody.innerHTML = rows.map(([k, v]) => `
          <div class="cmg-modal-recap-row">
            <span>${k}</span>
            <span>${v}</span>
          </div>`
        ).join('');
      }

      const priceEl = document.getElementById('cmg-modal-price-value');
      if (priceEl) priceEl.textContent = calcResult.soloPreventivo ? `~${this._fmt(calcResult.prezzoTotale)} (stima)` : this._fmt(calcResult.prezzoTotale);

      const unitEl = document.getElementById('cmg-modal-price-unit');
      if (unitEl && config.quantita > 1 && !calcResult.soloPreventivo) {
        unitEl.textContent   = `(${config.quantita} × ${this._fmt(calcResult.prezzoUnitario)})`;
        unitEl.style.display = 'inline';
      } else if (unitEl) {
        unitEl.style.display = 'none';
      }
    }

    _buildBodyField() {
      const { config, calcResult } = this.state;

      const newsletter = document.getElementById('cmg-check-newsletter');
      const noteEl     = document.getElementById('cmg-modal-note');

      // Profilo esaurito → la richiesta è di DISPONIBILITÀ, non un preventivo: mandare il
      // corpo standard significherebbe spedire al cliente un prezzo per qualcosa che non
      // possiamo fare. Va prima del guard su calcResult: qui il calcolo esiste ancora
      // (calcola() non conosce la disponibilità) e passerebbe di lì senza accorgersene.
      if (config.profilo?.disponibile === false) {
        const richiesta = [
          '=== RICHIESTA DISPONIBILITÀ PROFILO — FOTORECORD ===',
          '',
          `Profilo asta  : ${nb(config.profilo) || '—'}`,
          (config.base && config.altezza) ? `Dimensioni    : ${config.base} × ${config.altezza} cm` : null,
          '',
          'Vorrei sapere quando questo profilo tornerà disponibile.',
          '',
          'NOTE CLIENTE',
          noteEl?.value?.trim() || '(nessuna)',
          '',
          `Newsletter    : ${newsletter?.checked ? 'sì' : 'no'}`,
          'GDPR          : accettato',
          'Origine       : Configuratore Montature a Giorno / fotorecord.it',
        ].filter(l => l !== null).join('\n');
        const hidden = document.getElementById('cmg-modal-body');
        if (hidden) hidden.value = richiesta;
        return;
      }

      if (!calcResult) return;

      const stampaLine = (config.stampa && config.stampaTipo)
        ? `Stampa        : ${nb(config.stampa)} (${this._componenteCodice(config.stampa)})`
        : null;

      const lines = [
        '=== PREVENTIVO MONTATURA A GIORNO — FOTORECORD ===',
        '',
        'CONFIGURAZIONE',
        `Profilo asta  : ${nb(config.profilo) || '—'}`,
        `Dimensioni    : ${config.base} × ${config.altezza} cm`,
        `Fronte        : ${nb(config.fronte)  || '—'}`,
        `Retro         : ${nb(config.retro)   || '—'}`,
        stampaLine,
        `Clip          : ${this.state.config.clipQty || 0} pz`,
        `Quantità      : ${config.quantita}`,
        '',
        'PREZZO STIMATO (IVA 22% inclusa)',
        calcResult.soloPreventivo
          ? `Stima         : ~${this._fmt(calcResult.prezzoTotale)} (indicativa, lato > 105 cm)`
          : `Unitario      : ${this._fmt(calcResult.prezzoUnitario)}`,
        calcResult.soloPreventivo ? null : `Totale        : ${this._fmt(calcResult.prezzoTotale)}`,
        calcResult.breakdown.isGrande
          ? `Nota          : Include supplemento formato grande (+${this._fmt(this._getFascia(config.base, config.altezza).supplementoGrande)})` : null,
        '',
        'NOTE CLIENTE',
        noteEl?.value?.trim() || '(nessuna)',
        '',
        `Newsletter    : ${newsletter?.checked ? 'sì' : 'no'}`,
        'GDPR          : accettato',
        'Origine       : Configuratore Montature a Giorno / fotorecord.it',
      ].filter(l => l !== null).join('\n');

      const hiddenBody = document.getElementById('cmg-modal-body');
      if (hiddenBody) hiddenBody.value = lines;
    }

    /* ── UTILS (formato prezzi/round come il vecchio CMG) ── */
    _round(value, decimals) {
      const m = Math.pow(10, decimals);
      return Math.round(value * m) / m;
    }
    _fmt(value) { return FR.fmtEuro(value); }
  }

  /* ══════════════════════════════════════════════════════════════════════════
     PANNELLI SOLO VETRO (cpv) — nessun profilo; lastra + clip.
     Preview PORTATA A CANVAS (prima era SVG): stesso percorso di cattura
     anteprima/CORS degli altri tipi.
     ══════════════════════════════════════════════════════════════════════════ */
  class ConfiguratorePannelli extends ConfiguratoreBase {
    constructor() {
      super('cpv', { logTag: 'CPV', ids: { cta: 'cpv-btn-preventivo' }, pricing: { DIM_MIN: 9 } });
      this.ctaIncompleteText  = 'Inserisci le dimensioni';
      this.leadKey            = 'pannelli';
      this.state = {
        fronti:   [],
        retri:    [],
        stampe:   [],
        config: {
          base:       null,
          altezza:    null,
          /* null = risolto in loadData via _defaultFronte (vetro, come la copy).
             Il crilex cablato qui era il motivo per cui i pannelli partivano col
             materiale sbagliato — e più caro — rispetto alla copy della section. */
          fronteSku:  null,
          fronte:     null,
          /* null = risolto in loadData via _defaultRetro, come il fronte qui sopra.
             Lo sku cablato era 'mdf-parete', con il trattino: gli sku veri sono
             'mdfparete'/'mdftavolo' (to_id_db li scrive senza separatori), quindi non
             corrispondeva a NIENTE — nessun radio si accendeva allo step Retro mentre
             riepilogo e prezzo mostravano il retro giusto (controllo-sito 21/08). */
          retroSku:   null,
          retro:      null,
          quantita:   1,
          stampaTipo: null,    // null | 'foto'
          stampa:     null,
          clipQty:    null,
          clipMinQty: 4,
        },
        calcResult: null,
      };
      this.init();
    }

    _dataGlobal() { return window.SHOPIFY_PANNELLI_SOLO_VETRO; }

    /* ── INIT ── */
    init() {
      if (!window.SHOPIFY_PANNELLI_SOLO_VETRO) {
        console.error('[CPV] Dati non disponibili (SHOPIFY_PANNELLI_SOLO_VETRO undefined)');
        return;
      }
      this.loadData(window.SHOPIFY_PANNELLI_SOLO_VETRO);
      this.setupEventListeners();
      this.syncFronte();
      this.syncRetro();
      this.renderStampaOptions();
      this._checkSuccessQuery();
      this.setDefaultDim(20, 20);  // formato di default: 20×20
      this._applyUrlParams();
      this._mountMobileBar();
    }

    /* ── DEEP-LINK (kit / hub): ?base=&alt=&fronte= ── */
    _applyUrlParams() {
      const p = new URLSearchParams(window.location.search);
      const fronteSku = p.get('fronte');
      if (fronteSku) {
        /* Confine URL → lookup id_db-normalizzato (vedi ConfiguratoreBase._findBySku).
           Il deep-link kit vetro passava una chiave inesistente e questo find falliva
           in silenzio, lasciando il default del costruttore — che è CRILEX: kit vetro
           → crilex preselezionato, materiale diverso e più caro. */
        const f = this._findBySku(this.state.fronti, fronteSku);
        if (f) this.selectFronte(f.sku);
      }
      const base = parseFloat(p.get('base'));
      const alt  = parseFloat(p.get('alt'));
      if (!isNaN(base) && !isNaN(alt)) this.setDefaultDim(base, alt);
    }

    /* ── CARICAMENTO DATI ── */
    loadData(data) {
      this.state.fronti    = data.fronti || [];
      this.state.retri     = data.retri  || [];
      this.state.stampe    = (data.stampe || []).filter(s => s.tipo === 'foto');
      this.state.accessori = data.accessori || [];
      this.state.config.fronte = this.state.fronti.find(f => f.sku === this.state.config.fronteSku)
        || this._defaultFronte();
      this.state.config.fronteSku = this.state.config.fronte?.sku || null;
      this.state.config.retro  = this.state.retri.find(r => r.sku === this.state.config.retroSku)
        || this._defaultRetro() || null;
      /* Riallineare lo SKU all'oggetto risolto, come fa il fronte due righe sopra: senza,
         `config.retro` è giusto (riepilogo e prezzo tornano) ma `config.retroSku` resta il
         valore di partenza, e syncRetro() confronta i radio contro uno sku che non esiste. */
      this.state.config.retroSku = this.state.config.retro?.sku || null;

      // Clip: prezzo finale IVA incl./pz da accessori_configuratore → COSTO_CLIP a netto
      const clip = (data.accessori || []).find(a => a.categoria === 'attaccaglie');
      if (clip && clip.prezzo != null) {
        this.PRICING.COSTO_CLIP = clip.prezzo / (1 + this.PRICING.IVA);
      }

      window._CPV_RETRO = this.state.config.retro;
      document.dispatchEvent(new CustomEvent('cpv:retro-selected', { detail: this.state.config.retro }));
      this._loadClipImg(data.clipUrl || '');
    }

    /* ── EVENT LISTENERS ── */
    setupEventListeners() {
      if (this._listenersAttached) return;
      this._listenersAttached = true;
      // Preset dimensioni
      const presetsEl = document.getElementById('cpv-presets');
      if (presetsEl) {
        presetsEl.addEventListener('click', e => {
          const btn = e.target.closest('.cpv-preset');
          if (!btn) return;
          const base    = parseFloat(btn.dataset.base);
          const altezza = parseFloat(btn.dataset.alt);
          const inputB  = document.getElementById('cpv-base');
          const inputA  = document.getElementById('cpv-altezza');
          if (inputB) inputB.value = base;
          if (inputA) inputA.value = altezza;
          this._syncPresets(base, altezza);
          this.state.config.base    = base;
          this.state.config.altezza = altezza;
          this.updateLiveView(base, altezza);
          this.updateRetroAvailability();
          this.calculateLive();
        });
      }

      // Input dimensioni
      const inputBase    = document.getElementById('cpv-base');
      const inputAltezza = document.getElementById('cpv-altezza');
      if (inputBase)    inputBase.addEventListener('input',    () => this._debounce('dim', () => this.onDimChange()));
      if (inputAltezza) inputAltezza.addEventListener('input', () => this._debounce('dim', () => this.onDimChange()));

      // Stampa (radio: nessuna / foto)
      document.addEventListener('change', e => {
        if (e.target.name !== 'cpv-stampa') return;
        this.selectStampaTipo(e.target.value || null);
      });
      this._abilitaDeseleziona(document, 'cpv-stampa', () => this.selectStampaTipo(null));

      // Fronte / Retro: option card radio change event
      document.querySelectorAll('input[name="cpv-fronte"]').forEach(input => {
        input.addEventListener('change', e => this.selectFronte(e.target.value));
      });
      document.querySelectorAll('input[name="cpv-retro"]').forEach(input => {
        input.addEventListener('change', e => this.selectRetro(e.target.value));
      });

      // Quantità
      const qtyInput = document.getElementById('cpv-quantita');
      const qtyMinus = document.getElementById('cpv-qty-minus');
      const qtyPlus  = document.getElementById('cpv-qty-plus');
      if (qtyInput) qtyInput.addEventListener('input', () => this._debounce('qty', () => this.onQtyChange()));
      if (qtyMinus) qtyMinus.addEventListener('click', () => this.changeQty(-1));
      if (qtyPlus)  qtyPlus.addEventListener('click',  () => this.changeQty(+1));

      // Artwork upload (cablaggio base: input + overlay + tap canvas + ✕)
      this._wireArtworkUpload();

      // Reset
      const btnReset = document.getElementById('cpv-btn-reset');
      if (btnReset) btnReset.addEventListener('click', () => this.resetConfig());

      // Bottone preventivo
      const btnPreventivo = document.getElementById('cpv-btn-preventivo');
      if (btnPreventivo) btnPreventivo.addEventListener('click', () => {
        if (btnPreventivo.dataset.ctaMode === 'cart') this._addToCart();
        else this.openModal();
      });

      // Modal: chiudi
      const overlay    = document.getElementById('cpv-modal-overlay');
      const btnClose   = document.getElementById('cpv-modal-close');
      const btnAnnulla = document.getElementById('cpv-modal-cancel');
      if (overlay)    overlay.addEventListener('click',   e => { if (e.target === overlay) this.closeModal(); });
      if (btnClose)   btnClose.addEventListener('click',  () => this.closeModal());
      if (btnAnnulla) btnAnnulla.addEventListener('click', () => this.closeModal());

      // Form submit
      const form = document.getElementById('cpv-modal-form');
      if (form) form.addEventListener('submit', e => this.handleFormSubmit(e));

      // ESC
      document.addEventListener('keydown', e => { if (e.key === 'Escape') this.closeModal(); });
    }

    /* ── LIVE VIEW CANVAS (pannello proporzionale alle dimensioni) ── */
    updateLiveView(base, altezza) {
      if (base === undefined) { base = this.state.config.base; altezza = this.state.config.altezza; }
      const canvas      = document.getElementById('cpv-live-canvas');
      const placeholder = document.getElementById('cpv-live-placeholder');
      if (!canvas || !placeholder) return;

      const hasData = base !== null && altezza !== null
                      && !isNaN(base) && !isNaN(altezza)
                      && base > 0 && altezza > 0;

      if (!hasData) {
        canvas.style.display      = 'none';
        placeholder.style.display = 'flex';
        this._syncArtworkUI();
        return;
      }

      canvas.style.display      = 'block';
      placeholder.style.display = 'none';
      this._syncArtworkUI();
      this._fitCanvas(canvas);
      this._drawPreview(canvas, base, altezza);
    }

    _drawPreview(canvas, base, altezza) {
      const { ctx, W, H } = this._canvasCtx(canvas);
      ctx.clearRect(0, 0, W, H);

      // Area disponibile (escluso padding per le label dimensioni)
      const maxW = W - SVG_PAD * 2;
      const maxH = H - SVG_PAD * 2;

      // Rettangolo proporzionale
      const ratio = base / altezza;
      let rW, rH;
      if (ratio >= 1) {
        rW = maxW;
        rH = maxW / ratio;
        if (rH > maxH) { rH = maxH; rW = maxH * ratio; }
      } else {
        rH = maxH;
        rW = maxH * ratio;
        if (rW > maxW) { rW = maxW; rH = maxW / ratio; }
      }
      const x = (W - rW) / 2;
      const y = (H - rH) / 2;

      // Pannello bianco con ombra
      this._ombraCornice(ctx, x, y, rW, rH);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(x, y, rW, rH);

      // Foto del cliente (cover)
      if (this._artworkImg) {
        this._drawImgCover(ctx, this._artworkImg, x, y, rW, rH);
      }

      // Effetto stampa (lucida/tela) poi fronte (vetro/crilex)
      this._drawStampaEffetto(ctx, x, y, rW, rH, base);
      this._drawFronteEffetto(ctx, x, y, rW, rH);

      // Bordo grigio sottile
      ctx.strokeStyle = 'rgba(200,200,200,0.5)';
      ctx.lineWidth   = 0.8;
      ctx.strokeRect(x, y, rW, rH);

      // Clip metalliche (a cavallo del bordo, come nel vecchio SVG)
      if (this._clipImg) {
        this._drawClipsPannello(ctx, x, y, rW, rH, base, altezza);
      }

      // Label dimensioni (bordeaux)
      this._drawDimLabels(ctx, x, y, rW, rH, base, altezza);
    }

    _drawClipsPannello(ctx, x, y, rW, rH, base, altezza) {
      const img = this._clipImg;
      const CLIP_ASPECT = 29 / 118;  // altezza/larghezza del PNG clip

      // Clip orizzontale (top/bottom): 1 cm fisico lungo il bordo base
      const clipW_h = rW / base;
      const clipH_h = clipW_h * CLIP_ASPECT;
      // Clip verticale (left/right): 1 cm fisico lungo il bordo altezza
      const clipW_v = rH / altezza;
      const clipH_v = clipW_v * CLIP_ASPECT;

      const P = this.PRICING;
      const smallFmt = Math.min(base, altezza) <= P.SOGLIA_CLIP_LATO_MIN
                       && Math.max(base, altezza) <= P.SOGLIA_CLIP_LATO_MAX;
      const nAttacc  = smallFmt ? P.N_CLIPS_PER_SIDE_STD : P.N_CLIPS_PER_SIDE_GR;
      const positions = nAttacc >= 2 ? [0.25, 0.75] : [0.5];

      const clipY_top    = y - clipH_h / 2;        // a cavallo del bordo superiore
      const clipY_bottom = y + rH - clipH_h / 2;   // a cavallo del bordo inferiore

      const drawH = (cx, clipY) => ctx.drawImage(img, cx - clipW_h / 2, clipY, clipW_h, clipH_h);
      const drawV = (cx, cy) => {
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(-Math.PI / 2);
        ctx.drawImage(img, -clipW_v / 2, -clipH_v / 2, clipW_v, clipH_v);
        ctx.restore();
      };

      positions.forEach(t => {
        drawH(x + rW * t, clipY_top);
        drawH(x + rW * t, clipY_bottom);
        drawV(x,      y + rH * t);
        drawV(x + rW, y + rH * t);
      });
    }

    _drawDimLabels(ctx, x, y, rW, rH, base, altezza) {
      const labelOffset = 18;
      ctx.strokeStyle = '#8b2635';
      ctx.lineWidth   = 1.2;
      ctx.fillStyle   = '#8b2635';
      ctx.font        = '600 13px Helvetica,Arial,sans-serif';
      ctx.textAlign   = 'center';
      ctx.textBaseline = 'top';

      // Base (sotto)
      const baseLineY = y + rH + labelOffset;
      ctx.beginPath(); ctx.moveTo(x, baseLineY); ctx.lineTo(x + rW, baseLineY); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x, baseLineY - 5); ctx.lineTo(x, baseLineY + 5); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x + rW, baseLineY - 5); ctx.lineTo(x + rW, baseLineY + 5); ctx.stroke();
      ctx.fillText(`${base} cm`, x + rW / 2, baseLineY + 6);

      // Altezza (destra, ruotata)
      const altLineX = x + rW + labelOffset;
      ctx.beginPath(); ctx.moveTo(altLineX, y); ctx.lineTo(altLineX, y + rH); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(altLineX - 5, y); ctx.lineTo(altLineX + 5, y); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(altLineX - 5, y + rH); ctx.lineTo(altLineX + 5, y + rH); ctx.stroke();
      ctx.save();
      ctx.translate(altLineX + 14, y + rH / 2);
      ctx.rotate(Math.PI / 2);
      ctx.textBaseline = 'middle';
      ctx.fillText(`${altezza} cm`, 0, 0);
      ctx.restore();
    }

    /* ── DIMENSIONI ── */
    setDefaultDim(base, altezza) {
      const inputB = document.getElementById('cpv-base');
      const inputA = document.getElementById('cpv-altezza');
      if (inputB) inputB.value = base;
      if (inputA) inputA.value = altezza;
      this.state.config.base    = base;
      this.state.config.altezza = altezza;
      this._syncPresets(base, altezza);
      this.updateLiveView(base, altezza);
      this.updateRetroAvailability();
      this._updateStampaState();
      this.calculateLive();
    }

    onDimChange() {
      const base    = parseFloat(document.getElementById('cpv-base')?.value)    || null;
      const altezza = parseFloat(document.getElementById('cpv-altezza')?.value) || null;
      this.state.config.base    = base;
      this.state.config.altezza = altezza;
      this._syncPresets(base, altezza);
      this.updateLiveView(base, altezza);
      this.updateRetroAvailability();
      this._updateStampaState();
      this.calculateLive();
    }

    /* ── FRONTE ── */
    selectFronte(sku) {
      const fronte = this.state.fronti.find(f => f.sku === sku);
      if (!fronte) return;
      this.state.config.fronteSku = sku;
      this.state.config.fronte    = fronte;
      this.syncFronte();
      this.calculateLive();
    }

    syncFronte() {
      document.querySelectorAll('input[name="cpv-fronte"]').forEach(input => {
        input.checked = input.value === this.state.config.fronteSku;
      });
      const legend = document.getElementById('cpv-legend-fronte');
      if (legend && this.state.config.fronte) {
        legend.textContent = `— ${this.state.config.fronte.nome}`;
      }
      this._loadEffettoImg(this.state.config.fronte?.effettoUrl || '');
      if (this.state.config.fronte) {
        window._CPV_FRONTE = this.state.config.fronte;
        document.dispatchEvent(new CustomEvent('cpv:fronte-selected', { detail: this.state.config.fronte }));
      }
    }

    /* ── RETRO ── */
    selectRetro(sku) {
      const retro = this.state.retri.find(r => r.sku === sku);
      if (!retro) return;
      this.state.config.retroSku = sku;
      this.state.config.retro    = retro;
      this.syncRetro();
      window._CPV_RETRO = retro;
      document.dispatchEvent(new CustomEvent('cpv:retro-selected', { detail: retro }));
      this.calculateLive();
    }

    syncRetro() {
      document.querySelectorAll('input[name="cpv-retro"]').forEach(input => {
        input.checked = input.value === this.state.config.retroSku;
      });
      const legend = document.getElementById('cpv-legend-retro');
      if (legend && this.state.config.retro) {
        legend.textContent = `— ${this.state.config.retro.nome}`;
      }
    }

    updateRetroAvailability() {
      const { base, altezza } = this.state.config;
      const retroTavoloInput  = document.getElementById('cpv-retro-tavolo');
      const warning           = document.getElementById('cpv-tavolo-warning');
      if (!retroTavoloInput) return;

      const retro = this.state.retri.find(r => r.sku && isRetroTavolo(r));
      /* Come in CMG: il radio c'è, il dato no. Si dichiara invece di lasciar passare un
         formato che il piedistallo non regge. */
      if (!retro) {
        console.error('[CPV] nessun retro con montaggio="tavolo" nel dataset: '
                    + 'gate sul formato non applicato', this.state.retri);
        return;
      }

      const { maxB, maxA } = limitiRetroTavolo(retro, 'CPV');
      const fits = (base !== null && altezza !== null) && (
        (base <= maxB && altezza <= maxA) ||
        (altezza <= maxB && base <= maxA)
      );

      retroTavoloInput.disabled = !fits;

      if (warning) warning.classList.toggle('is-visible', !fits && base !== null && altezza !== null);

      if (!fits && isRetroTavolo(this.state.config.retro)) {
        /* Il retro di ripiego si CHIEDE, non si cabla: selectRetro esce subito su uno sku
           che non esiste, quindi con 'mdf-parete' l'avviso «Abbiamo selezionato MDF da
           Parete» compariva mentre la configurazione teneva il retro da tavolo su un
           formato non producibile (controllo-sito 21/08). */
        const ripiego = this._defaultRetro();
        if (ripiego) this.selectRetro(ripiego.sku);
      }
    }

    /* ── RESET ── */
    resetConfig() {
      const dfFronte = this._defaultFronte();   // vetro, come la copy (niente handle cablati)
      const dfRetro  = this._defaultRetro();    // idem: niente sku cablati, li risolve il DB
      this.state.config = {
        base:       null,
        altezza:    null,
        fronteSku:  dfFronte?.sku || null,
        fronte:     dfFronte,
        retroSku:   dfRetro?.sku || null,
        retro:      dfRetro,
        quantita:   1,
        stampaTipo: null,
        stampa:     null,
        clipQty:    null,
        clipMinQty: 4,
      };
      const inputB = document.getElementById('cpv-base');
      const inputA = document.getElementById('cpv-altezza');
      if (inputB) inputB.value = '';
      if (inputA) inputA.value = '';
      document.querySelectorAll('.cpv-preset').forEach(b => b.classList.remove('is-active'));

      // Riabilita MDF tavolo
      const tavoloInput = document.getElementById('cpv-retro-tavolo');
      if (tavoloInput) tavoloInput.disabled = false;
      const warning = document.getElementById('cpv-tavolo-warning');
      if (warning) warning.classList.remove('is-visible');

      this._artworkImg  = null;
      this._artworkFile = null;
      const ai = document.getElementById('cpv-artwork-input');
      if (ai) ai.value = '';
      this._artworkNotice('', 'clear');
      this._syncArtworkUI();

      this.syncFronte();
      this.syncRetro();
      this.renderStampaOptions();
      this._updateStampaLegend();
      this.updateLiveView(null, null);
      this.calculateLive();
    }

    /* ── VALIDAZIONE ── */
    validateConfig() {
      const { base, altezza, fronte, retro } = this.state.config;
      const errors   = [];
      const warnings = [];

      if (!fronte || !retro) {
        errors.push('Dati materiali non disponibili. Ricarica la pagina.');
      }

      if (base === null || isNaN(base)) {
        errors.push('Inserisci la misura base (larghezza).');
      } else if (base < this.PRICING.DIM_MIN) {
        errors.push(`Base minima: ${this.PRICING.DIM_MIN} cm.`);
      }

      if (altezza === null || isNaN(altezza)) {
        errors.push('Inserisci la misura altezza.');
      } else if (altezza < this.PRICING.DIM_MIN) {
        errors.push(`Altezza minima: ${this.PRICING.DIM_MIN} cm.`);
      }

      if (base !== null && altezza !== null && !isNaN(base) && !isNaN(altezza)) {
        const f = this._getFascia(base, altezza);
        if (f.soloPreventivo) {
          warnings.push('Formato grande: configurazione disponibile solo su preventivo.');
        } else if (f.supplementoGrande > 0) {
          warnings.push('Per formati con un lato oltre ' + this.PRICING.SOGLIA_SUPPLEMENTO_CM + ' cm, il trasporto avviene in imballaggio rinforzato.');
        }
      }

      const vincoloCrilex = this._vincoloCrilex(base, altezza);
      if (vincoloCrilex) errors.push(vincoloCrilex);

      return { valid: errors.length === 0, errors, warnings };
    }

    calcola() {
      const { config } = this.state;
      const { base, altezza, fronte, retro, quantita, stampa, stampaTipo } = config;
      if (base === null || altezza === null || !fronte || !retro) return null;

      const fascia         = this._getFascia(base, altezza);
      const soloPreventivo = fascia.soloPreventivo;
      /* flag esplicito della fascia (30/07/2026): prima si deduceva da
         `supplementoGrande > 0`, cioè dall'importo dell'imballo — vedi FR.getFascia. */
      const isGrande       = fascia.isGrande;
      /* markup PER-COMPONENTE: ogni voce porta markup (std) + markupGrande. */
      const mk = this._makeMk(isGrande);

      /* Formula condivisa col modo "pannello" del configuratore opere (FOTORECORD). */
      const c = window.FOTORECORD.pannelloNetto({
        base, altezza, fronte, retro,
        stampa:   (stampa && stampaTipo === 'foto') ? stampa : null,
        nClips:   this.state.config.clipQty || window.FOTORECORD.numeroClip(base, altezza, this.PRICING),
        clipUnit: this.PRICING.COSTO_CLIP,
        mk,
      });
      const { costoFronte, costoRetro, costoStampa, nClips } = c;
      const costoAttacc = c.costoClip;
      let imponibile = c.netto;
      /* Dato incompleto → niente prezzo: `calculateLive` tratta null come "non calcolabile". */
      if (this._markupMancante(mk)) return null;
      if (fascia.supplementoGrande > 0) imponibile += window.FOTORECORD.imballoNetto(fascia);

      const prezzoUnitario = this._round(imponibile * (1 + this.PRICING.IVA), 2);
      const prezzoTotale   = this._round(prezzoUnitario * quantita, 2);

      return {
        soloPreventivo,
        prezzoUnitario,
        prezzoTotale,
        breakdown: { costoFronte, costoRetro, costoAttacc, costoStampa, nClips, imponibile, isGrande, stampa, stampaTipo, vendita: c.vendita },
      };
    }

    calculateLive() {
      const validation = this.validateConfig();
      this._renderValidation(validation);

      const result = validation.valid ? this.calcola() : null;
      this.state.calcResult = result;
      // Carrello nativo: fascia di prezzo (variante reale) col prezzo >= a quello calcolato.
      this._bucket = (result && !result.soloPreventivo) ? this._pickBucket(result.prezzoUnitario) : null;
      this._renderPrice(result);
      this._updateClipMin();
      this._updateCta(result);
      /* Nessun profilo; clip comprese nel prezzo → nessun importo a parte. */
      const v = (result && !result.soloPreventivo) ? result.breakdown.vendita : null;
      this._publishPrezziComponenti(v ? { fronte: v.fronte, retro: v.retro, stampa: v.stampa, accessori: null } : null);
    }

    _renderValidation({ errors, warnings }) {
      const area = document.getElementById('cpv-validation');
      if (!area) return;
      area.innerHTML = [
        ...errors.map(e   => `<div class="cpv-msg error"><span>✕</span><span>${this._esc(e)}</span></div>`),
        ...warnings.map(w => `<div class="cpv-msg warning"><span>Attenzione:</span><span>${this._esc(w)}</span></div>`),
      ].join('');
    }

    _renderPrice(result) {
      const priceEl    = document.getElementById('cpv-price-total');
      const unitNote   = document.getElementById('cpv-price-unit-note');
      const extraNote  = document.getElementById('cpv-extra-grande-note');
      const priceElM   = document.getElementById('cpv-price-total-m');
      const unitNoteM  = document.getElementById('cpv-price-unit-note-m');
      const extraNoteM = document.getElementById('cpv-extra-grande-note-m');
      const { config } = this.state;
      this._rigaSpedizione(false);
      // Carrello nativo: se c'è una fascia, mostro il prezzo della fascia (quello che il cliente paga).
      const useBucket = !!(result && !result.soloPreventivo && this._bucket);
      const dispUnit  = useBucket ? this._bucket.c / 100 : (result ? result.prezzoUnitario : 0);
      const dispTotal = useBucket ? dispUnit * (config.quantita || 1) : (result ? result.prezzoTotale : 0);

      const _sync = (el, un, en) => {
        if (result && result.soloPreventivo) {
          if (el) { el.textContent = this._fmt(result.prezzoTotale); el.removeAttribute('data-placeholder'); }
          if (un) un.style.display = 'none';
          if (en) { en.textContent = 'Stima indicativa — il prezzo definitivo, spedizione compresa, viene confermato via email'; en.style.display = 'inline'; }
        } else if (result) {
          if (el) { el.textContent = this._fmt(dispTotal); el.removeAttribute('data-placeholder'); }
          if (un) {
            if (config.quantita > 1) { un.textContent = ` — ${this._fmt(dispUnit)} cad.`; un.style.display = 'inline'; }
            else { un.style.display = 'none'; }
          }
          this._rigaSpedizione(true);
          // Nota extra: solo supplemento grande se applicabile
          if (en) {
            const f = this._getFascia(config.base, config.altezza);
            if (f.supplementoGrande > 0) { en.textContent = `Supplemento formato grande: +${this._fmt(f.supplementoGrande)}`; en.style.display = 'inline'; }
            else { en.style.display = 'none'; }
          }
        } else {
          if (el) { el.textContent = 'Inserisci le dimensioni'; el.setAttribute('data-placeholder', 'true'); }
          if (un) un.style.display = 'none';
          if (en) en.style.display = 'none';
        }
      };

      _sync(priceEl, unitNote, extraNote);
      _sync(priceElM, unitNoteM, extraNoteM);

      // Aggiorna legend dimensioni
      const legendDim = document.getElementById('cpv-legend-dim');
      if (legendDim) {
        legendDim.textContent = (config.base && config.altezza)
          ? `— ${config.base} × ${config.altezza} cm` : '';
      }
    }

    _updateCta(result) {
      this._setCta({ result: result, bucket: this._bucket, incompleteText: this.ctaIncompleteText });
    }

    // Pannelli usa le fasce del PROPRIO prodotto (window.PANNELLI_BUCKETS) → la riga a
    // carrello è una variante di pannelli-solo-vetro, l'ordine linka al prodotto reale.
    _pickBucket(euro) {
      return window.FOTORECORD.core.pickBucket(euro, window.PANNELLI_BUCKETS || window.SU_MISURA_BUCKETS);
    }

    _buildCartProperties() {
      const { config, calcResult } = this.state;
      const props = {
        'Tipologia':         'Pannello solo vetro',
        'Dimensioni':        `${config.base} × ${config.altezza} cm`,
        'Fronte':            nb(config.fronte) || '—',
        'Retro':             nb(config.retro)  || '—',
        'Clip':              `${this.state.config.clipQty || 0} pz`,
        '_prezzo_calcolato_pezzo': (calcResult?.prezzoUnitario || 0).toFixed(2),
      };
      if (config.stampa && config.stampaTipo) props['Stampa'] = nb(config.stampa);
      // Proprietà nascoste — vedi il commento gemello in
      // ConfiguratoreCornice._buildCartProperties. Nessun profilo per il pannello.
      props['_tipo_prodotto'] = 'pannello';
      props['_fronte_sku']    = config.fronte?.sku || '';
      props['_retro_sku']     = config.retro?.sku  || '';
      props['_clip_qty']      = String(this.state.config.clipQty || 0);
      if (config.stampa && config.stampaTipo) {
        props['_stampa_tipo'] = config.stampaTipo;
        props['_stampa_sku']  = config.stampa.sku || '';
      }
      return props;
    }

    /* ── MODAL ── */
    openModal() {
      if (!this.state.calcResult) return;
      this._populateRecap();
      const overlay = document.getElementById('cpv-modal-overlay');
      if (overlay) {
        overlay.classList.add('open');
        document.body.style.overflow = 'hidden';
        setTimeout(() => overlay.querySelector('input[type="text"]')?.focus(), 150);
      }
    }

    closeModal() {
      const overlay = document.getElementById('cpv-modal-overlay');
      if (overlay) {
        overlay.classList.remove('open');
        document.body.style.overflow = '';
      }
    }

    _populateRecap() {
      const { config, calcResult } = this.state;
      if (!calcResult) return;

      const rows = [
        ['Dimensioni interne', `${config.base} × ${config.altezza} cm`],
        ['Fronte',             nb(config.fronte) || '—'],
        ['Retro',              nb(config.retro)  || '—'],
        ['Accessori',          `${this.state.config.clipQty || 0} clip`],
        ['Quantità',           String(config.quantita)],
      ];
      if (config.stampa && config.stampaTipo) {
        rows.splice(3, 0, ['Stampa', nb(config.stampa)]);
      }

      const recapBody = document.getElementById('cpv-modal-recap-rows');
      if (recapBody) {
        recapBody.innerHTML = rows.map(([k, v]) => `
          <div class="cpv-modal-recap-row">
            <span>${k}</span>
            <span>${v}</span>
          </div>
        `).join('');
      }

      const priceEl = document.getElementById('cpv-modal-price-value');
      if (priceEl) priceEl.textContent = calcResult.soloPreventivo ? `~${this._fmt(calcResult.prezzoTotale)} (stima)` : this._fmt(calcResult.prezzoTotale);

      const unitEl = document.getElementById('cpv-modal-price-unit');
      if (unitEl && config.quantita > 1 && !calcResult.soloPreventivo) {
        unitEl.textContent   = `(${config.quantita} × ${this._fmt(calcResult.prezzoUnitario)})`;
        unitEl.style.display = 'inline';
      } else if (unitEl) {
        unitEl.style.display = 'none';
      }
    }

    _buildBodyField() {
      const { config, calcResult } = this.state;
      if (!calcResult) return;

      const newsletter = document.getElementById('cpv-check-newsletter');
      const noteEl     = document.getElementById('cpv-modal-note');

      const stampaLine = (config.stampa && config.stampaTipo)
        ? `Stampa      : ${nb(config.stampa)} (${this._componenteCodice(config.stampa)})`
        : null;

      const body = [
        '=== PREVENTIVO PANNELLO SOLO VETRO — FOTORECORD ===',
        '',
        'CONFIGURAZIONE',
        `Dimensioni  : ${config.base} × ${config.altezza} cm`,
        `Fronte      : ${nb(config.fronte) || '—'}`,
        `Retro       : ${nb(config.retro)  || '—'}`,
        stampaLine,
        `Clip        : ${this.state.config.clipQty || 0} pz`,
        `Quantità    : ${config.quantita}`,
        '',
        'PREZZO STIMATO (IVA 22% inclusa)',
        calcResult.soloPreventivo
          ? `Stima       : ~${this._fmt(calcResult.prezzoTotale)} (indicativa, lato > 105 cm)`
          : `Unitario    : ${this._fmt(calcResult.prezzoUnitario)}`,
        calcResult.soloPreventivo ? '' : `Totale      : ${this._fmt(calcResult.prezzoTotale)}`,
        calcResult.breakdown.isGrande
          ? `Nota        : Include supplemento formato grande (+${this._fmt(window.FOTORECORD.PRICING_SSOT.SUPPLEMENTO_IMBALLO_IVA)})` : '',
        '',
        'NOTE CLIENTE',
        noteEl?.value?.trim() || '(nessuna)',
        '',
        `Newsletter  : ${newsletter?.checked ? 'sì' : 'no'}`,
        'GDPR        : accettato',
        'Origine     : Configuratore Pannelli Solo Vetro / fotorecord.it',
      ].filter(line => line !== null).join('\n');

      const hiddenBody = document.getElementById('cpv-modal-body');
      if (hiddenBody) hiddenBody.value = body;
    }

    /* ── UTILS (formato prezzi/round come il vecchio CPV) ── */
    _round(value, decimals) {
      const m = Math.pow(10, decimals);
      return Math.round(value * m) / m;
    }
    _fmt(value) { return FR.fmtEuro(value); }
  }

  /* ══════════════════════════════════════════════════════════════════════════
     OPERE D'AUTORE (caoa) — opera del catalogo + cornice; carrello a 2 righe
     (_bundle); nessun upload foto cliente (l'artwork è l'opera).
     ══════════════════════════════════════════════════════════════════════════ */
  class ConfiguratoreOpere extends ConfiguratoreBase {

    constructor() {
      super('caoa', { logTag: 'CAOA', ids: { cta: 'caoa-cta' }, pricing: { DIM_MIN: 10 } });
      this.state = {
        opera:       null,
        profili:     [],
        fronti:      [],
        retri:       [],
        passLegno:   [],
        passCartone: [],
        accessori:   [],
        stampe:      [],
        filters: { battente: '', colore: '', finitura: '' },
        config: {
          /* Modo di montaggio dell'opera: 'cornice' (com'è sempre stato) | 'montatura'
             | 'pannello'. Cambia gli step visibili, l'anteprima, la formula del prezzo
             e la Tipologia che finisce nel carrello — l'opera resta la stessa. */
          tipo:               'cornice',
          base:               null,
          altezza:            null,
          solostampa:         false,
          stampaSku:          null,
          stampa:             null,
          profiloSku:         null,
          profilo:            null,
          fronteSku:          null,
          fronte:             null,
          retroSku:           null,
          retro:              null,
          hasPassCartone:     false,
          passCartoneSku:     null,
          passCartone:        null,
          passCartoneLargh:   PASS_CARTONE_LARGH_DEFAULT_CM,
          hasPassLegno:       false,
          passLegnoSku:       null,
          passLegno:          null,
          attaccaglieQtys:    {},
          // true = quantità scelta dall'operatore: stop ai default automatici.
          attTouched:         false,
          quantita:           1,
        },
      };

      this._operaImg          = null;
      this._operaImgUrl       = '';
      this._frameImg          = null;
      this._frameImgUrl       = '';
      this._patternImg        = null;
      this._patternImgUrl     = '';
      this._passLegnoImg      = null;
      this._passLegnoImgUrl   = '';
      this._passLegnoPatternImg = null;
      this._passLegnoPatternUrl = '';
      this._passCartoneImg    = null;
      this._passCartoneImgUrl = '';
      this._passCartoneTile    = null;
      this._passCartoneTileKey = '';
    }

    _dataGlobal() { return window.SHOPIFY_OPERE_AUTORE; }

    init() {
      const data = window.SHOPIFY_OPERE_AUTORE;
      if (!data) { console.error('[CAOA] Dati non disponibili'); return; }
      this._renderModiMontaggio(this.state.config.tipo, (modo) => this.setTipo(modo));
      this.loadData(data);
      this.renderStampaOptions();
      this._updateStampaLegend();   // l'accordion Stampa deve sapere la scelta iniziale, non solo i cambi
      this.renderFilters();
      this.renderGallery();
      this.syncFronte();
      this.syncRetro();
      this.renderPassCartoneGallery();
      this.renderPassLegnoGallery();
      this._updatePassCartoneUI();
      this._updatePassLegnoState();
      this._updateAttaccaglie();
      this.setupEventListeners();
      this._preloadOperaImage();
      this._setDefaultDim();
      this._checkSuccessSession('caoa_success');
      this._applyModoUI();
      // Primo DISPONIBILE, non primo assoluto (vedi _primoProfiloUtile).
      const primo = this._primoProfiloUtile();
      if (primo) this.selectProfilo(primo.sku);
      this._applyTipoParam();
      this._mountMobileBar();
    }

    /* ══════════════════════════════════════════════════════════════════════
       MODI DI MONTAGGIO — cornice / montatura a giorno / pannello solo vetro
       ══════════════════════════════════════════════════════════════════════ */

    /* Elenco profili ammessi nel modo corrente. Lo scope arriva dal DATO
       (`configuratore` di ASTE_CORNICI_FULL = colonna `configuratori` di
       db_metro_lineare): in montatura restano fuori i profili che a giorno non si
       producono, senza una seconda lista da tenere allineata a mano. */
    _profiliDelModo(tipo) {
      const scope = (tipo === 'montatura') ? 'montature_a_giorno' : 'aste_su_misura';
      const tutti = this._profiliTutti || [];
      return tutti.filter(p => (p.configuratore || '').includes(scope));
    }

    /* Un materiale è ammesso nel modo corrente se il suo `scope` lo dice (stringa CSV
       che arriva dalla colonna `configuratori`, emessa dalla section). Se lo scope non
       c'è affatto — blob generato da una section più vecchia, che capita nella finestra
       fra un deploy e l'altro — si tiene tutto: meglio un'opzione di troppo che un
       configuratore senza fronte né retro. */
    _perModo(list, tipo) {
      const scope = { cornice: 'opere_autore', montatura: 'montature_a_giorno', pannello: 'pannelli_solo_vetro' }[tipo];
      if (!(list || []).some(x => x && x.scope)) return list || [];
      return (list || []).filter(x => !x.scope || String(x.scope).includes(scope));
    }

    setTipo(tipo) {
      if (!MODI_MONTAGGIO.some(m => m.key === tipo)) return;
      const c = this.state.config;
      c.tipo = tipo;

      /* Montatura e pannello non hanno passepartout (né cartone né legno): non è una
         semplificazione dell'interfaccia, è che il prodotto non li prevede. */
      if (tipo !== 'cornice') {
        c.hasPassCartone = false; c.passCartoneSku = null; c.passCartone = null;
        c.hasPassLegno   = false; c.passLegnoSku   = null; c.passLegno   = null;
        this._passCartoneImg = null; this._passCartoneImgUrl = '';
        this._passLegnoImg   = null; this._passLegnoImgUrl   = '';
        this._passLegnoPatternImg = null; this._passLegnoPatternUrl = '';
        /* Le clip sono incluse nel prezzo e non si scelgono: le attaccaglie sì, ma
           solo in cornice (come nei configuratori pubblici). */
        c.attaccaglieQtys = {}; c.attTouched = false;
      }

      // fronte/retro ammessi nel modo: se quello scelto non lo è più, si ricade sul default
      this.state.fronti = this._perModo(this._fronteTutti, tipo);
      this.state.retri  = this._perModo(this._retroTutti,  tipo);
      /* «Senza lastra» / «senza fondo» (sku vuoto) e' una scelta lecita solo in cornice:
         li' resta com'era; negli altri modi cade al default qui sotto. */
      if (c.fronteSku && !this.state.fronti.some(f => f.sku === c.fronteSku)) {
        const df = this._defaultFronte(); c.fronteSku = df?.sku || NESSUNO_SKU; c.fronte = df || null;
      }
      if (c.retroSku && !this.state.retri.some(r => r.sku === c.retroSku)) {
        const dr = this._defaultRetro(); c.retroSku = dr?.sku || NESSUNO_SKU; c.retro = dr || null;
      }
      if (tipo !== 'cornice' && (!c.fronteSku || !c.retroSku)) {
        /* Montatura e pannello non si fanno senza lastra e senza fondo: se si arrivava
           da una cornice "Nessuno", si riporta il default invece di calcolare su zero. */
        if (!c.fronteSku) { const df = this._defaultFronte(); c.fronteSku = df?.sku || null; c.fronte = df || null; }
        if (!c.retroSku)  { const dr = this._defaultRetro();  c.retroSku  = dr?.sku || null; c.retro  = dr || null; }
      }
      this._applyClipDelModo();

      // profili del modo (il pannello non ne ha)
      this.state.profili = this._profiliDelModo(tipo);
      if (tipo === 'pannello') {
        c.profiloSku = null; c.profilo = null;
      } else if (!this.state.profili.some(p => p.sku === c.profiloSku)) {
        c.profiloSku = null; c.profilo = null;
        // Cambio modo (cornice ⇄ montatura): la lista profili cambia, il default pure.
        // Primo DISPONIBILE, non primo assoluto (vedi _primoProfiloUtile).
        const primo = this._primoProfiloUtile();
        if (primo) this.selectProfilo(primo.sku);
      }

      this._renderModiMontaggio(tipo, (m) => this.setTipo(m));
      this._applyModoUI();
      this.renderFilters();
      this.renderGallery();
      this.renderStampaOptions();   // la tela sparisce fuori dalla cornice (vedi sotto)
      this.syncFronte();
      this.syncRetro();
      this._updatePassCartoneUI();
      this._updatePassLegnoState();
      this._updateAttaccaglie();
      /* Le schede sotto il configuratore (cornice, fronte, retro, passepartout, accessori)
         sono sezioni a sé che ascoltano gli eventi: senza questi dispatch restavano
         popolate col profilo e i passepartout di prima, cioè con roba che in questo
         montaggio non esiste e che nel prezzo non c'è. Fronte e retro cambiano anche
         loro col modo (il cartone ondulato non esiste in montatura: il default diventa
         lo schienale MDF) e fino all'11/09/2026 non venivano rilanciati: la scheda Retro
         diceva «Cartone ondulato» col prezzo dello schienale. */
      window._CAOA_FRONTE = c.fronte;
      window._CAOA_RETRO  = c.retro;
      document.dispatchEvent(new CustomEvent('caoa:fronte-selected', { detail: c.fronte }));
      document.dispatchEvent(new CustomEvent('caoa:retro-selected',  { detail: c.retro  }));
      this._dispatchPassEvents();
      if (tipo === 'pannello') {
        window._CAOA_PROFILO = null;
        document.dispatchEvent(new CustomEvent('caoa:cornice-selected', { detail: null }));
      }
      this.updateLiveView();
      this.calculateLive();
    }

    /* Mostra/nasconde gli step secondo il modo. Ogni step dichiara nel markup in quali
       modi vive (`data-modi="cornice montatura"`): così aggiungere uno step non
       richiede di tornare qui a elencare id. */
    _applyModoUI() {
      const tipo = this.state.config.tipo;
      document.querySelectorAll('#configuratore-opere-autore [data-modi]').forEach(el => {
        const modi = (el.getAttribute('data-modi') || '').split(/\s+/).filter(Boolean);
        el.style.display = modi.includes(tipo) ? '' : 'none';
      });
      const root = document.getElementById('configuratore-opere-autore');
      if (root) root.dataset.modo = tipo;
    }

    /* Clip del modo corrente: prezzo (per il calcolo) e immagine (per l'anteprima).
       In modo cornice non servono: le clip non entrano in una cornice. */
    _applyClipDelModo() {
      const modo  = this.state.config.tipo;
      const scope = modo === 'montatura' ? 'montature_a_giorno'
                  : modo === 'pannello'  ? 'pannelli_solo_vetro' : null;
      const clip  = scope
        ? (this._clipTutte || []).find(c => c && String(c.scope || '').includes(scope))
        : null;
      this._clipDelModo   = clip || null;
      this._clipUnitNetto = clip && clip.prezzo != null
        ? clip.prezzo / (1 + this.PRICING.IVA)
        : 0.10 / (1 + this.PRICING.IVA);
      this._loadClipImg(scope ? ((this._clipUrl || {})[modo] || '') : '');
    }

    /* Deep-link: /products/<opera>?tipo=montatura */
    _applyTipoParam() {
      try {
        const t = new URLSearchParams(window.location.search).get('tipo');
        if (t && t !== this.state.config.tipo) this.setTipo(t);
      } catch (e) { /* URL esotico: si resta in cornice */ }
    }

    /* ── CARICAMENTO DATI ── */
    loadData(data) {
      this.state.opera      = data.opera || {};
      /* Liste COMPLETE + liste del modo corrente: il modo di montaggio (cornice /
         montatura / pannello) ne offre sottoinsiemi diversi, e per tornare indietro
         serve avere ancora l'originale. */
      this._fronteTutti     = data.fronti      || [];
      this._retroTutti      = data.retri       || [];
      this.state.fronti     = this._perModo(this._fronteTutti, this.state.config.tipo);
      this.state.retri      = this._perModo(this._retroTutti,  this.state.config.tipo);
      this.state.passLegno  = data.passLegno   || [];
      this.state.passCartone= data.passCartone || [];
      this.state.accessori  = data.accessori   || [];
      /* Clip di montaggio (montatura/pannello): prezzo di VENDITA IVA incl./pz dal
         metaobject, riportato a netto — niente markup, è già un prezzo deciso.
         Le clip sono DUE (una per montature, una per pannelli) e si sceglie quella del
         modo: prendere la prima dell'elenco funzionava solo finché costavano uguale. */
      this._clipTutte = data.clip || [];
      this._clipUrl   = { montatura: data.clipUrlMontatura || '', pannello: data.clipUrlPannello || '' };
      this._applyClipDelModo();

      // Profili: usa ASTE_CORNICI_FULL (generato da script) se disponibile
      const full = window.ASTE_CORNICI_FULL;
      if (full && full.length > 0) {
        this.state.profili = full
          .map(r => ({
            /* sku = id_db (SSOT: generate_aste_js_asset.py emette solo `sku`). Il fallback
               a `r.id_metro_lineare` e' stato tolto il 20/07/2026: quella colonna non esiste
               piu' in db_metro_lineare.csv ne' nell'asset generato, quindi era codice morto
               che poteva solo reintrodurre un id trattinato nello sku. */
            sku:         r.sku || '',
            nome:        r.nome             || r.title || '',
            colore:      r.colore_della_cornice || r.colore   || '',
            finitura:    r.finitura_della_cornice || r.finitura || '',
            stile:       r.stile || '',
            larghezzaMm:    parseFloat(r.larghezza_sezione_mm) || 0,
            altezzaSezione: parseFloat(r.altezza_sezione_mm)  || 0,
            battenteMm:     parseFloat(r.altezza_battente_mm) || 0,
            sezioneUrl:     r.sezione_profilo_url || '',
            costoMl:        parseFloat(r.costo_fornitore_1)   || 0,
            /* markup per-componente: senza questi due campi mk(profilo) restituiva 0 e
               il fallback `|| 2.9` a valle applicava SEMPRE il markup standard, anche in
               fascia grande — le opere 61-105 cm perdevano il markup 3,5 sulla cornice. */
            markup:         parseFloat(r.markup)        || 0,
            markupGrande:   parseFloat(r.markup_grande) || 0,
            imgUrl:         r.immagine_url || '',
            patternUrl:     r.pattern_url  || '',
            /* Foto reali del profilo — stesso contratto del ramo aste (vedi ~1164). */
            fotoCampioneUrl: r.foto_campione_url || '',
            fotoAngoloUrl:   r.foto_angolo_url   || '',
            fotoFallback:    r.foto_fallback === true,
            disponibile: r.disponibile !== false,
            /* scope del profilo (colonna `configuratori` di db_metro_lineare): dice in
               quali modi di montaggio è ammesso. Prima il filtro era qui e teneva solo
               'aste_su_misura', quindi il modo montatura non avrebbe avuto profili. */
            configuratore: r.configuratore || '',
          }));
        this._profiliTutti = this.state.profili;
        this.state.profili = this._profiliDelModo(this.state.config.tipo);
      } else {
        this._profiliTutti = data.profili || [];
        this.state.profili = this._profiliTutti;
      }

      // Stampe: un item per tipo (foto e tela), con nomi canonici.
      // Le fasce foto (small/pro) sono tenute a parte e risolte per misura nel prezzo.
      const rawStampe = data.stampe || [];
      this._fotoTiers = rawStampe.filter(s => s.sku && !s.conTelaio);
      const stampeResult = [];
      let hasFoto = false, hasTela = false;
      for (const s of rawStampe) {
        if (!s.sku) continue;
        if (s.conTelaio && !hasTela) {
          stampeResult.push({ ...s, nome: 'Stampa su tela' });
          hasTela = true;
        } else if (!s.conTelaio && !hasFoto) {
          stampeResult.push({ ...s, nome: 'Stampa fotografica' });
          hasFoto = true;
        }
      }
      this.state.stampe = stampeResult;

      // default fronte = VETRO, retro = il primo SENZA limite di formato (_defaultFronte
      // / _defaultRetro): entrambi dal dato, non dalla posizione nell'elenco.
      const dfFronte = this._defaultFronte();
      const dfRetro  = this._defaultRetro();
      this.state.config.fronteSku = dfFronte?.sku || NESSUNO_SKU;
      this.state.config.fronte    = dfFronte      || null;
      this.state.config.retroSku  = dfRetro?.sku  || NESSUNO_SKU;
      this.state.config.retro     = dfRetro       || null;


      const badge = document.getElementById('caoa-live-ratio-badge');
      if (badge && this.state.opera?.ratio) badge.textContent = `Ratio ${this.state.opera.ratio}:1`;

      window._CAOA_FRONTE = this.state.config.fronte;
      window._CAOA_RETRO  = this.state.config.retro;
      document.dispatchEvent(new CustomEvent('caoa:fronte-selected', { detail: this.state.config.fronte }));
      document.dispatchEvent(new CustomEvent('caoa:retro-selected',  { detail: this.state.config.retro  }));
      this._dispatchPassEvents();
      this._loadEffettoImg(this.state.config.fronte?.effettoUrl || '');
    }

    /* ── DIMENSIONI (ratio-lock) ── */
    _setDefaultDim() {
      const ratio = parseFloat(this.state.opera?.ratio) || 1.5;
      const base  = 30;
      const alt   = Math.round((base / ratio) * 2) / 2;
      this._applyDim(base, alt);
    }

    _applyDim(base, altezza) {
      const elBase = document.getElementById('caoa-base');
      const elAlt  = document.getElementById('caoa-altezza');
      if (elBase) elBase.value = base;
      if (elAlt)  elAlt.value  = altezza;
      this.state.config.base    = base;
      this.state.config.altezza = altezza;
      document.querySelectorAll('.caoa-preset').forEach(b => b.classList.toggle('is-active', parseFloat(b.dataset.base) === base));
      this._updateDimLegend();
      this._updateAttaccaglie();
      this.updateLiveView();
      this._applyRetroTavoloGate();  // le misure cambiano il formato finito: il retro tavolo va rivalutato
      this.calculateLive();
      document.dispatchEvent(new CustomEvent('caoa:config-changed', { detail: { base, altezza } }));
    }

    _dimFromBase(base) {
      const ratio   = parseFloat(this.state.opera?.ratio) || 1.5;
      const altezza = Math.round((base / ratio) * 2) / 2;
      return { base, altezza };
    }

    _updateDimLegend() {
      const { base, altezza } = this.state.config;
      const el = document.getElementById('caoa-legend-dim');
      if (el) el.textContent = (base && altezza) ? `— ${base}×${altezza} cm` : '';
    }

    /* ── STAMPA (modello opere: record concreti + "Solo stampa") ── */
    _resolveFotoTier() {
      const list = this._fotoTiers || [];
      if (!list.length) return null;
      const { base, altezza } = this.state.config;
      const small = list.find(s => (s.maxBase || 999) < 999);
      const pro   = list.find(s => (s.maxBase || 999) >= 999) || list[list.length - 1];
      if (!small) return pro;
      const isSmall = base && altezza
        && ((base <= small.maxBase && altezza <= small.maxAlt) ||
            (altezza <= small.maxBase && base <= small.maxAlt));
      return isSmall ? small : pro;
    }

    renderStampaOptions() {
      const el = document.getElementById('caoa-stampa-options');
      if (!el) return;
      const SOLO = '__solo_stampa__';
      const soloSel = this.state.config.stampaSku === SOLO;
      const soloHtml = `<label class="caoa-option${soloSel ? ' selected' : ''}">
          <input type="radio" name="caoa-stampa" value="${SOLO}"${soloSel ? ' checked' : ''}>
          <span class="caoa-option-card">
            ${this._optionImg('')}
            <span class="caoa-option-dot"></span>
            <span class="caoa-option-text"><strong>Nessuna stampa</strong><span>Senza cornice</span></span>
          </span>
        </label>`;
      /* La stampa su TELA esiste solo in cornice: la tela sta su un telaio 43T, che in
         una montatura a giorno o in un pannello non ci va — infatti i due configuratori
         pubblici la scartano già nel Liquid. Se era selezionata e si cambia modo, si
         torna alla stampa fotografica invece di lasciare un prodotto inesistente. */
      const stampeDelModo = this.state.config.tipo === 'cornice'
        ? this.state.stampe
        : this.state.stampe.filter(s => !s.conTelaio);
      if (this.state.config.stampaSku && !stampeDelModo.some(s => s.sku === this.state.config.stampaSku)
          && this.state.config.stampaSku !== SOLO) {
        const foto = stampeDelModo[0] || null;
        this.state.config.stampaSku = foto?.sku || null;
        this.state.config.stampa    = foto;
      }
      if (!stampeDelModo.length) {
        el.innerHTML = soloHtml;
        return;
      }
      el.innerHTML = soloHtml + stampeDelModo.map(s => {
        const sel = s.sku === this.state.config.stampaSku;
        const subtitle = s.descrizioneBreve || '';
        const img = this._optionImg(s.immagineUrl, true);
        return `<label class="caoa-option${sel ? ' selected' : ''}">
          <input type="radio" name="caoa-stampa" value="${this._esc(s.sku)}"${sel ? ' checked' : ''}>
          <span class="caoa-option-card">
            ${img}
            <span class="caoa-option-dot"></span>
            <span class="caoa-option-text"><strong>${this._esc(s.nome)}</strong><span>${subtitle}</span></span>
          </span>
        </label>`;
      }).join('');
    }

    selectStampa(sku) {
      const SOLO = '__solo_stampa__';
      if (sku === SOLO) {
        this.state.config.stampaSku = SOLO;
        this.state.config.stampa    = null;
        this.renderStampaOptions();
        this._updateStampaLegend();
        this._toggleSoloStampa(true);
        return;
      }
      const s = this.state.stampe.find(x => x.sku === sku);
      if (!s) return;
      this.state.config.stampaSku = sku;
      this.state.config.stampa    = s;
      this.renderStampaOptions();
      this._updateStampaLegend();
      this._toggleSoloStampa(false);
    }

    _updateStampaLegend() {
      this._dispatchStampaEvent();
      const el = document.getElementById('caoa-legend-stampa');
      if (el) el.textContent = this.state.config.stampa ? `— ${nb(this.state.config.stampa)}` : '';
    }

    /* ── SOLO STAMPA ── */
    _toggleSoloStampa(val) {
      this.state.config.solostampa = val;
      ['caoa-step-profilo','caoa-step-fronte','caoa-step-retro','caoa-step-pass-cartone','caoa-step-pass-legno'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.style.display = val ? 'none' : '';
      });
      if (val) {
        this.state.config.profiloSku = null;
        this.state.config.profilo    = null;
        window._CAOA_PROFILO = null;
        document.dispatchEvent(new CustomEvent('caoa:cornice-selected', { detail: null }));
      } else {
        /* Rimettere `display=''` su tutti gli step riportava a galla passepartout e
           profilo anche in montatura e pannello, dove non esistono: bastava spuntare e
           togliere "Solo stampa". Il modo ha l'ultima parola. */
        this._applyModoUI();
      }
      this.calculateLive();
      this.updateLiveView();
    }

    /* ── FILTRI ── */
    renderFilters() {
      this._renderBattenteFilter();
      this._renderColoreFilter();
      this._renderFinituraFilter();
    }

    _renderBattenteFilter() {
      const el = document.getElementById('caoa-filter-battente');
      if (!el) return;
      const opts = [
        { label: 'Tutti',             value: ''      },
        { label: 'Normale',           value: 'basso' },
        { label: 'Alto almeno 20 mm', value: 'alto'  },
      ];
      el.innerHTML = opts.map(o =>
        `<button type="button" class="caoa-pill${o.value === this.state.filters.battente ? ' active' : ''}" data-battente="${this._esc(o.value)}">${this._esc(o.label)}</button>`
      ).join('');
    }

    _renderColoreFilter() {
      const el = document.getElementById('caoa-filter-colore');
      if (!el) return;
      const colori = [...new Set(this.state.profili.map(p => (p.colore || '').trim()).filter(Boolean))].sort();
      const opts   = [{ label: 'Tutti', value: '' }, ...colori.map(c => ({ label: c, value: c }))];
      el.innerHTML = opts.map(o =>
        `<button type="button" class="caoa-pill${o.value === this.state.filters.colore ? ' active' : ''}" data-colore="${this._esc(o.value)}">${this._esc(o.label)}</button>`
      ).join('');
    }

    _renderFinituraFilter() {
      const el = document.getElementById('caoa-filter-finitura');
      if (!el) return;
      const finiture = [...new Set(this.state.profili.map(p => (p.finitura || '').trim()).filter(Boolean))].sort();
      const opts     = [{ label: 'Tutti', value: '' }, ...finiture.map(f => ({ label: f, value: f }))];
      el.innerHTML = opts.map(o =>
        `<button type="button" class="caoa-pill${o.value === this.state.filters.finitura ? ' active' : ''}" data-finitura="${this._esc(o.value)}">${this._esc(o.label)}</button>`
      ).join('');
    }

    _filteredProfili() {
      const { battente, colore, finitura } = this.state.filters;
      return this.state.profili.filter(p => {
        if (battente === 'alto'  && (p.battenteMm || 0) < BATTENTE_ALTO_MM) return false;
        if (battente === 'basso' && (p.battenteMm || 0) >= BATTENTE_ALTO_MM) return false;
        if (colore   && (p.colore   || '').toLowerCase() !== colore.toLowerCase())   return false;
        if (finitura && (p.finitura || '').toLowerCase() !== finitura.toLowerCase()) return false;
        return true;
      });
    }

    /* ── GALLERY PROFILI ── */
    renderGallery() {
      const grid    = document.getElementById('caoa-gallery-grid');
      const countEl = document.getElementById('caoa-gallery-count');
      if (!grid) return;
      const list = this._filteredProfili();
      if (countEl) countEl.textContent = `${list.length} ${list.length === 1 ? 'profilo' : 'profili'}`;
      if (!list.length) {
        grid.innerHTML = '<div class="caoa-gallery-empty">Nessun profilo con questi filtri</div>';
        return;
      }
      grid.innerHTML = list.map(p => {
        const sel    = p.sku === this.state.config.profiloSku ? ' selected' : '';
        const imgHtml = p.imgUrl
          ? `<img class="caoa-gallery-item-img" src="${this._esc(this._thumbUrl(p.imgUrl))}" alt="" loading="lazy">`
          : `<div class="caoa-gallery-item-placeholder">${this._esc(p.nome.split(' ').slice(0, 2).join(' '))}</div>`;
        return `<div class="caoa-gallery-item${sel}" data-sku="${this._esc(p.sku)}" title="${this._esc(p.nome)}">${imgHtml}</div>`;
      }).join('');
      this._abilitaListbox(grid);
    }

    selectProfilo(sku) {
      const p = this.state.profili.find(x => x.sku === sku);
      if (!p) return;
      this.state.config.profiloSku = sku;
      this.state.config.profilo    = p;
      this.renderGallery();
      this._loadFrameImg(p.imgUrl || '');
      this._loadPatternImg(p.patternUrl || '');
      this._updatePassLegnoState();
      this.calculateLive();
      /* La variabile globale serve all'init di opera-cornice-detail/detail-accordion, che
         partono DOPO questo primo giro e non vedono l'evento: senza, l'accordion profilo
         restava vuoto fino al primo clic (visto sul live l'11/09/2026). */
      window._CAOA_PROFILO = p;
      document.dispatchEvent(new CustomEvent('caoa:cornice-selected', { detail: p }));
    }

    /* ── FRONTE / RETRO (radio dinamiche, pattern CASM) ── */
    _setFronte(sku) {
      const f = this.state.fronti.find(x => x.sku === sku) || null;
      this.state.config.fronteSku = f ? f.sku : NESSUNO_SKU;
      this.state.config.fronte    = f;
      this._loadEffettoImg(f?.effettoUrl || '');
      window._CAOA_FRONTE = f;
      document.dispatchEvent(new CustomEvent('caoa:fronte-selected', { detail: f }));
      this.calculateLive();
    }
    _setRetro(sku) {
      const r = this.state.retri.find(x => x.sku === sku) || null;
      this.state.config.retroSku = r ? r.sku : NESSUNO_SKU;
      this.state.config.retro    = r;
      window._CAOA_RETRO = r;
      document.dispatchEvent(new CustomEvent('caoa:retro-selected', { detail: r }));
      this.calculateLive();
    }
    /* Riclic sulla stampa scelta: si torna senza stampa (l'opera resta com'e' — e' il
       cliente che decide se stamparla qui), stesso stato dell'apertura pagina. */
    deselezionaStampa() {
      this.state.config.stampaSku = null;
      this.state.config.stampa    = null;
      this.renderStampaOptions();
      this._updateStampaLegend();
      this._toggleSoloStampa(false);
    }

    syncFronte() {
      const el = document.getElementById('caoa-fronte-options');
      if (!el) return;
      el.innerHTML = this.state.fronti.map(f => {
        const ch     = f.sku === this.state.config.fronteSku ? ' checked' : '';
        const img    = this._optionImg(f.immagineUrl);
        const desc   = f.descrizioneBreve || '';
        return `<label class="caoa-option">
          <input type="radio" name="caoa-fronte" value="${this._esc(f.sku)}"${ch}>
          <span class="caoa-option-card">
            ${img}
            <span class="caoa-option-dot"></span>
            <span class="caoa-option-text">
              <strong>${this._esc(f.nome)}</strong>
              <span>${this._esc(desc)}</span>
            </span>
          </span>
        </label>`;
      }).join('');
    }

    /* Se il retro tavolo selezionato non regge più il formato FINITO (opera +
       passepartout), ripiega sul default e ridisegna — poi syncRetro mostra
       l'opzione disabilitata. Da chiamare a ogni cambio di misure o passepartout. */
    _applyRetroTavoloGate() {
      const sel = this.state.retri.find(r => r.sku === this.state.config.retroSku);
      if (sel && this._isRetroDisabled(sel)) {
        const dr = this._defaultRetro();
        if (dr) { this.state.config.retroSku = dr.sku; this.state.config.retro = dr; }
      }
      this.syncRetro();
    }

    syncRetro() {
      const el = document.getElementById('caoa-retro-options');
      if (!el) return;
      el.innerHTML = this.state.retri.map(r => {
        const ch     = r.sku === this.state.config.retroSku ? ' checked' : '';
        const dis    = this._isRetroDisabled(r) ? ' disabled' : '';
        const img    = this._optionImg(r.immagineUrl);
        const desc   = dis ? this._hintRetroLimite(r) : (r.descrizioneBreve || '');
        return `<label class="caoa-option${dis ? ' caoa-option--disabled' : ''}">
          <input type="radio" name="caoa-retro" value="${this._esc(r.sku)}"${ch}${dis}>
          <span class="caoa-option-card">
            ${img}
            <span class="caoa-option-dot"></span>
            <span class="caoa-option-text">
              <strong>${this._esc(r.nome)}</strong>
              ${desc ? `<span>${this._esc(desc)}</span>` : ''}
            </span>
          </span>
        </label>`;
      }).join('');
    }

    /* ── PASSEPARTOUT CARTONE ── */
    renderPassCartoneGallery() {
      const { base, altezza } = this.state.config;
      const grid = document.getElementById('caoa-pass-cartone-gallery');
      if (!grid) return;
      if (!this.state.passCartone.length) { grid.innerHTML = '<p class="caoa-hint">Nessun passepartout in cartone disponibile al momento.</p>'; return; }
      grid.innerHTML = this.state.passCartone.map(p => {
        const sel      = (this.state.config.hasPassCartone && p.sku === this.state.config.passCartoneSku) ? ' selected' : '';
        const compat   = _passCartoneCompatibile(p, base, altezza);
        const disabled = compat ? '' : ' caoa-gallery-item--disabled';
        const ariaDisabled = compat ? '' : ' aria-disabled="true"';
        const imgHtml = p.imgUrl
          ? `<img src="${this._esc(this._thumbUrl(p.imgUrl))}" alt="${this._esc(p.nome)}" loading="lazy">`
          : `<div class="caoa-gallery-item-placeholder">${this._esc(p.nome.split(' ').slice(0, 2).join(' '))}</div>`;
        return `<div class="caoa-gallery-item${sel}${disabled}" data-pass-cartone="${this._esc(p.sku)}"${ariaDisabled} title="${this._esc(p.nome)}${sel ? ' — riclicca per rimuovere' : ''}">${imgHtml}</div>`;
      }).join('');
      this._abilitaListbox(grid);
    }

    _updatePassCartoneUI() {
      const wrap = document.getElementById('caoa-pass-cartone-largh-wrap');
      if (wrap) wrap.style.display = this.state.config.hasPassCartone ? '' : 'none';
    }

    /* ── PASSEPARTOUT LEGNO ── */
    renderPassLegnoGallery() {
      const grid = document.getElementById('caoa-pass-legno-gallery');
      if (!grid) return;
      if (!this.state.passLegno.length) { grid.innerHTML = '<p class="caoa-hint">Nessun passepartout in legno disponibile al momento.</p>'; return; }
      grid.innerHTML = this.state.passLegno.map(p => {
        const sel     = (this.state.config.hasPassLegno && p.sku === this.state.config.passLegnoSku) ? ' selected' : '';
        const imgHtml = p.imgUrl
          ? `<img class="caoa-gallery-item-img" src="${this._esc(this._thumbUrl(p.imgUrl))}" alt="${this._esc(p.nome)}" loading="lazy">`
          : `<div class="caoa-gallery-item-placeholder">${this._esc(p.nome.split(' ').slice(0, 2).join(' '))}</div>`;
        return `<div class="caoa-gallery-item${sel}" data-pass-legno="${this._esc(p.sku)}" title="${this._esc(p.nome)} — ${p.larghezzaMm || '?'}mm${sel ? ' — riclicca per rimuovere' : ''}">${imgHtml}</div>`;
      }).join('');
      this._abilitaListbox(grid);
    }

    _updatePassLegnoState() {
      const profilo  = this.state.config.profilo;
      const battente = profilo?.battenteMm || 0;
      const locked   = profilo && battente > 0 && battente < BATTENTE_ALTO_MM;

      const step  = document.getElementById('caoa-step-pass-legno');
      const badge = document.getElementById('caoa-pass-legno-locked-badge');
      if (step)  step.classList.toggle('caoa-step--locked', !!locked);
      if (badge) badge.style.display = locked ? '' : 'none';

      if (locked && this.state.config.hasPassLegno) {
        this.state.config.hasPassLegno   = false;
        this.state.config.passLegnoSku   = null;
        this.state.config.passLegno      = null;
        this._passLegnoImg               = null;
        this._passLegnoImgUrl            = '';
        this._passLegnoPatternImg        = null;
        this._passLegnoPatternUrl        = '';
        this.renderPassLegnoGallery();
      }
    }

    /* ── PASSEPARTOUT EVENTS ── */
    _dispatchPassEvents() {
      const { hasPassCartone, passCartone, passCartoneLargh, hasPassLegno, passLegno } = this.state.config;
      const cartoneDetail = (hasPassCartone && passCartone)
        ? { tipo: 'cartone', sku: passCartone.sku || '', nome: passCartone.nome, nomeBreve: passCartone.nomeBreve || '', imgUrl: passCartone.imgUrl || '', larghezza: passCartoneLargh,
            spessoreMm: passCartone.spessoreMm || 0, anima: passCartone.anima || '',
            colore: passCartone.colore || '', tinta: passCartone.tinta || '', note: passCartone.note || '' }
        : null;
      const legnoDetail = (hasPassLegno && passLegno)
        ? { tipo: 'legno', sku: passLegno.sku || '', nome: passLegno.nome, nomeBreve: passLegno.nomeBreve || '', imgUrl: passLegno.imgUrl || '', larghezza: (passLegno.larghezzaMm || 0) / 10,
            colore: passLegno.colore || '',
            altezzaSezione: passLegno.altezzaSezione || 0, battenteMm: passLegno.battenteMm || 0,
            fotoCampioneUrl: passLegno.fotoCampioneUrl || '', fotoAngoloUrl: passLegno.fotoAngoloUrl || '' }
        : null;
      window._CAOA_PASS_CARTONE = cartoneDetail;
      window._CAOA_PASS_LEGNO   = legnoDetail;
      document.dispatchEvent(new CustomEvent('caoa:pass-cartone-changed', { detail: cartoneDetail }));
      document.dispatchEvent(new CustomEvent('caoa:pass-legno-changed',   { detail: legnoDetail   }));
    }

    /* ── ATTACCAGLIE (auto) ── */
    /* Terza copia della stessa regola attaccaglie (dopo aste e price_logic): soglia e
       conteggi vengono ora da PRICING (SSOT: price_logic.get_hangers_count) e si misura
       il BATTENTE, non l'opera — le attaccaglie stanno sul pezzo fisico, che col
       passepartout è più grande. Allineata il 28/07/2026. */
    _updateAttaccaglie() {
      /* Le attaccaglie sono roba da cornice. In montatura e pannello il pezzo lo tengono
         insieme le CLIP, che sono incluse nel prezzo e non si scelgono: qui si esce
         subito, altrimenti si ripopolavano le quantità che `setTipo` aveva azzerato e
         finivano in comanda attaccaglie mai scelte e non pagate. */
      if (this.state.config.tipo !== 'cornice') {
        this.state.config.attaccaglieQtys = {};
        return;
      }
      if (this.state.config.attTouched) return;
      const P = this.PRICING;
      const { base, altezza, hasPassCartone, passCartone, passCartoneLargh,
              hasPassLegno, passLegno } = this.state.config;
      if (!base || !altezza) return;
      const att = this.state.accessori.filter(a => (a.categoria || '').includes('attaccaglie'));
      if (!att.length) return;
      const ppcLargh = (hasPassCartone && passCartone && passCartoneLargh > 0) ? passCartoneLargh : 0;
      const pplMm    = (hasPassLegno && passLegno) ? (passLegno.larghezzaMm || 0) : 0;
      const batt     = window.FOTORECORD.dimsBattente(base, altezza, ppcLargh, pplMm);
      const suggested = (batt.b > P.SOGLIA_ATTACCAGLIE_CM || batt.h > P.SOGLIA_ATTACCAGLIE_CM)
        ? P.N_ATTACCAGLIE_GRANDE
        : P.N_ATTACCAGLIE_STD;
      this.state.config.attaccaglieQtys = { [att[0].sku]: suggested };
    }

    _updateAccessoriStep() {
      const el    = document.getElementById('caoa-attacc-step');
      const legEl = document.getElementById('caoa-legend-attacc');
      /* In montatura e pannello la scheda "Accessori" sotto il configuratore deve
         parlare delle CLIP, non delle attaccaglie da cornice: quelle non esistono in
         quel montaggio e non sono nel prezzo. Stesso dettaglio che i due configuratori
         pubblici pubblicano da `_updateClipMin`, così le sezioni a valle non devono
         sapere da quale configuratore arriva l'evento. */
      const modo = this.state.config.tipo;
      if (modo !== 'cornice') {
        const clip = this._clipDelModo;
        const n    = this._ultimeClip
                  || window.FOTORECORD.numeroClip(this.state.config.base || 0, this.state.config.altezza || 0, this.PRICING);
        const dett = {
          tipo: 'clip',
          nome: clip?.nome || 'Clip di fissaggio',
          imgUrl: (this._clipUrl || {})[modo] || '',
          materiale: clip?.materiale || '', colore: clip?.colore || '',
          descrizioneBreve: clip?.descrizioneBreve || '', note: clip?.note || '',
          clipQty: n,
        };
        if (legEl) legEl.textContent = ` — ${n} pz incluse`;
        window._CAOA_ACCESSORI = dett;
        document.dispatchEvent(new CustomEvent('caoa:accessori-changed', { detail: dett }));
        document.dispatchEvent(new CustomEvent('caoa:clip-changed',      { detail: dett }));
        if (el) {
          el.innerHTML = `<p class="caoa-hint">Le <strong>${n} clip</strong> di fissaggio sono incluse nel prezzo: `
                       + `tengono insieme lastra, opera e fondo. Non c'è nulla da scegliere.</p>`;
        }
        return;
      }
      const items = this.state.accessori.filter(a => (a.categoria || '').includes('attaccaglie'));
      const qtys  = this.state.config.attaccaglieQtys || {};
      const totQty = Object.values(qtys).reduce((s, q) => s + (q || 0), 0);
      if (legEl) legEl.textContent = totQty > 0 ? ` — ${totQty} pz` : '';
      const firstItem = items.find(a => (qtys[a.sku] || 0) > 0) || items[0] || null;
      const accdDetail = { tipo: 'attaccaglia', sku: firstItem?.sku || '', nome: firstItem?.nome || '', nomeBreve: firstItem?.nomeBreve || '', imgUrl: firstItem?.imgUrl || '', materiale: firstItem?.materiale || '', colore: firstItem?.colore || '', descrizioneBreve: firstItem?.descrizioneBreve || '', note: firstItem?.note || '', totQty, items: items.map(a => ({ sku: a.sku, nome: a.nome, qty: qtys[a.sku] || 0 })) };
      window._CAOA_ACCESSORI = accdDetail;
      document.dispatchEvent(new CustomEvent('caoa:accessori-changed', { detail: accdDetail }));
      if (!items.length) {
        if (el) el.innerHTML = '<p class="caoa-hint">Nessun accessorio disponibile.</p>';
        return;
      }
      if (!el) return;
      /* Stessa tile a 6 colonne di renderAttaccaglieStep (aste su misura, 16/09/2026):
         i due configuratori devono restare uguali. */
      const core = window.FOTORECORD.core;
      el.innerHTML =
        `<div class="caoa-attacc-gallery">` +
        items.map(a => {
          const qty   = qtys[a.sku] || 0;
          const label = core.didascaliaAccessorio(a.colore, a.sku);
          const img   = a.imgUrl
            ? `<img class="caoa-attacc-img" src="${this._esc(this._thumbUrl(a.imgUrl, 200))}" alt="" loading="lazy">`
            : '<div class="caoa-attacc-img-placeholder"></div>';
          return `<div class="caoa-attacc-card${qty > 0 ? ' active' : ''}" title="${this._esc(a.nome)}${a.descrizioneBreve ? ' — ' + this._esc(a.descrizioneBreve) : ''}">
            ${img}
            <div class="caoa-attacc-name">${this._esc(label)}</div>
            <div class="caoa-qty-stepper">
              <button type="button" class="caoa-qty-btn" data-attacc-delta="-1" data-attacc-sku="${this._esc(a.sku)}" aria-label="Diminuisci ${this._esc(label)}">−</button>
              <input type="number" class="caoa-qty-input caoa-attacc-qty-input" data-attacc-sku="${this._esc(a.sku)}"
                     value="${qty}" min="0" max="20" step="1" aria-label="Quantità ${this._esc(a.nome)}">
              <button type="button" class="caoa-qty-btn" data-attacc-delta="1" data-attacc-sku="${this._esc(a.sku)}" aria-label="Aumenta ${this._esc(label)}">+</button>
            </div>
          </div>`;
        }).join('') +
        `</div>`;
    }

    /* ── CANVAS PREVIEW (stesso approccio CASM, con immagine opera) ── */
    updateLiveView() {
      const { base, altezza } = this.state.config;
      const canvas = document.getElementById('caoa-live-canvas');
      const ph     = document.getElementById('caoa-live-placeholder');
      if (!canvas) return;
      const hasData = base > 0 && altezza > 0 && !isNaN(base) && !isNaN(altezza);
      if (!hasData) {
        canvas.style.display = 'none';
        if (ph) ph.style.display = 'flex';
        return;
      }
      canvas.style.display = 'block';
      canvas.hidden = false;
      if (ph) ph.style.display = 'none';
      this._fitCanvas(canvas);
      this._drawPreview(canvas);
      /* L'<img> dell'opera nell'HTML ha fatto da LCP: da qui in poi disegna il canvas.
         Si nasconde solo quando l'immagine dell'opera e' davvero nel canvas, altrimenti
         il cliente vedrebbe il rettangolo grigio di attesa al posto dell'opera. */
      if (this._operaImg) document.getElementById('caoa-opera-img')?.setAttribute('hidden', '');
    }

    _drawPreview(canvas) {
      const { ctx, W, H } = this._canvasCtx(canvas);
      const {
        base, altezza, profilo, solostampa,
        hasPassLegno, passLegno,
        hasPassCartone, passCartone, passCartoneLargh,
      } = this.state.config;

      /* Montatura e pannello si disegnano come nei configuratori pubblici: filo di
         profilo sottile (0,4 cm visibili) o nessun profilo, e l'opera che occupa tutto.
         Riusa lo stesso corpo di disegno passando una sezione visiva ridotta: il
         risultato è la stessa immagine che il cliente vede sulle pagine pubbliche. */
      const modo = this.state.config.tipo;
      const sezioneVisiva = (modo === 'montatura') ? PROF_VISIBILE_CM
                          : (modo === 'pannello')  ? 0
                          : null;

      const sezioneMm  = sezioneVisiva !== null ? sezioneVisiva * 10
                       : solostampa ? 0 : (profilo?.larghezzaMm || 20);
      const sezioneCm  = sezioneMm / 10.0;
      const passLegnoCm   = (!solostampa && hasPassLegno   && passLegno)   ? (passLegno.larghezzaMm || 0) / 10.0 : 0;
      const passCartoneCm = (!solostampa && hasPassCartone && passCartone) ? (passCartoneLargh || 0)              : 0;

      /* Catena sormonti (SSOT: PRICING_SSOT.SORMONTO_CM): fasce VISIBILI — legno
         = larghezza − sormonto, cartone = fascia inserita, opera − 2×sormonto.
         In solostampa (nessuna cornice) l'opera si mostra intera. */
      const sormCm     = solostampa ? 0 : (this.PRICING.SORMONTO_CM || 0);
      const legnoVisCm = passLegnoCm > 0 ? Math.max(passLegnoCm - sormCm, 0) : 0;
      const operaVisW  = Math.max(base    - 2 * sormCm, 0.1);
      const operaVisH  = Math.max(altezza - 2 * sormCm, 0.1);

      const totW  = operaVisW + 2 * (sezioneCm + legnoVisCm + passCartoneCm);
      const totH  = operaVisH + 2 * (sezioneCm + legnoVisCm + passCartoneCm);
      const scale = Math.min((W - CVS_PAD * 2) / totW, (H - CVS_PAD * 2) / totH);
      const drawW = totW * scale;
      const drawH = totH * scale;
      const fx    = (W - drawW) / 2;
      const fy    = (H - drawH) / 2;
      const profPx = sezioneCm * scale;
      const legnoPx = legnoVisCm * scale;
      const cartPx  = passCartoneCm * scale;

      /* canvas trasparente: il fondo è quello della card */
      ctx.clearRect(0, 0, W, H);

      /* ombra */
      this._ombraCornice(ctx, fx, fy, drawW, drawH);
      ctx.fillStyle = '#ccc';
      ctx.fillRect(fx, fy, drawW, drawH);

      /* ── LAYER 1: cornice ── */
      if (!solostampa) {
        if (this._frameImg && profilo) {
          const slicePx = this._frameImg.naturalWidth * (sezioneMm / 200);
          this._draw9Slice(ctx, this._frameImg, slicePx, fx, fy, drawW, drawH, profPx, this._patternImg);
        } else {
          ctx.fillStyle = profilo ? this._fallbackColor(profilo.colore) : 'rgba(0,0,0,0.10)';
          ctx.fillRect(fx,             fy,             drawW, profPx);
          ctx.fillRect(fx,             fy+drawH-profPx, drawW, profPx);
          ctx.fillRect(fx,             fy+profPx,       profPx, drawH-2*profPx);
          ctx.fillRect(fx+drawW-profPx, fy+profPx,      profPx, drawH-2*profPx);
        }
      }

      let opX = fx + profPx, opY = fy + profPx;
      let opW = drawW - 2 * profPx, opH = drawH - 2 * profPx;

      /* ── LAYER 2: passepartout legno ── */
      if (legnoPx > 0) {
        if (this._passLegnoImg) {
          const slicePx = this._passLegnoImg.naturalWidth * ((passLegno.larghezzaMm || 30) / 200);
          this._draw9Slice(ctx, this._passLegnoImg, slicePx, opX, opY, opW, opH, legnoPx, this._passLegnoPatternImg);
        } else {
          ctx.fillStyle = PASS_LEGNO_COLOR;
          ctx.fillRect(opX, opY, opW, legnoPx);
          ctx.fillRect(opX, opY+opH-legnoPx, opW, legnoPx);
          ctx.fillRect(opX, opY+legnoPx, legnoPx, opH-2*legnoPx);
          ctx.fillRect(opX+opW-legnoPx, opY+legnoPx, legnoPx, opH-2*legnoPx);
        }
        opX += legnoPx; opY += legnoPx; opW -= 2*legnoPx; opH -= 2*legnoPx;
      }

      /* ── LAYER 3: passepartout cartone ── */
      if (cartPx > 0) {
        if (this._passCartoneImg) {
          const tileKey = `${this.state.config.passCartoneSku}_${Math.round(cartPx)}`;
          if (this._passCartoneTileKey !== tileKey) {
            const ts = Math.max(1, Math.round(cartPx));
            const off = document.createElement('canvas');
            off.width = ts; off.height = ts;
            off.getContext('2d').drawImage(this._passCartoneImg, 0, 0, ts, ts);
            this._passCartoneTile    = off;
            this._passCartoneTileKey = tileKey;
          }
          const pattern = ctx.createPattern(this._passCartoneTile, 'repeat');
          const oc = 1;
          [
            { x: opX,              y: opY,              w: opW,   h: cartPx },
            { x: opX,              y: opY+opH-cartPx,   w: opW,   h: cartPx },
            { x: opX,              y: opY+cartPx-oc,        w: cartPx, h: opH-2*cartPx+2*oc },
            { x: opX+opW-cartPx,   y: opY+cartPx-oc,        w: cartPx, h: opH-2*cartPx+2*oc },
          ].forEach(b => {
            ctx.save();
            ctx.beginPath(); ctx.rect(b.x, b.y, b.w, b.h); ctx.clip();
            pattern.setTransform(new DOMMatrix().translate(b.x, b.y));
            ctx.fillStyle = pattern; ctx.fillRect(b.x, b.y, b.w, b.h);
            ctx.restore();
          });
          ctx.strokeStyle = 'rgba(0,0,0,0.12)'; ctx.lineWidth = 1;
          ctx.strokeRect(opX+cartPx, opY+cartPx, opW-2*cartPx, opH-2*cartPx);
        } else {
          ctx.fillStyle = PASS_CARTONE_COLOR;
          ctx.fillRect(opX, opY, opW, cartPx);
          ctx.fillRect(opX, opY+opH-cartPx, opW, cartPx);
          ctx.fillRect(opX, opY+cartPx, cartPx, opH-2*cartPx);
          ctx.fillRect(opX+opW-cartPx, opY+cartPx, cartPx, opH-2*cartPx);
        }
        this._drawPassCartoneBevel(ctx, opX, opY, opW, opH, cartPx, scale);
        opX += cartPx; opY += cartPx;
        opW -= 2*cartPx; opH -= 2*cartPx;
      }

      /* ── LAYER 4: immagine opera ── */
      if (this._operaImg && opW > 0 && opH > 0) {
        this._drawImgCover(ctx, this._operaImg, opX, opY, opW, opH);
      } else {
        ctx.fillStyle = '#e8e0d8';
        ctx.fillRect(opX, opY, opW, opH);
      }

      /* ── LAYER 5: effetto stampa (lucida/tela) poi fronte (vetro/crilex) ── */
      this._drawStampaEffetto(ctx, opX, opY, opW, opH, base);
      this._drawFronteEffetto(ctx, opX, opY, opW, opH);

      ctx.strokeStyle = 'rgba(0,0,0,0.08)'; ctx.lineWidth = 1;
      ctx.strokeRect(fx, fy, drawW, drawH);

      /* ── LAYER 6: clip metalliche (solo montatura/pannello) ──
         Sono l'unico segno visibile che tiene insieme il pacchetto: senza, l'anteprima
         è una lastra che sta su da sola. Si riusa `_drawClips` della classe montature —
         stessa geometria, stessa immagine, stesso conteggio dei configuratori pubblici. */
      if (modo !== 'cornice' && this._clipImg) {
        ConfiguratoreMontature.prototype._drawClips.call(this, ctx, fx, fy, drawW, drawH, base, altezza);
      }

      this._drawDimLabels(ctx, fx, fy, drawW, drawH, totW, totH);
    }

    _drawDimLabels(ctx, fx, fy, drawW, drawH, extW, extH) {
      const off = 14, tick = 5;
      ctx.strokeStyle = '#8b2635'; ctx.lineWidth = 1.2; ctx.fillStyle = '#8b2635';
      ctx.font = '600 13px Helvetica,Arial,sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'top';
      const by = fy + drawH + off;
      ctx.beginPath(); ctx.moveTo(fx, by); ctx.lineTo(fx + drawW, by); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(fx, by - tick); ctx.lineTo(fx, by + tick); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(fx + drawW, by - tick); ctx.lineTo(fx + drawW, by + tick); ctx.stroke();
      ctx.fillText(`${this._round(extW, 1)} cm`, fx + drawW / 2, by + 3);
      const ax = fx + drawW + off;
      ctx.beginPath(); ctx.moveTo(ax, fy); ctx.lineTo(ax, fy + drawH); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(ax - tick, fy); ctx.lineTo(ax + tick, fy); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(ax - tick, fy + drawH); ctx.lineTo(ax + tick, fy + drawH); ctx.stroke();
      ctx.save();
      ctx.translate(ax + 14, fy + drawH / 2); ctx.rotate(Math.PI / 2);
      ctx.textBaseline = 'middle'; ctx.fillText(`${this._round(extH, 1)} cm`, 0, 0);
      ctx.restore();
    }

    /* ── Immagine opera ── */
    _preloadOperaImage() {
      const url = this.state.opera?.imgSmall || this.state.opera?.imgUrl;
      if (!url || url === this._operaImgUrl) return;
      this._operaImgUrl = url;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        this._operaImg = img;
        // Ratio reale dall'immagine — sovrascrive il metafield custom.proporzioni
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
          this.state.opera.ratio = img.naturalWidth / img.naturalHeight;
        }
        const badge = document.getElementById('caoa-live-ratio-badge');
        if (badge) {
          const r = this.state.opera.ratio;
          badge.textContent = r > 1.05 ? 'Orizzontale' : r < 0.95 ? 'Verticale' : 'Quadrato';
        }
        this._setDefaultDim();
        this.updateLiveView();
      };
      img.onerror = () => console.warn('[CAOA] Immagine opera non caricata:', url);
      img.src = url;
    }

    /* ── VALIDAZIONE ──
       Il contenitore `#caoa-validation` sta nella section dal principio, ma nessuno lo
       riempiva: questo era l'unico dei quattro configuratori senza `validateConfig`, e
       una misura sotto il minimo o un formato da imballo rinforzato passavano muti.

       Regole identiche ai fratelli. Quello che NON fa, di proposito: non spegne la CTA e
       non azzera il prezzo. Gli altri tre fanno `valid ? calcola() : null`, ma qui il
       calcolo e' inline in calculateLive, e cambiare quel gate vorrebbe dire cambiare il
       percorso di conversione di 142 pagine opera — una decisione, non una correzione.
       Per la stessa ragione non c'e' il controllo "hai scelto un profilo": oggi una
       pagina opera un prezzo lo mostra comunque (la sola stampa), e trasformarlo in
       errore e' quella stessa decisione. */
    validateConfig() {
      const { base, altezza, profilo } = this.state.config;
      const errors   = [];
      const warnings = [];

      /* I minimi del profilo scelto vincono su quello del configuratore, come in CASM;
         senza profilo (solo stampa, o non ancora scelto) vale PRICING.DIM_MIN. */
      const minBase = (profilo && profilo.dimMinBase > 0) ? profilo.dimMinBase : this.PRICING.DIM_MIN;
      const minAlt  = (profilo && profilo.dimMinAlt  > 0) ? profilo.dimMinAlt  : this.PRICING.DIM_MIN;

      if (base === null || isNaN(base)) {
        errors.push('Inserisci la misura base (larghezza).');
      } else if (base < minBase) {
        errors.push(`Base minima: ${minBase} cm.`);
      }

      if (altezza === null || isNaN(altezza)) {
        errors.push('Inserisci la misura altezza.');
      } else if (altezza < minAlt) {
        errors.push(`Altezza minima: ${minAlt} cm.`);
      }

      if (base !== null && altezza !== null && !isNaN(base) && !isNaN(altezza)) {
        const f = this._getFascia(base, altezza);
        if (f.soloPreventivo) {
          warnings.push('Formato grande: configurazione disponibile solo su preventivo.');
        } else if (f.supplementoGrande > 0) {
          warnings.push('Per formati con un lato oltre ' + this.PRICING.SOGLIA_SUPPLEMENTO_CM + ' cm, il trasporto avviene in imballaggio rinforzato.');
        }
      }

      const vincoloCrilex = this._vincoloCrilex(base, altezza);
      if (vincoloCrilex) errors.push(vincoloCrilex);

      return { valid: errors.length === 0, errors, warnings };
    }

    _renderValidation({ errors, warnings }) {
      const area = document.getElementById('caoa-validation');
      if (!area) return;
      area.innerHTML = [
        ...errors.map(e   => `<div class="caoa-msg error"><span>✕</span><span>${this._esc(e)}</span></div>`),
        ...warnings.map(w => `<div class="caoa-msg warning"><span>Attenzione:</span><span>${this._esc(w)}</span></div>`),
      ].join('');
    }

    /* ── CALCOLO PREZZO ── */
    calculateLive() {
      // gallery compat + auto-deselect del cartone incompatibile con le dimensioni
      this.renderPassCartoneGallery();
      if (this.state.config.hasPassCartone) {
        const _pc = this.state.config.passCartone;
        const _b  = this.state.config.base, _a = this.state.config.altezza;
        if (_pc && !_passCartoneCompatibile(_pc, _b, _a)) {
          this.state.config.hasPassCartone = false;
          this.state.config.passCartoneSku = null;
          this.state.config.passCartone    = null;
          this._passCartoneImg = null; this._passCartoneImgUrl = '';
          this.renderPassCartoneGallery();
          this._updatePassCartoneUI();
        }
      }
      /* Attaccaglie automatiche: dipendono dal battente, quindi cambiano anche col
         passepartout e non solo con le misure — stessa ragione del ramo aste. */
      this._updateAttaccaglie();

      /* PRIMA del rientro anticipato qui sotto: senza base o altezza calculateLive esce
         subito, ed e' proprio il caso in cui il messaggio serve di piu'. */
      this._renderValidation(this.validateConfig());

      const {
        base, altezza, profilo, fronte, retro, solostampa, stampa,
        hasPassLegno, passLegno,
        hasPassCartone, passCartone, passCartoneLargh,
      } = this.state.config;

      /* Un'opera in cornice senza stampa scelta non e' un acquisto: il carrello prendeva
         opera + cornice senza stampa (controllo-sito 14/10/2026, 51 € invece di 60).
         Si aspetta la scelta del cliente, come per le misure; «Solo stampa» ha il suo ramo. */
      this._mancaStampa = !!(base && altezza && !solostampa && !stampa);
      if (!base || !altezza || this._mancaStampa) { this._lastCalcResult = null; this._renderPrice(null); this._publishPrezziComponenti(null); return; }

      const fascia         = this._getFascia(base, altezza);
      const soloPreventivo = fascia.soloPreventivo;
      /* flag esplicito della fascia (30/07/2026): prima si deduceva da
         `supplementoGrande > 0`, cioè dall'importo dell'imballo — vedi FR.getFascia. */
      const isGrande       = fascia.isGrande;

      const areaMq   = base * altezza / 10000;
      /* markup PER-COMPONENTE: fronte/retro/stampa portano markup (std) + markupGrande. */
      const mk = this._makeMk(isGrande);

      /* stampa */
      let costoStampa = 0;
      if (stampa) {
        if (stampa.conTelaio) {
          /* Tela + TELAIO (listello 43T) — stessa formula del ramo aste, stessa SSOT.
             Le misure del telaio sono un dato di catalogo (PRICING.TELAIO_TELA_*), non
             un'opzione del cliente: nessun selettore di altezza in questo configuratore. */
          const P = this.PRICING;
          const baseTela = base    + 2 * P.TELAIO_TELA_SPESS_CM + 2 * P.ABBONDANZA_TELA_CM;
          const altTela  = altezza + 2 * P.TELAIO_TELA_SPESS_CM + 2 * P.ABBONDANZA_TELA_CM;
          const mkTelaio = isGrande ? P.TELAIO_TELA_MARKUP_GRANDE : P.TELAIO_TELA_MARKUP;
          const mlTelaio = window.FOTORECORD.perimEsternoM(
            base - 2 * P.TELAIO_TELA_LARGH_CM, altezza - 2 * P.TELAIO_TELA_LARGH_CM,
            P.TELAIO_TELA_LARGH_CM * 10);
          costoStampa = (baseTela * altTela / 10000) * (stampa.costoMq || 0) * mk(stampa)
                      + mlTelaio * P.TELAIO_TELA_COSTO_ML * mkTelaio;
        } else {
          // Stampa fotografica: fascia (small/pro) risolta per misura, markup ereditato da stampa
          const fotoTier = this._resolveFotoTier() || stampa;
          costoStampa = areaMq * (fotoTier.costoMq || 0) * mk(fotoTier, stampa);
        }
      }

      /* cornice — catena sormonti (SSOT: FOTORECORD.dimsBattente = price_logic):
         asta/fronte/retro sulla misura BATTENTE; areaMq (opera) resta per la stampa. */
      /* Gli addendi restano separati (`voci`) perche' li leggono i summary degli accordion;
         `costoCornice` e' la loro somma, come prima. accessori: null = clip comprese nel
         prezzo (montatura/pannello), non prezzate a parte. */
      const voci = { profilo: 0, fronte: 0, retro: 0, passCartone: 0, passLegno: 0, accessori: null };
      const _modo = this.state.config.tipo;
      if (!solostampa && _modo !== 'cornice') {
        /* MONTATURA / PANNELLO — stessa identica formula dei due configuratori
           pubblici: si chiamano le funzioni condivise di FOTORECORD, non se ne
           riscrive una terza copia. Differenze rispetto alla cornice: niente
           passepartout, niente attaccaglie a scelta, clip incluse nel prezzo.
           La stampa NON entra qui: resta la voce `costoStampa` calcolata sopra,
           che nelle opere ha le sue fasce foto (small/pro) già risolte. */
        const nClips   = window.FOTORECORD.numeroClip(base, altezza, this.PRICING);
        const clipUnit = this._clipUnitNetto;
        const comuni   = { base, altezza, fronte, retro, stampa: null, nClips, clipUnit, mk };
        const c = (_modo === 'montatura')
          ? window.FOTORECORD.montaturaNetto({ ...comuni, profilo, profCm: this.PRICING.PROF_CM })
          : window.FOTORECORD.pannelloNetto(comuni);
        voci.profilo = c.vendita.asta || 0;
        voci.fronte  = c.vendita.fronte;
        voci.retro   = c.vendita.retro;
        /* le clip entrano nel totale ma non hanno un importo da mostrare a parte */
        voci.clip    = c.vendita.clip;
        this._ultimeClip = c.nClips;
      } else if (!solostampa && profilo) {
        const _ppcLargh = (hasPassCartone && passCartone && passCartoneLargh > 0) ? passCartoneLargh : 0;
        const _pplMm    = (hasPassLegno && passLegno) ? (passLegno.larghezzaMm || 0) : 0;
        const batt      = window.FOTORECORD.dimsBattente(base, altezza, _ppcLargh, _pplMm);
        const areaBattMq = batt.b * batt.h / 10000;
        voci.profilo = window.FOTORECORD.perimEsternoM(batt.b, batt.h, profilo.larghezzaMm) * (profilo.costoMl || 0) * mk(profilo);
        if (fronte && fronte.sku) {
          voci.fronte = areaBattMq * (fronte.costoMq || 0) * mk(fronte);
        }
        if (retro && retro.sku) {
          voci.retro = areaBattMq * (retro.costoMq || 0) * mk(retro);
        }
        if (hasPassLegno && passLegno) {
          const pplInt = window.FOTORECORD.ppLegnoInterne(base, altezza, _ppcLargh);
          voci.passLegno = window.FOTORECORD.perimEsternoM(pplInt.b, pplInt.h, passLegno.larghezzaMm) * (passLegno.costoMl || 0) * mk(passLegno);
        }
        if (hasPassCartone && passCartone && passCartoneLargh > 0) {
          const areaCart = window.FOTORECORD.areaPpCartoneMq(base, altezza, passCartoneLargh);
          voci.passCartone = areaCart * (passCartone.costoMq || 0) * mk(passCartone);
        }
        const attItemsCalc = this.state.accessori.filter(a => (a.categoria || '').includes('attaccaglie'));
        const attQtysCalc  = this.state.config.attaccaglieQtys || {};
        voci.accessori = 0;
        attItemsCalc.forEach(a => {
          const qty = attQtysCalc[a.sku] || 0;
          if (qty > 0) {
            /* a.prezzo = prezzo di vendita (IVA incl./pz) riportato a netto. Il ripiego
               `a.costoUnitario * 2.9` era irraggiungibile e sbagliato — vedi nota gemella
               nel calcolo delle aste. */
            const unit = (Number(a.prezzo) || 0) / (1 + this.PRICING.IVA);
            voci.accessori += unit * qty;
          }
        });

      }
      const costoCornice = voci.profilo + voci.fronte + voci.retro + voci.passCartone + voci.passLegno
                         + (voci.accessori || 0) + (voci.clip || 0);

      /* Dato incompleto → nessun prezzo, non un prezzo approssimato. */
      if (this._markupMancante(mk)) { this._lastCalcResult = null; this._renderPrice(null); this._publishPrezziComponenti(null); return; }

      /* totale: valoreOpera già IVA inclusa + config × IVA */
      const valoreOpera = parseFloat(this.state.opera?.valoreOpera) || 0;
      const supplemento = window.FOTORECORD.imballoNetto(fascia);
      const costoConfig    = (costoStampa + costoCornice + supplemento) * (1 + this.PRICING.IVA);
      const prezzoUnitario = valoreOpera + costoConfig;
      const quantita       = this.state.config.quantita || 1;
      const tot            = prezzoUnitario * quantita;

      this._lastCalcResult = { tot, prezzoUnitario, costoConfig };
      this._renderPrice(tot, fascia, valoreOpera, costoStampa, costoCornice);
      /* In fascia solo-preventivo il totale e' una stima: niente importi per componente. */
      this._publishPrezziComponenti(soloPreventivo ? null : {
        profilo: voci.profilo, fronte: voci.fronte, retro: voci.retro,
        passCartone: voci.passCartone, passLegno: voci.passLegno,
        stampa: costoStampa, accessori: voci.accessori,
      });
      this._updateAccessoriStep();
    }

    _renderPrice(tot, fascia, valoreOpera, costoStampa, costoCornice) {
      const amountEls  = document.querySelectorAll('.caoa-price-amount');
      const noteEls    = document.querySelectorAll('.caoa-price-note');
      const eyebrowEls = document.querySelectorAll('.caoa-price-eyebrow');
      this._rigaSpedizione(false);

      if (fascia?.soloPreventivo) {
        eyebrowEls.forEach(el => { el.textContent = 'Totale configurazione'; });
        amountEls.forEach(el => { el.textContent = `€ ${this._fmt(tot)}`; el.setAttribute('data-placeholder', 'false'); });
        this._setCta({ result: { soloPreventivo: true } });
        document.querySelectorAll('.caoa-price-breakdown').forEach(el => el.style.display = 'none');
        noteEls.forEach(el => { el.textContent = 'Stima indicativa — il prezzo definitivo, spedizione compresa, viene confermato via email'; el.style.color = 'var(--c-warning)'; el.style.fontStyle = 'italic'; });
        return;
      }

      eyebrowEls.forEach(el => { el.textContent = 'Totale configurazione'; });
      noteEls.forEach(el => { el.style.color = ''; el.style.fontStyle = ''; });

      if (tot === null) {
        const manca = this._mancaStampa ? 'Scegli la stampa' : 'Scegli dimensioni';
        amountEls.forEach(el => { el.textContent = manca; el.setAttribute('data-placeholder', 'true'); });
        this._setCta({ result: null, incompleteText: this._mancaStampa ? 'Scegli la stampa' : 'Scegli le dimensioni' });
        document.querySelectorAll('.caoa-price-breakdown').forEach(el => el.style.display = 'none');
        noteEls.forEach(el => { el.textContent = ''; });
        return;
      }

      const quantita       = this.state.config.quantita || 1;
      const prezzoUnitario = this._lastCalcResult?.prezzoUnitario;
      const supplemento    = window.FOTORECORD.imballoNetto(fascia);
      const costoConf      = (costoStampa + costoCornice + supplemento) * (1 + this.PRICING.IVA);
      const bucket         = this._pickBucket(costoConf);
      this._bucket         = bucket;
      const bucketEuro     = bucket ? bucket.c / 100 : costoConf;
      const totDisplay     = ((valoreOpera || 0) + bucketEuro) * quantita;

      amountEls.forEach(el => { el.textContent = `€ ${this._fmt(totDisplay)}`; el.removeAttribute('data-placeholder'); });
      this._setCta({ result: { soloPreventivo: false }, bucket: bucket });

      document.querySelectorAll('.caoa-price-unit').forEach(el => {
        if (quantita > 1 && prezzoUnitario) { el.textContent = `${quantita} × € ${this._fmt((valoreOpera || 0) + bucketEuro)} cad.`; el.style.display = ''; }
        else { el.style.display = 'none'; }
      });

      document.querySelectorAll('.caoa-price-breakdown').forEach(el => {
        el.style.display = '';
        const parts = [];
        if (valoreOpera) parts.push(`Opera: € ${this._fmt(valoreOpera)}`);
        if (bucketEuro)  parts.push(`Config.: € ${this._fmt(bucketEuro)}`);
        el.textContent = parts.join(' + ');
      });

      this._rigaSpedizione(true);
      // Nota dell'imballo: c'e' se l'imballo e' nel prezzo (formato oltre la soglia).
      if (supplemento > 0) {
        noteEls.forEach(el => { el.textContent = this._notaImballo(this.PRICING.SUPPLEMENTO_IMBALLO_IVA); });
      } else {
        noteEls.forEach(el => { el.textContent = ''; });
      }
    }

    /* ── MODAL PREVENTIVO ── */
    openModal() {
      const overlay = document.getElementById('caoa-modal-overlay');
      if (!overlay) return;
      this._buildModalText();
      overlay.classList.add('open');
      document.body.style.overflow = 'hidden';
    }

    _buildModalText() {
      const { base, altezza, profilo, fronte, retro, stampa,
              solostampa, hasPassLegno, passLegno, hasPassCartone, passCartone, passCartoneLargh } = this.state.config;
      const opera = this.state.opera;

      const rows = [
        ['Opera',             nb(opera) || '—'],
        ['Dimensioni stampa', `${base} × ${altezza} cm`],
        ['Tipo stampa',       nb(stampa) || '—'],
      ];
      if (solostampa) {
        rows.push(['Configurazione', 'Nessuna stampa, senza cornice']);
      } else {
        rows.push(['Profilo asta', nb(profilo) || '—']);
        rows.push(['Fronte', nb(fronte) || 'Nessuno']);
        rows.push(['Retro',  nb(retro)  || 'Nessuno']);
        if (hasPassLegno)   rows.push(['Passepartout legno', nb(passLegno) || '—']);
        if (hasPassCartone) rows.push(['Passepartout cartone', `${nb(passCartone) || '—'} - bordo ${passCartoneLargh} cm`]);
        const attArr    = this.state.accessori.filter(x => (x.categoria || '').includes('attaccaglie'));
        const attQtysM  = this.state.config.attaccaglieQtys || {};
        const attPartsM = attArr.filter(a => (attQtysM[a.sku] || 0) > 0).map(a => `${attQtysM[a.sku]} × ${nb(a)}`);
        if (attPartsM.length) rows.push(['Accessori', attPartsM.join(', ')]);
      }
      const qty = this.state.config.quantita || 1;
      if (qty > 1) rows.push(['Quantità', String(qty)]);

      const recapBody = document.getElementById('caoa-modal-recap-rows');
      if (recapBody) {
        recapBody.innerHTML = rows.map(([k, v]) =>
          `<div class="caoa-modal-recap-row"><span>${this._esc(k)}</span><span>${this._esc(v)}</span></div>`
        ).join('');
      }

      const priceEl  = document.querySelector('.caoa-price-amount');
      const priceTxt = priceEl?.textContent || '—';
      const priceValEl = document.getElementById('caoa-modal-price-value');
      if (priceValEl) priceValEl.textContent = priceTxt;

      const lines = [
        `Richiesta preventivo — Opera d'Autore`,
        `Opera: ${nb(opera) || '—'}`,
        ...rows.slice(1).map(([k, v]) => `${k}: ${v}`),
        `Stima prezzo: ${priceTxt}`,
      ];
      const hidden = document.getElementById('caoa-modal-body');
      if (hidden) hidden.value = lines.join('\n');
    }

    /* ── CARRELLO (bundle: opera + fascia configurazione) ── */
    async _addToCart() {
      const operaVariantId = (this.state.opera && this.state.opera.variantId)
        || window.SHOPIFY_OPERE_AUTORE?.opera?.variantId || null;
      const costoConfig    = this._lastCalcResult?.costoConfig;
      const quantita       = this.state.config.quantita || 1;
      const bucket         = this._bucket || this._pickBucket(costoConfig);
      if (!operaVariantId) return;
      const ctaBtn = document.getElementById('caoa-cta');
      if (ctaBtn) { ctaBtn.disabled = true; ctaBtn.textContent = 'Aggiungendo al carrello…'; }
      try {
        // id di bundle: lega la riga "opera" e la riga "cornice/configurazione" nello stesso ordine
        this._bundleId = 'opera-' + (this.state.opera?.handle || 'na') + '-' + Date.now().toString(36);
        const items = [
          // 1. Opera: prodotto reale al prezzo Shopify (inventory tracking corretto).
          // Stessa quantità della configurazione: ogni pezzo è una stampa dell'opera e
          // porta il suo `valore_opera` (compenso per opera VENDUTA, price_logic.
          // calcola_netto_autore) — è quello che la pagina mostra, «N × (opera + config)».
          // Fino al 16/09/2026 qui c'era `quantity: 1` (dal 3.0): a 2 pezzi il cliente
          // pagava un'opera e due cornici, un pezzo in meno di quanto detto a schermo.
          { id: operaVariantId, quantity: quantita,
            properties: { 'Tipologia': "Opera d'autore",
                          'Configurazione': { cornice: 'Opera con cornice',
                                              montatura: 'Opera in montatura a giorno',
                                              pannello: 'Opera in pannello solo vetro' }[this.state.config.tipo] || 'Opera con cornice',
                          '_bundle': this._bundleId } },
        ];
        // 2. Cornice/configurazione: fascia di prezzo (costoConfig IVA inclusa)
        if (bucket && bucket.id) {
          items.push({ id: bucket.id, quantity: quantita,
            properties: this._buildCartProperties() });
        }
        // Anteprima del mockup (opera incorniciata) sulla riga "opera"
        const anteprima = await this._capturePreviewBlob();
        if (anteprima) items[0].properties['_anteprima'] = anteprima;
        await window.FOTORECORD.core.addToCart({ items: items });
      } catch (e) {
        console.error('[CAOA] add to cart error', e);
        if (ctaBtn) { ctaBtn.disabled = false; ctaBtn.textContent = 'Aggiungi al carrello'; }
      }
    }

    _buildCartProperties() {
      const { base, altezza, profilo, fronte, retro, solostampa,
              stampa, hasPassLegno, passLegno,
              hasPassCartone, passCartone, passCartoneLargh } = this.state.config;
      const operaTitolo = nb(this.state.opera) || '—';
      const modo = this.state.config.tipo;
      /* La Tipologia disambigua le superfici in carrello, nelle notifiche e in comanda:
         qui deve dire anche COME l'opera è montata, altrimenti in laboratorio una
         montatura a giorno arriva indistinguibile da una cornice. */
      const TIPOLOGIA = { cornice: 'Cornice su misura', montatura: 'Montatura a giorno', pannello: 'Pannello solo vetro' };
      const props = {
        'Tipologia':         solostampa ? 'Stampa fotografica' : (TIPOLOGIA[modo] || 'Cornice su misura'),
        'Opera':             operaTitolo,
        'Dimensioni':        `${base} × ${altezza} cm`,
        // Prezzo di UN pezzo della SOLA configurazione (la riga-fascia): l'opera è
        // l'altra riga del bundle e ha il suo prezzo Shopify — vedi commento gemello
        // in ConfiguratoreCornice._buildCartProperties.
        '_prezzo_calcolato_pezzo': (this._lastCalcResult?.costoConfig || 0).toFixed(2),
      };
      if (this._bundleId) props['_bundle'] = this._bundleId;
      if (!solostampa && modo !== 'cornice') {
        // montatura/pannello: niente passepartout, clip incluse (non sono una scelta)
        if (modo === 'montatura' && profilo) props['Profilo'] = nb(profilo);
        if (fronte?.sku) props['Fronte'] = nb(fronte);
        if (retro?.sku)  props['Retro']  = nb(retro);
        props['Clip di fissaggio'] = String(this._ultimeClip || window.FOTORECORD.numeroClip(base, altezza, this.PRICING));
        // Nascoste — vedi commento gemello in ConfiguratoreCornice._buildCartProperties.
        // Questa classe non ridefinisce _profiloCodice: qui torna sempre l'id_db puro
        // (nessun prefisso 'montatura', a differenza di ConfiguratoreMontature).
        props['_tipo_prodotto'] = modo; // 'montatura' | 'pannello'
        if (modo === 'montatura') props['_profilo_id_db'] = profilo?.sku ? this._profiloCodice(profilo) : '';
        props['_fronte_sku'] = fronte?.sku || '';
        props['_retro_sku']  = retro?.sku  || '';
        props['_clip_qty']   = props['Clip di fissaggio'];
      } else if (!solostampa && profilo) {
        props['Profilo'] = nb(profilo);
        if (fronte?.sku)  props['Fronte'] = nb(fronte);
        if (retro?.sku)   props['Retro']  = nb(retro);
        if (hasPassLegno && passLegno)     props['Passepartout in legno']    = nb(passLegno);
        if (hasPassCartone && passCartone) props['Passepartout in cartone']  = `${nb(passCartone)} - bordo ${passCartoneLargh} cm`;
        // Nascoste — cornice-in-bundle.
        props['_tipo_prodotto'] = 'aste';
        props['_profilo_id_db'] = profilo?.sku ? this._profiloCodice(profilo) : '';
        props['_fronte_sku']    = fronte?.sku || '';
        props['_retro_sku']     = retro?.sku  || '';
        if (hasPassCartone && passCartone) {
          props['_pp_cartone_sku']      = passCartone.sku || '';
          props['_pp_cartone_largh_cm'] = String(passCartoneLargh || '');
        }
        if (hasPassLegno && passLegno) props['_pp_legno_sku'] = passLegno.sku || '';
      }
      if (stampa) {
        props['Stampa'] = nb(stampa) + (stampa.conTelaio ? ' - su telaio' : '');
        props['_stampa_tipo'] = stampa.conTelaio ? 'tela' : 'foto';
        props['_stampa_sku']  = stampa.sku || '';
      }
      return props;
    }

    _closeModal() {
      const overlay = document.getElementById('caoa-modal-overlay');
      if (overlay) overlay.classList.remove('open');
      document.body.style.overflow = '';
    }

    /* ── EVENT LISTENERS ── */
    setupEventListeners() {
      if (this._listenersAttached) return;
      this._listenersAttached = true;
      const root = document.getElementById('configuratore-opere-autore');
      if (!root) return;

      /* filtri */
      root.addEventListener('click', e => {
        const btn = e.target.closest('[data-battente]');
        if (!btn) return;
        this.state.filters.battente = btn.dataset.battente;
        this._renderBattenteFilter();
        this.renderGallery();
      });
      root.addEventListener('click', e => {
        const btn = e.target.closest('[data-colore]');
        if (!btn) return;
        this.state.filters.colore = btn.dataset.colore;
        root.querySelectorAll('[data-colore]').forEach(b => b.classList.toggle('active', b === btn));
        this.renderGallery();
      });
      root.addEventListener('click', e => {
        const btn = e.target.closest('[data-finitura]');
        if (!btn) return;
        this.state.filters.finitura = btn.dataset.finitura;
        root.querySelectorAll('[data-finitura]').forEach(b => b.classList.toggle('active', b === btn));
        this.renderGallery();
      });

      /* selezione profilo */
      root.addEventListener('click', e => {
        const item = e.target.closest('.caoa-gallery-item[data-sku]');
        if (!item) return;
        this.selectProfilo(item.dataset.sku);
      });

      /* stampa (riclic = nessuna) */
      root.addEventListener('change', e => {
        if (e.target.name !== 'caoa-stampa') return;
        this.selectStampa(e.target.value);
      });
      this._abilitaDeseleziona(root, 'caoa-stampa', () => this.deselezionaStampa());

      /* dimensione base (ratio-lock) */
      const inputB = document.getElementById('caoa-base');
      if (inputB) {
        inputB.addEventListener('input', () => {
          const base = parseFloat(inputB.value) || this.PRICING.DIM_MIN;
          const { altezza } = this._dimFromBase(base);
          this._applyDim(base, altezza);
        });
      }
      /* preset di base, come negli altri configuratori (l'altezza segue il ratio) */
      root.addEventListener('click', e => {
        const btn = e.target.closest('.caoa-preset');
        if (!btn) return;
        const base = parseFloat(btn.dataset.base);
        const { altezza } = this._dimFromBase(base);
        this._applyDim(base, altezza);
      });

      /* fronte / retro (riclic = senza lastra / senza fondo — solo in cornice: negli
         altri modi setTipo riporta il default) */
      root.addEventListener('change', e => {
        if (e.target.name === 'caoa-fronte') this._setFronte(e.target.value);
        if (e.target.name === 'caoa-retro')  this._setRetro(e.target.value);
      });
      this._abilitaDeseleziona(root, 'caoa-fronte', () => {
        if (this.state.config.tipo !== 'cornice') { this.syncFronte(); return; }   // montatura/pannello: la lastra ci vuole
        this._setFronte(NESSUNO_SKU);
      });
      this._abilitaDeseleziona(root, 'caoa-retro', () => {
        if (this.state.config.tipo !== 'cornice') { this.syncRetro(); return; }    // idem il fondo
        this._setRetro(NESSUNO_SKU);
      });

      /* passepartout cartone (toggle) */
      root.addEventListener('click', e => {
        const item = e.target.closest('[data-pass-cartone]');
        if (!item) return;
        if (item.classList.contains('caoa-gallery-item--disabled')) return;
        const sku = item.dataset.passCartone;
        const isSel = this.state.config.hasPassCartone && this.state.config.passCartoneSku === sku;
        if (isSel) {
          this.state.config.hasPassCartone = false;
          this.state.config.passCartoneSku = null;
          this.state.config.passCartone    = null;
          this._passCartoneImg = null; this._passCartoneImgUrl = '';
        } else {
          const p = this.state.passCartone.find(x => x.sku === sku);
          this.state.config.hasPassCartone = true;
          this.state.config.passCartoneSku = sku;
          this.state.config.passCartone    = p || null;
          this._loadPassCartoneImg(p?.imgUrl || '');
          // Aggiorna min/max/value dell'input larghezza
          const pcWidth = document.getElementById('caoa-pass-cartone-largh');
          if (pcWidth && p) {
            const { base, altezza } = this.state.config;
            const minL = (p.larghezzaMinimaPass > 0) ? p.larghezzaMinimaPass : PASS_CARTONE_LARGH_MIN_CM;
            const maxL = Math.min(PASS_CARTONE_LARGH_MAX_CM,
              Math.floor(Math.min(p.lastraBase - base, p.lastraAlt - altezza) / 2 / 0.5) * 0.5);
            pcWidth.min   = minL;
            pcWidth.max   = maxL > minL ? maxL : minL;
            pcWidth.value = Math.min(maxL > minL ? maxL : minL, Math.max(minL, parseFloat(pcWidth.value) || minL));
            this.state.config.passCartoneLargh = parseFloat(pcWidth.value);
          }
        }
        this.renderPassCartoneGallery();
        this._updatePassCartoneUI();
        this._dispatchPassEvents();
        this.updateLiveView();
        this._applyRetroTavoloGate();  // il pass cambia il formato finito
        this.calculateLive();
      });

      /* larghezza bordo cartone */
      const pcWidth = document.getElementById('caoa-pass-cartone-largh');
      if (pcWidth) {
        pcWidth.addEventListener('input', () => {
          const minL = (this.state.config.passCartone?.larghezzaMinimaPass > 0)
            ? this.state.config.passCartone.larghezzaMinimaPass : PASS_CARTONE_LARGH_MIN_CM;
          const val = parseFloat(pcWidth.value);
          // tetto = max dell'input (lastra e PASS_CARTONE_LARGH_MAX_CM, messo alla selezione)
          const maxL = parseFloat(pcWidth.max) > 0 ? parseFloat(pcWidth.max) : PASS_CARTONE_LARGH_MAX_CM;
          this.state.config.passCartoneLargh = isNaN(val) ? minL : Math.min(maxL, Math.max(minL, val));
          this._dispatchPassEvents();
          this.updateLiveView();
          this._applyRetroTavoloGate();  // il pass cambia il formato finito
          this.calculateLive();
        });
      }

      /* passepartout legno (toggle) */
      root.addEventListener('click', e => {
        const item = e.target.closest('[data-pass-legno]');
        if (!item) return;
        const step = document.getElementById('caoa-step-pass-legno');
        if (step?.classList.contains('caoa-step--locked')) return;
        const sku = item.dataset.passLegno;
        const isSel = this.state.config.hasPassLegno && this.state.config.passLegnoSku === sku;
        if (isSel) {
          this.state.config.hasPassLegno = false;
          this.state.config.passLegnoSku = null;
          this.state.config.passLegno    = null;
          this._passLegnoImg = null; this._passLegnoImgUrl = '';
          this._passLegnoPatternImg = null; this._passLegnoPatternUrl = '';
        } else {
          const p = this.state.passLegno.find(x => x.sku === sku);
          this.state.config.hasPassLegno = true;
          this.state.config.passLegnoSku = sku;
          this.state.config.passLegno    = p || null;
          this._loadPassLegnoImg(p?.imgUrl || '');
          this._loadPassLegnoPatternImg(p?.patternUrl || '');
        }
        this.renderPassLegnoGallery();
        this._dispatchPassEvents();
        this.updateLiveView();
        this._applyRetroTavoloGate();  // il pass cambia il formato finito
        this.calculateLive();
      });

      /* accessori stepper (delegated) */
      root.addEventListener('input', e => {
        const sku = e.target.dataset.attaccSku;
        if (!sku || e.target.type !== 'number') return;
        const v = parseInt(e.target.value, 10);
        this.state.config.attaccaglieQtys[sku] = isNaN(v) || v < 0 ? 0 : v;
        this.state.config.attTouched = true;   // stop al ricalcolo automatico
        this._updateAccessoriStep();
        this.calculateLive();
      });
      root.addEventListener('click', e => {
        const btn = e.target.closest('[data-attacc-delta]');
        if (!btn) return;
        const sku   = btn.dataset.attaccSku;
        const delta = parseInt(btn.dataset.attaccDelta, 10);
        if (!sku || isNaN(delta)) return;
        const qtys = this.state.config.attaccaglieQtys;
        qtys[sku] = Math.max(0, (qtys[sku] || 0) + delta);
        this.state.config.attTouched = true;   // stop al ricalcolo automatico
        this._updateAccessoriStep();
        this.calculateLive();
      });

      /* quantità */
      const qtyInput = document.getElementById('caoa-quantita');
      const qtyMinus = document.getElementById('caoa-qty-minus');
      const qtyPlus  = document.getElementById('caoa-qty-plus');
      if (qtyMinus) qtyMinus.addEventListener('click', () => this.changeQty(-1));
      if (qtyPlus)  qtyPlus.addEventListener('click',  () => this.changeQty(+1));
      if (qtyInput) qtyInput.addEventListener('change', () => this.onQtyChange());

      /* CTA */
      const ctaBtn = document.getElementById('caoa-cta');
      if (ctaBtn) ctaBtn.addEventListener('click', () => {
        if (ctaBtn.dataset.ctaMode === 'cart') this._addToCart();
        else this.openModal();
      });

      /* modal */
      document.getElementById('caoa-modal-close')?.addEventListener('click',  () => this._closeModal());
      document.getElementById('caoa-modal-cancel')?.addEventListener('click', () => this._closeModal());
      document.getElementById('caoa-modal-overlay')?.addEventListener('click', e => {
        if (e.target.id === 'caoa-modal-overlay') this._closeModal();
      });
      const form = document.getElementById('caoa-modal-form');
      if (form) {
        form.addEventListener('submit', () => {
          sessionStorage.setItem('caoa_success', '1');
          const noteEl = document.getElementById('caoa-modal-note');
          const bodyEl = document.getElementById('caoa-modal-body');
          if (noteEl && bodyEl && noteEl.value.trim()) {
            bodyEl.value += '\n\nNote aggiuntive:\n' + noteEl.value.trim();
          }
          if (typeof window.fotorecordLeadConversion === 'function') {
            window.fotorecordLeadConversion('opere_autore');
          }
        });
      }
    }
  }

  /* ══════════════════════════════════════════════════════════════════════════
     PASSEPARTOUT MULTIFOTO (casm, modalita' della stessa sezione) — ADR
     docs/decisioni/2026-10-09-il-passepartout-multifoto-e-una-configurazione-con-un-progetto.md
     ----------------------------------------------------------------------------
     Una cornice normale (asta, legno, fronte, retro, attaccaglie: tutto ereditato dal
     configuratore aste) con UN passepartout in cartone a piu' finestre. Al posto delle
     misure dell'opera il cliente sceglie un PROGETTO (window.PASSEPARTOUT_MULTIFOTO,
     asset generato dai file FRPM sul NAS) e puo' girarlo di un quarto; l'«opera» della
     cornice e' il passepartout intero, quindi base/altezza = misura del passepartout e
     la fascia del cartone ad anello resta 0 (le fasce di 3 cm sono del progetto).
     Prezzo: FR.corniceNetto senza cartone + FR.passepartoutMultifotoNetto (cartone
     sull'area intera, lavorazione per finestra), come passepartout_multifoto.prezzo.
     Una foto per finestra: ognuna entra nel carrello come «Stampa fotografica» del suo
     formato, legata alla cornice da `_bundle`, come le cornici multiple FRCM.
     ══════════════════════════════════════════════════════════════════════════ */
  const _cmIt = mm => (Math.round(mm) / 10).toLocaleString('it-IT');

  class ConfiguratoreMultifoto extends ConfiguratoreAste {
    constructor() {
      super();
      this.state.progetti   = [];
      this.state.filtroFoto = 0;
      // stampaFoto: il cliente fa stampare le foto (una riga «Stampa fotografica» per
      // finestra) o le carica solo per vedere l'effetto. Si parte da «solo la cornice»,
      // come sulle cornici multiple (Andrea, 10/10/2026).
      Object.assign(this.state.config, { progettoId: null, rotazione: 0, passCartoneLargh: 0, stampaFoto: false });
      this._foto        = {};      // per finestra ORIGINALE (non girata): { file, img }
      this._fotoTarget  = null;
      this._finestrePx  = [];
    }

    init() {
      const data = window.SHOPIFY_ASTE_SU_MISURA;
      if (!data) { console.error('[CASM-PM] Dati non disponibili'); return; }
      this.state.progetti = Array.isArray(window.PASSEPARTOUT_MULTIFOTO) ? window.PASSEPARTOUT_MULTIFOTO : [];
      this.__bgColor = 'transparent';
      this.loadData(data);
      this.renderFilters();
      this.renderGallery();
      this.setupEventListeners();
      this.syncFronte();
      this.syncRetro();
      this.renderPassLegnoGallery();
      this._updatePassLegnoState();
      this.setStampaFoto(false);
      const p = new URLSearchParams(window.location.search);
      const richiesto = this.state.progetti.find(x => x.id === (p.get('progetto') || '').toLowerCase());
      const primo = richiesto || this.state.progetti[0];
      if (primo) this.selectProgetto(primo.id, parseInt(p.get('rot'), 10) || 0);
      this._selezionaCartone(p.get('passC'));
      /* deep-link da un kit multifoto (snippets/kit-configura-deeplink): fronte e retro del
         kit; il parametro VUOTO e' la scelta «Nessuno», come nel configuratore aste */
      [['fronte', this.state.fronti, s => this._setFronte(s), () => this.syncFronte()],
       ['retro', this.state.retri, s => this._setRetro(s), () => this.syncRetro()]].forEach(([k, lista, set, sync]) => {
        const v = p.get(k);
        if (v === null) return;
        const item = v === '' ? null : this._findBySku(lista, v);
        if (v === '' || item) { set(item ? item.sku : NESSUNO_SKU); sync(); }
      });
      this.renderAttaccaglieStep();
      const profilo = (p.get('sku') && this._findBySku(this.state.profili, p.get('sku'))) || this._primoProfiloUtile();
      if (profilo) this.selectProfilo(profilo.sku);
      this._mountMobileBar();
    }

    /* ── PROGETTO ── */
    _progetto() {
      return this.state.progetti.find(x => x.id === this.state.config.progettoId) || null;
    }

    /* Il progetto girato di `rotazione` gradi in senso orario, in mm. Stessa formula di
       passepartout_multifoto.ruota e stesso ordine delle finestre (dall'alto, da sinistra):
       la «Posizione» che va in produzione e' quella che vede il cliente. `orig` e' la
       finestra nel progetto non girato: una foto caricata gira con la sua finestra. */
    _geometria() {
      const pr = this._progetto();
      if (!pr) return null;
      let B = pr.esterno[0], H = pr.esterno[1];
      let fin = pr.finestre.map((f, i) => ({ x: f[0], y: f[1], b: f[2], h: f[3], formato: f[4], orig: i }));
      const giri = Math.round((this.state.config.rotazione || 0) / 90) % 4;
      for (let k = 0; k < giri; k++) {
        fin = fin.map(f => ({ x: H - f.y - f.h, y: f.x, b: f.h, h: f.b, formato: f.formato, orig: f.orig }));
        [B, H] = [H, B];
      }
      fin.sort((a, c) => (a.y - c.y) || (a.x - c.x));
      return { B, H, finestre: fin };
    }

    _verso(f) { return f.b === f.h ? 'quadrata' : (f.h > f.b ? 'verticale' : 'orizzontale'); }

    selectProgetto(id, rotazione) {
      const cambia = id !== this.state.config.progettoId;
      this.state.config.progettoId = id;
      this.state.config.rotazione  = (((rotazione || 0) % 360) + 360) % 360;
      if (cambia) { this._foto = {}; this._rifiuti = {}; }
      const g = this._geometria();
      if (!g) return;
      this.state.config.base    = g.B / 10;
      this.state.config.altezza = g.H / 10;
      this.updateLiveView();
      this.updateRetroAvailability();
      this.syncRetro();
      this._updatePassLegnoState();
      this._maybeInitAttaccaglie();
      this.renderAttaccaglieStep();
      this.renderProgetti();
      this.renderFoto();
      this.calculateLive();
    }

    ruota() {
      if (!this.state.config.progettoId) return;
      this.selectProgetto(this.state.config.progettoId, this.state.config.rotazione + 90);
    }

    _miniatura(pr) {
      const [B, H] = pr.esterno;
      const rect = pr.finestre.map(f => `<rect x="${f[0]}" y="${f[1]}" width="${f[2]}" height="${f[3]}" fill="#b29190"/>`).join('');
      return `<svg viewBox="0 0 ${B} ${H}" role="img" aria-hidden="true" style="max-width:100%;max-height:100%;">`
           + `<rect width="${B}" height="${H}" fill="#efede8" stroke="#9c9a92" stroke-width="2"/>${rect}</svg>`;
    }

    renderProgetti() {
      const pills = document.getElementById('casm-pm-filtro-foto');
      const grid  = document.getElementById('casm-pm-progetti');
      if (!grid) return;
      const conteggi = [...new Set(this.state.progetti.map(p => p.finestre.length))].sort((a, b) => a - b);
      if (pills) {
        pills.innerHTML = [0, ...conteggi].map(n =>
          `<button type="button" class="casm-pill${n === this.state.filtroFoto ? ' active' : ''}" data-pm-foto="${n}">${n ? n + ' foto' : 'Tutti'}</button>`
        ).join('');
      }
      const lista = this.state.progetti.filter(p => !this.state.filtroFoto || p.finestre.length === this.state.filtroFoto);
      grid.innerHTML = lista.map(p => {
        const sel = p.id === this.state.config.progettoId ? ' selected' : '';
        return `<div class="casm-gallery-item casm-pm-progetto${sel}" data-progetto="${this._esc(p.id)}"
                     title="${this._esc(p.codice)} · ${this._esc(p.disposizione)} · ${p.finestre.length} foto · ${_cmIt(p.esterno[0])} × ${_cmIt(p.esterno[1])} cm">
                  <div class="casm-pm-progetto-img">${this._miniatura(p)}</div>
                  <div class="casm-pm-progetto-nome">${this._esc(p.disposizione)} · ${p.finestre.length} foto</div>
                </div>`;
      }).join('');
      this._abilitaListbox(grid);
      const info = document.getElementById('casm-pm-info');
      const g = this._geometria(), pr = this._progetto();
      if (info) {
        info.textContent = (g && pr)
          ? `${pr.codice} · ${pr.disposizione} · passepartout ${_cmIt(g.B)} × ${_cmIt(g.H)} cm`
            + (this.state.config.rotazione ? ` · girato di ${this.state.config.rotazione}°` : '')
          : '';
      }
    }

    /* ── STAMPA: le foto le stampiamo noi, o servono a vedere l'effetto ── */
    setStampaFoto(si) {
      const c = this.state.config;
      c.stampaFoto = !!si;
      // il record «stampa fotografica» del configuratore: alimenta la scheda «Stampa» qui
      // sotto (material-detail, evento casm:stampa-changed). Il PREZZO no: ogni stampa e' la
      // sua riga del prodotto stampa-fotografica, e calculateLive passa stampa: null.
      // il record e' quello delle varianti del prodotto stampe (sku `frst0001-10x15` ->
      // `frst0001`), non quello scelto sulla misura del passepartout come per un'opera
      const sku = (Object.values(window.STAMPA_FOTO_VARIANTI || {})[0]?.sku || '').split('-')[0];
      c.stampaTipo = c.stampaFoto ? 'foto' : null;
      c.stampa     = c.stampaFoto ? (this._findBySku(this.state.stampe, sku) || this._resolveStampa('foto')) : null;
      this._dispatchStampaEvent();
      this.renderStampaScelta();
      this.renderFoto();
      this._renderStampe();
      this.calculateLive();
    }

    renderStampaScelta() {
      const el = document.getElementById('casm-pm-stampa-options');
      if (!el) return;
      const foto = this.state.stampe.find(s => s.tipo === 'foto');
      const opts = [
        { v: '', titolo: 'Solo la cornice', testo: 'Le foto che carichi servono a vedere come verrà: le stampe le porti tu.' },
        { v: 'foto', titolo: 'Stampate voi le mie foto', testo: 'Una stampa fotografica per finestra, nel suo formato: arriva già al suo posto.',
          img: foto?.immagineUrl || '' },
      ];
      el.innerHTML = opts.map(o => {
        const sel = (o.v === 'foto') === this.state.config.stampaFoto;
        return `<label class="casm-option${sel ? ' selected' : ''}">
          <input type="radio" name="casm-pm-stampa" value="${o.v}"${sel ? ' checked' : ''}>
          <span class="casm-option-card">
            ${o.img ? this._optionImg(o.img, true) : ''}
            <span class="casm-option-dot"></span>
            <span class="casm-option-text"><strong>${this._esc(o.titolo)}</strong><span>${this._esc(o.testo)}</span></span>
          </span>
        </label>`;
      }).join('');
      const hint = document.getElementById('casm-pm-foto-hint');
      if (hint) hint.textContent = this.state.config.stampaFoto
        ? 'Carica una foto per finestra: tocca la finestra nell\'anteprima o il suo box qui sotto.'
        : 'Puoi caricare le tue foto per vedere come verrà: tocca una finestra o il suo box qui sotto.';
    }

    /* ── FOTO, una per finestra: i box dei posti (assets/foto-posti.css), gli stessi delle
       cornici multiple e nello stesso posto, sotto l'anteprima (Andrea, 10/10/2026) ── */
    renderFoto() {
      const el = document.getElementById('casm-pm-foto');
      const g  = this._geometria();
      if (!el || !g) return;
      const varianti = window.STAMPA_FOTO_VARIANTI || {};
      const n = g.finestre.length;
      el.innerHTML = `<ul class="foto-posti__lista">${g.finestre.map((f, i) => {
        const foto = this._foto[f.orig];
        const v    = varianti[f.formato];
        const prezzo = (this.state.config.stampaFoto && v && v.prezzo > 0) ? `<br>stampa € ${this._fmt(v.prezzo / 100)}` : '';
        const verso = f.b === f.h ? '' : ` ${this._verso(f)}`;
        const rifiuto = (this._rifiuti || {})[f.orig] || '';
        return `<li><button type="button" class="foto-posti__posto${foto ? ' foto-posti__posto--piena' : ''}${rifiuto ? ' foto-posti__posto--rifiutata' : ''}" data-pm-finestra="${f.orig}"
                  aria-label="Foto ${i + 1} di ${n}, ${this._esc(f.formato)}${verso}${foto ? ': cambia la foto' : ''}">
                  <span class="foto-posti__miniatura" aria-hidden="true">+${foto ? `<img alt="" src="${foto.img.src}">` : ''}</span>
                  <span>Foto ${i + 1}<br>${this._esc(f.formato)}${verso}${prezzo}</span>
                </button>${rifiuto ? `<p class="foto-posti__avviso" role="alert">${this._esc(rifiuto)}</p>` : ''}</li>`;
      }).join('')}</ul>`;
    }

    _onFotoFile(file, orig) {
      const input = document.getElementById('casm-pm-foto-input');
      if (!file || orig == null) return;
      if (!/^image\/(jpeg|png)$/.test(file.type)) {
        this._artworkNotice('Carica le foto in formato JPG o PNG.', 'error');
        if (input) input.value = '';
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        this._artworkNotice('Una foto supera i 20 MB: inviala a info@fotorecord.it con i dati dell’ordine.', 'error');
        if (input) input.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onload = ev => {
        const img = new Image();
        img.onload = () => {
          if (input) input.value = '';
          const finestre = this._geometria()?.finestre || [];
          const f = finestre.find(x => x.orig === orig);
          // il verso, come sulle cornici multiple: una foto dell'altro verso si taglierebbe a meta'
          const vf = img.naturalHeight > img.naturalWidth * 1.05 ? 'verticale'
            : img.naturalWidth > img.naturalHeight * 1.05 ? 'orizzontale' : '';
          if (f && f.b !== f.h && vf && vf !== this._verso(f)) {
            // l'avviso sul posto stesso, sotto il suo box (Andrea, 12/10/2026), come sulle multiple
            this._rifiuti = { ...(this._rifiuti || {}), [orig]: `Questo posto vuole una foto ${this._verso(f)} (${this._verso(f) === 'verticale' ? 'più alta che larga' : 'più larga che alta'}): scegline una così.` };
            this.renderFoto();
            return;
          }
          if (this._rifiuti) delete this._rifiuti[orig];
          this._foto[orig] = { file, img };
          // 150 ppi sul lato lungo della foto stampata, come il caricamento delle multiple
          const lungo = f ? Math.max(f.b, f.h) + 2 * 10 * (this.PRICING.SORMONTO_CM || 0) : 0;  // la foto: finestra + sormonto
          const serve = Math.round((lungo / 25.4) * 150);
          this._artworkNotice(Math.max(img.naturalWidth, img.naturalHeight) < serve * 0.85
            ? 'Una foto ha una risoluzione bassa per il suo formato: la stampa potrebbe risultare poco nitida.'
            : '', Math.max(img.naturalWidth, img.naturalHeight) < serve * 0.85 ? 'warn' : 'clear');
          this.renderFoto();
          this.updateLiveView();
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    }

    _apriFoto(orig) {
      const input = document.getElementById('casm-pm-foto-input');
      if (!input) return;
      this._fotoTarget = orig;
      input.click();
    }

    /* Il tocco sul canvas apre il caricamento della finestra toccata (coordinate logiche
       400×400, vedi _fitCanvas/_canvasCtx). */
    _wireArtworkUpload() {
      const input  = document.getElementById('casm-pm-foto-input');
      const canvas = document.getElementById('casm-live-canvas');
      if (input) input.addEventListener('change', e => this._onFotoFile(e.target.files?.[0], this._fotoTarget));
      if (canvas) canvas.addEventListener('click', e => {
        const r = canvas.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width * CVS_W;
        const y = (e.clientY - r.top) / r.height * CVS_H;
        const hit = this._finestrePx.find(f => x >= f.x && x <= f.x + f.w && y >= f.y && y <= f.y + f.h);
        if (hit) this._apriFoto(hit.orig);
      });
    }

    _syncArtworkUI() {}

    /* ── CARTONE: obbligatorio, una tinta su tutto il passepartout ── */
    _selezionaCartone(sku) {
      const lista = this.state.passCartone;
      const item = (sku && this._findBySku(lista, sku))
        || lista.find(p => /bianc/i.test(`${p.colore} ${p.nome}`))
        || lista[0];
      if (!item) return;
      Object.assign(this.state.config, { hasPassCartone: true, passCartoneSku: item.sku, passCartone: item, passCartoneLargh: 0 });
      this._loadPassCartoneImg(item.imgUrl || '');
      this.renderPassCartoneGallery();
      this._dispatchPassEvents();
      this.calculateLive();
    }

    _cartoneBasta(p) {
      const g = this._geometria();
      if (!p || !g || !p.lastraBase || !p.lastraAlt) return true;
      const B = g.B / 10, H = g.H / 10;
      return (B <= p.lastraBase && H <= p.lastraAlt) || (B <= p.lastraAlt && H <= p.lastraBase);
    }

    _loadPassCartoneImg(url) {
      super._loadPassCartoneImg(url);
      this._pmTile = null; this._pmTileKey = '';
    }

    setupEventListeners() {
      super.setupEventListeners();
      const root = document.getElementById('configuratore-aste-su-misura');
      if (!root) return;
      // il cartone non si toglie: e' il passepartout. Il riclick sul selezionato si ferma qui,
      // prima del toggle del configuratore aste (che ascolta sulla radice).
      document.getElementById('casm-pass-cartone-gallery')?.addEventListener('click', e => {
        const item = e.target.closest('[data-pass-cartone]');
        if (item && this.state.config.hasPassCartone && item.dataset.passCartone === this.state.config.passCartoneSku) {
          e.stopPropagation();
        }
      });
      root.addEventListener('click', e => {
        const card = e.target.closest('[data-progetto]');
        if (card) { this.selectProgetto(card.dataset.progetto, 0); return; }
        const pill = e.target.closest('[data-pm-foto]');
        if (pill) { this.state.filtroFoto = parseInt(pill.dataset.pmFoto, 10) || 0; this.renderProgetti(); return; }
        if (e.target.closest('#casm-pm-ruota')) { this.ruota(); return; }
        const btn = e.target.closest('[data-pm-finestra]');
        if (btn) this._apriFoto(parseInt(btn.dataset.pmFinestra, 10));
      });
      root.addEventListener('change', e => {
        if (e.target.name === 'casm-pm-stampa') this.setStampaFoto(e.target.value === 'foto');
      });
    }

    /* ── DISEGNO: il cartone su tutta l'apertura, le finestre con le foto ── */
    _drawApertura(ctx, opX, opY, opW, opH, scale) {
      const g = this._geometria();
      if (!g) return super._drawApertura(ctx, opX, opY, opW, opH, scale);
      const k    = scale / 10;                                   // px per mm
      const sorm = (this.PRICING.SORMONTO_CM || 0) * 10;          // mm sotto la cornice
      if (this._passCartoneImg) {
        const lato = Math.max(8, Math.round(60 * k));
        const key  = `${this.state.config.passCartoneSku}_${lato}`;
        if (this._pmTileKey !== key) {
          const off = document.createElement('canvas');
          off.width = lato; off.height = lato;
          off.getContext('2d').drawImage(this._passCartoneImg, 0, 0, lato, lato);
          this._pmTile = off; this._pmTileKey = key;
        }
        const pattern = ctx.createPattern(this._pmTile, 'repeat');
        pattern.setTransform(new DOMMatrix().translate(opX, opY));
        ctx.fillStyle = pattern;
      } else {
        ctx.fillStyle = PASS_CARTONE_COLOR;
      }
      ctx.fillRect(opX, opY, opW, opH);
      this._finestrePx = [];
      g.finestre.forEach((f, i) => {
        const x = opX + (f.x - sorm) * k, y = opY + (f.y - sorm) * k;
        const w = f.b * k, h = f.h * k;
        const foto = this._foto[f.orig];
        // `_fotoPerFinestra` (per misura della finestra in mm) o `_fotoTutte` (la stessa in ogni
        // finestra): i render dei kit, motore headless
        const img  = foto?.img || this._fotoPerFinestra?.[`${f.b}x${f.h}`] || this._fotoTutte || null;
        ctx.fillStyle = img ? '#ffffff' : '#e9e5dd';
        ctx.fillRect(x, y, w, h);
        if (img) {
          this._drawImgCover(ctx, img, x, y, w, h);
        } else if (!this.__noNumeri) {
          ctx.fillStyle = '#9a948b';
          ctx.font = `600 ${Math.max(10, Math.min(18, Math.round(Math.min(w, h) / 4)))}px Helvetica,Arial,sans-serif`;
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.fillText(String(i + 1), x + w / 2, y + h / 2);
        }
        // il taglio a 45° del passepartout: una luce sul bordo della finestra
        ctx.strokeStyle = 'rgba(255,255,255,0.85)'; ctx.lineWidth = 1.5;
        ctx.strokeRect(x - 0.75, y - 0.75, w + 1.5, h + 1.5);
        ctx.strokeStyle = 'rgba(0,0,0,0.15)'; ctx.lineWidth = 1;
        ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
        this._finestrePx.push({ orig: f.orig, x, y, w, h });
      });
      this._drawFronteEffetto(ctx, opX, opY, opW, opH);
    }

    /* ── PREZZO ── */
    validateConfig() {
      const msgs = [];
      const g = this._geometria();
      if (!g) { msgs.push({ type: 'error', text: 'Scegli un progetto.' }); return msgs; }
      const { profilo, passCartone, hasPassCartone } = this.state.config;
      if (!profilo) msgs.push({ type: 'error', text: 'Seleziona un profilo dalla gallery.' });
      if (!hasPassCartone || !passCartone) msgs.push({ type: 'error', text: 'Scegli il colore del passepartout.' });
      else if (!this._cartoneBasta(passCartone)) msgs.push({ type: 'error', text: 'Questo passepartout non basta per il progetto scelto: scegline un altro.' });
      const vincolo = this._vincoloCrilex(this.state.config.base, this.state.config.altezza);
      if (vincolo) msgs.push({ type: 'error', text: vincolo });
      const varianti = window.STAMPA_FOTO_VARIANTI || {};
      const senza = [...new Set(g.finestre.filter(f => !(varianti[f.formato]?.id > 0)).map(f => f.formato))];
      if (senza.length && this.state.config.stampaFoto) msgs.push({ type: 'warning', text: `La stampa ${senza.join(', ')} non è ancora ordinabile online: scrivici e la prepariamo noi.` });
      return msgs;
    }

    calculateLive() {
      this.renderPassCartoneGallery();
      if (this._maybeInitAttaccaglie()) this.renderAttaccaglieStep();
      const validation = this.validateConfig();
      this._renderValidation(validation);
      const nessuno = () => {
        this._lastCalcResult = null; this._renderPrice(null); this._publishPrezziComponenti(null); this._renderStampe();
      };
      if (validation.some(v => v.type === 'error')) return nessuno();
      const { base, altezza, profilo, fronte, retro, hasPassLegno, passLegno, passCartone } = this.state.config;
      const g = this._geometria();
      const attItems = this.state.accessori.filter(x => (x.categoria || '').includes('attaccaglie'));
      const attaccaglie = attItems.map(a => ({ prezzo: a.prezzo, qty: (this.state.config.attaccaglieQtys || {})[a.sku] || 0 }));
      // il conto e' FR.passepartoutMultifotoTotale (configuratore-pricing.js), lo stesso della parita'
      const { fascia, mk, voci, pm, prezzoUnitario } = FR.passepartoutMultifotoTotale({
        base, altezza, profilo, fronte, retro, passCartone, attaccaglie,
        passLegno: (hasPassLegno && passLegno) ? passLegno : null, nFinestre: g.finestre.length, P: this.PRICING,
      });
      if (this._markupMancante(mk)) return nessuno();
      const tot            = prezzoUnitario * (this.state.config.quantita || 1);
      this._lastCalcResult = { tot, prezzoUnitario, fascia };
      this._renderPrice(tot, fascia);
      this._publishPrezziComponenti(fascia.soloPreventivo ? null : {
        profilo: voci.asta, fronte: voci.fronte, retro: voci.retro,
        passCartone: pm.cartone + pm.lavorazione, passLegno: voci.passLegno, stampa: 0, accessori: voci.accessori,
      });
      this._renderStampe();
      const sezCm = (profilo?.larghezzaMm || 0) / 10, sorm = this.PRICING.SORMONTO_CM || 0;
      const dimText = sezCm > 0
        ? `${this._round(voci.batt.b - 2 * sorm + 2 * sezCm, 1)} × ${this._round(voci.batt.h - 2 * sorm + 2 * sezCm, 1)} cm` : '';
      document.querySelectorAll('.casm-price-dims').forEach(el => { el.textContent = dimText; el.style.display = dimText ? '' : 'none'; });
    }

    /* Le stampe sono righe a parte del carrello (il loro prezzo e' quello della variante):
       qui si dice quanto aggiungono, cosi' il totale non sorprende al carrello. */
    _renderStampe() {
      const el = document.getElementById('casm-pm-stampe');
      const g  = this._geometria();
      if (!el) return;
      const varianti = window.STAMPA_FOTO_VARIANTI || {};
      const pezzi = this.state.config.quantita || 1;
      const prezzi = (g?.finestre || []).map(f => varianti[f.formato]?.prezzo || 0);
      if (!this.state.config.stampaFoto || !prezzi.length || prezzi.some(p => !(p > 0))) { el.style.display = 'none'; return; }
      const totale = prezzi.reduce((s, p) => s + p, 0) / 100 * pezzi;
      el.textContent = `Più le stampe delle tue foto: ${prezzi.length * pezzi} stampe, € ${this._fmt(totale)}`;
      el.style.display = '';
    }

    /* ── RIEPILOGO E CARRELLO ── */
    _descrizioneProgetto() {
      const pr = this._progetto(), g = this._geometria();
      if (!pr || !g) return '';
      const rot = this.state.config.rotazione;
      return `${pr.codice} ${pr.disposizione}, ${g.finestre.length} foto${rot ? `, girato di ${rot}°` : ''}`;
    }

    _buildRecap() {
      const el = document.getElementById('casm-modal-recap-rows');
      if (!el) return;
      const { base, altezza, profilo, fronte, retro, passCartone, hasPassLegno, passLegno } = this.state.config;
      const rows = [
        ['Progetto', this._esc(this._descrizioneProgetto()) || '—'],
        ['Passepartout', base && altezza ? `${base} × ${altezza} cm` : '—'],
        ['Profilo asta', nb(profilo) || '—'],
        ['Fronte', nb(fronte) || 'Nessuno'],
        ['Retro', nb(retro) || 'Nessuno'],
        ['Passepartout in cartone', nb(passCartone) || '—'],
      ];
      if (hasPassLegno) rows.push(['Passepartout in legno', nb(passLegno) || '—']);
      el.innerHTML = rows.map(([l, v]) => `<div class="casm-modal-recap-row"><span>${l}</span><span>${v}</span></div>`).join('');
      const priceEl = document.getElementById('casm-modal-price-value');
      const srcEl   = document.querySelector('.casm-price-amount');
      if (priceEl && srcEl) priceEl.textContent = srcEl.textContent;
    }

    _buildModalText() {
      const ta = document.getElementById('casm-modal-body');
      if (!ta) return;
      const { base, altezza, profilo, fronte, retro, passCartone, hasPassLegno, passLegno } = this.state.config;
      const lines = [
        'Configurazione Passepartout multifoto',
        `Progetto: ${this._descrizioneProgetto()}`,
        `Passepartout: ${base} × ${altezza} cm, ${nb(passCartone) || '—'}`,
        `Profilo asta: ${nb(profilo) || '—'}`,
        `Fronte: ${nb(fronte) || 'Nessuno'}`,
        `Retro: ${nb(retro) || 'Nessuno'}`,
      ];
      if (hasPassLegno) lines.push(`Passepartout in legno: ${nb(passLegno) || '—'}`);
      const srcEl = document.querySelector('.casm-price-amount');
      lines.push(`Stima prezzo della cornice: ${srcEl?.textContent || '—'}`);
      ta.value = lines.join('\n');
    }

    _buildCartProperties() {
      const props = super._buildCartProperties();
      const pr = this._progetto();
      const { base, altezza, passCartone } = this.state.config;
      // la cornice su misura qui e' «passepartout multifoto»: il progetto sostituisce la fascia
      delete props['Passepartout in cartone'];
      delete props['_pp_cartone_largh_cm'];
      // la stampa NON e' della cornice: ogni foto e' la sua riga «Stampa fotografica». Sulla
      // riga della cornice resterebbe una stampa dell'opera, che il foglio produrrebbe.
      delete props['Stampa'];
      delete props['_stampa_tipo'];
      delete props['_stampa_sku'];
      return Object.assign({
        'Tipologia':               'Passepartout multifoto',
        'Dimensioni':              `${base} × ${altezza} cm`,
        'Progetto':                this._descrizioneProgetto(),
      }, props, {
        'Tipologia':               'Passepartout multifoto',
        'Dimensioni':              `${base} × ${altezza} cm`,
        'Passepartout in cartone': nb(passCartone),
        'Stampe delle foto':       this.state.config.stampaFoto ? 'una per finestra, nelle righe Stampa fotografica' : 'le porti tu',
        '_pp_cartone_sku':         passCartone?.sku || '',
        '_progetto':               pr?.id || '',
        '_rotazione':              String(this.state.config.rotazione || 0),
      });
    }

    async _postStampa(item) {
      const fd = new FormData();
      fd.append('id', String(item.id));
      fd.append('quantity', String(item.quantity));
      Object.entries(item.properties).forEach(([k, v]) => {
        if (v instanceof Blob) fd.append(`properties[${k}]`, v, v.name || 'foto.jpg');
        else fd.append(`properties[${k}]`, String(v));
      });
      const res = await fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: fd });
      if (!res.ok) throw new Error(await res.text());
    }

    async _addToCart() {
      const bucket = this._bucket || this._pickBucket(this._lastCalcResult?.prezzoUnitario);
      const g = this._geometria(), pr = this._progetto();
      if (!bucket || !(bucket.id > 0) || !g || !pr) {
        console.error('[CASM-PM] add to cart: configurazione incompleta');
        return;
      }
      const stampa = this.state.config.stampaFoto;
      const mancanti = stampa ? g.finestre.map((f, i) => (this._foto[f.orig]?.file ? null : i + 1)).filter(Boolean) : [];
      if (mancanti.length) {
        this._artworkNotice(`Carica la foto per ${mancanti.length === 1 ? 'la finestra' : 'le finestre'} ${mancanti.join(', ')}: ogni finestra ha la sua stampa.`, 'error');
        document.getElementById('casm-pm-foto')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      const varianti = window.STAMPA_FOTO_VARIANTI || {};
      if (stampa && g.finestre.some(f => !(varianti[f.formato]?.id > 0))) {
        this._artworkNotice('Una delle stampe di questo progetto non è ancora ordinabile online: scrivici e la prepariamo noi.', 'error');
        return;
      }
      const btn = document.getElementById(this.ids.cta);
      if (btn) { btn.disabled = true; btn.textContent = this.ctaBusyText || 'Aggiungendo…'; }
      const pezzi  = this.state.config.quantita || 1;
      const bundle = 'pm-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      // il verso e il profilo: due cornici dello stesso progetto nello stesso ordine hanno
      // le finestre numerate diversamente, e lo staff deve sapere a quale va ogni stampa
      const perCornice = `${this._descrizioneProgetto()} · ${FR.core.pubblico(this._profiloCodice(this.state.config.profilo))}`;
      try {
        const props = this._buildCartProperties();
        props['_bundle'] = bundle;
        const anteprima = await this._capturePreviewBlob();
        if (anteprima) props['_anteprima'] = anteprima;
        // le foto caricate solo per vedere l'effetto restano nell'anteprima (_anteprima)
        for (let i = 0; stampa && i < g.finestre.length; i++) {
          const f = g.finestre[i], v = varianti[f.formato];
          await this._postStampa({ id: v.id, quantity: pezzi, properties: {
            'Foto da stampare': this._foto[f.orig].file,
            'Per la cornice':   perCornice,
            'Posizione':        `foto ${i + 1} di ${g.finestre.length}`,
            '_bundle':          bundle,
            '_stampa_tipo':     'foto',
            '_stampa_sku':      v.sku || '',
          } });
        }
        await FR.core.addToCart({ items: [{ id: bucket.id, quantity: pezzi, properties: props }] });
      } catch (e) {
        console.error('[CASM-PM] add to cart error', e);
        this._artworkNotice('Non siamo riusciti ad aggiungere tutto al carrello: controlla il carrello e riprova.', 'error');
        if (btn) { btn.disabled = false; btn.textContent = 'Aggiungi al carrello'; }
      }
    }
  }

  /* ══════════════════════════════════════════════════════════════════════════
     CORNICI CON ASTE A L — per le tele su telaio (ADR 2026-10-12)
     La stessa section in modalita' `aste_a_l` (data-modalita). Cambia il pezzo: la
     misura e' la TELA del cliente, niente fronte/retro/passepartout/stampa; in piu'
     l'aria (la fessura tra tela e profilo) e il kit di montaggio. Prezzo:
     FR.astaLNetto (specchio di price_engine.calcola_asta_l). Disegno: il ramo
     floater — la faccia intera del profilo, il canale in ombra, la tela sospesa.
     ══════════════════════════════════════════════════════════════════════════ */
  const ASTA_L_DEFAULT_BASE = 30, ASTA_L_DEFAULT_ALTEZZA = 40;

  class ConfiguratoreAsteL extends ConfiguratoreAste {
    constructor() {
      super();
      this.logTag = 'CASM-L';
      // ariaMm: la fessura scelta (mm); kit: con il kit di montaggio (asta preforata,
      // viti, spessori). L'aria la decide il profilo (FR.arieAmmesseAstaL): si parte
      // dalla «tela sospesa» (10 mm, Andrea 13/10), dove ammessa.
      Object.assign(this.state.config, { ariaMm: null, kit: false });
    }

    init() {
      const data = window.SHOPIFY_ASTE_SU_MISURA;
      if (!data) { console.error('[CASM-L] Dati non disponibili'); return; }
      this.__bgColor = 'transparent';
      this.loadData(data);
      this.renderFilters();
      this.renderGallery();
      this.setupEventListeners();
      this._setupListenersAstaL();
      this.renderAttaccaglieStep();
      this._renderLimitiTela();
      this._checkSuccessSession('casm_success');
      this.setDefaultDim(ASTA_L_DEFAULT_BASE, ASTA_L_DEFAULT_ALTEZZA);
      const locked = this._applyLockedProfile(data);
      const primo = locked ? null : this._primoProfiloUtile();
      if (primo) this.selectProfilo(primo.sku);
      this._applyUrlParams();
      const p = new URLSearchParams(window.location.search);
      if (p.get('aria')) this.setAria(parseFloat(p.get('aria')));
      if (p.get('kit') === '1') this.setKit(true);
      this.renderAriaOptions();
      this.renderKitOptions();
      this._mountMobileBar();
    }

    /* ── DATI: solo i profili `aste_a_l`, con le quote della L ── */
    loadData(data) {
      const full = Array.isArray(window.ASTE_CORNICI_FULL) ? window.ASTE_CORNICI_FULL : [];
      this.state.profili = full
        .filter(r => (r.configuratore || '').includes('aste_a_l'))
        .map(r => ({
          sku:            r.sku        || '',
          nome:           r.nome       || '',
          colore:         r.colore     || '',
          finitura:       r.finitura   || '',
          stile:          r.stile      || '',
          larghezzaMm:    r.larghezza_sezione_mm || 0,   // la base dell'asta (faccia intera)
          altezzaSezione: r.altezza_sezione_mm   || 0,   // altezza esterna
          battenteMm:     r.altezza_battente_mm  || 0,   // profondita' utile (portata)
          pareteMm:       r.parete_mm            || 0,   // la fascia a vista sul fronte
          larghezzaBattenteMm: r.larghezza_battente_mm || 0,  // il fondo del canale
          pianoGiocoMm:   r.piano_gioco_mm       || 0,   // il piano dello scalino (591)
          ariaImpostaMm:  r.aria_imposta_mm      || 0,   // aria data dal profilo
          costoMl:        r.costo_fornitore_1    || 0,
          markup:         r.markup               || 0,
          markupGrande:   r.markup_grande        || 0,
          imgUrl:         r.immagine_url         || '',
          patternUrl:     r.pattern_url          || '',
          sezioneUrl:     r.sezione_profilo_url  || '',
          fotoCampioneUrl: r.foto_campione_url   || '',
          fotoAngoloUrl:   r.foto_angolo_url     || '',
          fotoFallback:    r.foto_fallback === true,
          disponibile:    r.disponibile !== false,
        }));
      this.state.fronti = []; this.state.retri = [];
      this.state.passLegno = []; this.state.passCartone = []; this.state.stampe = [];
      this.state.accessori = data.accessori || [];
      this.state.config.fronteSku = NESSUNO_SKU; this.state.config.fronte = null;
      this.state.config.retroSku  = NESSUNO_SKU; this.state.config.retro  = null;
      window._CASM_FRONTE = null; window._CASM_RETRO = null;
    }

    renderFilters() {
      this._renderColoreFilter();
      this._renderFinituraFilter();
    }

    /* ── ARIA ── */
    _arieAmmesse(profilo) {
      return FR.arieAmmesseAstaL(profilo || this.state.config.profilo, this.PRICING);
    }
    _ariaDefault(arie) {
      const pref = this.PRICING.ARIA_ASTA_L_B_MM;
      return arie.includes(pref) ? pref : arie[0];
    }
    selectProfilo(sku) {
      const p = this.state.profili.find(x => x.sku === sku);
      if (p) {
        const arie = this._arieAmmesse(p);
        if (!arie.includes(this.state.config.ariaMm)) this.state.config.ariaMm = this._ariaDefault(arie);
      }
      super.selectProfilo(sku);
      this.renderAriaOptions();
      this.renderKitOptions();
      this._renderLimitiTela();
    }
    setAria(mm) {
      const arie = this._arieAmmesse();
      if (!arie.includes(mm)) return;
      this.state.config.ariaMm = mm;
      this.renderAriaOptions();
      this._maybeInitAttaccaglie();
      this.renderAttaccaglieStep();
      this.updateLiveView();
      this.calculateLive();
    }
    setKit(on) {
      this.state.config.kit = !!on;
      this.renderKitOptions();
      this.calculateLive();
    }
    _descrizioneAria(mm, profilo) {
      const P = this.PRICING;
      if ((profilo?.ariaImpostaMm || 0) > 0) return 'data dal gradino del profilo: il telaio batte contro lo scalino';
      if (mm <= P.ARIA_ASTA_L_A_MM) return 'tela a filo del profilo, fessura sottile';
      if (mm >= P.ARIA_ASTA_L_LARGA_MM) return 'fessura larga: la tela resta ben staccata';
      return 'tela sospesa: si vede il fondo del canale';
    }
    _opzioneHtml(name, value, checked, titolo, sotto) {
      const p = this.p;
      return `<label class="${p}-option${checked ? ' selected' : ''}">
        <input type="radio" name="${p}-${name}" value="${this._esc(value)}"${checked ? ' checked' : ''}>
        <span class="${p}-option-card">
          <span class="${p}-option-dot"></span>
          <span class="${p}-option-text">
            <strong>${this._esc(titolo)}</strong>
            <span>${this._esc(sotto)}</span>
          </span>
        </span>
      </label>`;
    }
    renderAriaOptions() {
      const el = document.getElementById('casm-aria-options');
      if (!el) return;
      const profilo = this.state.config.profilo;
      if (!profilo) { el.innerHTML = `<p class="${this.p}-hint">Scegli prima il profilo.</p>`; return; }
      const arie = this._arieAmmesse(profilo);
      // aria data dal gradino del profilo (591): niente scelta, una frase sola (ADR 12/10, punto 3)
      const imposta = (profilo.ariaImpostaMm || 0) > 0;
      const desc = document.getElementById('casm-aria-desc');
      if (desc) desc.style.display = imposta ? 'none' : '';
      if (imposta) {
        el.innerHTML = `<p class="${this.p}-step-desc">Aria di ${this._esc(String(arie[0]))} mm, data dal gradino del profilo: il telaio batte contro lo scalino.</p>`;
        return;
      }
      el.innerHTML = arie.map(a => this._opzioneHtml('aria', a, a === this.state.config.ariaMm,
        `${a} mm`, this._descrizioneAria(a, profilo))).join('');
    }
    renderKitOptions() {
      const el = document.getElementById('casm-kit-options');
      if (!el) return;
      const P = this.PRICING;
      const kit = !!this.state.config.kit;
      el.innerHTML =
        this._opzioneHtml('kit', '0', !kit, 'Sola cornice', 'Assemblata e rifinita, con le attaccaglie. Le viti le metti tu.') +
        this._opzioneHtml('kit', '1', kit, 'Con kit di montaggio',
          `Asta già forata, viti della misura giusta e quattro spessori per centrare la tela · + € ${this._fmt(P.KIT_MONTAGGIO_ASTA_L_IVA)}`);
    }
    _renderLimitiTela() {
      const el = document.getElementById('casm-al-limiti');
      if (!el) return;
      const P = this.PRICING, profilo = this.state.config.profilo;
      const portata = profilo?.battenteMm ? ` Con questo profilo il canale è profondo ${profilo.battenteMm} mm: un telaio fino a ${Math.max(0, profilo.battenteMm - 2 * (P.TELA_SPESSORE_MM || 1))} mm resta dentro, uno più alto sporge in avanti.` : '';
      el.textContent = `Tele da ${P.TELA_ASTA_L_LATO_MIN_CM} cm di lato fino a ${P.TELA_ASTA_L_LATO_MAX_CM} cm sul lato lungo. Oltre, richiedi un preventivo gratuito.${portata}`;
    }
    _setupListenersAstaL() {
      const root = document.getElementById('configuratore-aste-su-misura');
      if (!root) return;
      root.addEventListener('change', e => {
        if (e.target.name === 'casm-aria') this.setAria(parseFloat(e.target.value));
        if (e.target.name === 'casm-kit')  this.setKit(e.target.value === '1');
      });
    }

    /* ── ATTACCAGLIE: sul canale (il pezzo fisico), come price_engine ── */
    _nAttaccaglie() {
      const { base, altezza, profilo, ariaMm } = this.state.config;
      const d = FR.dimsAstaL(base || 0, altezza || 0, ariaMm || 0, profilo?.pareteMm || 0);
      return FR.nAttaccaglie(d.internoB, d.internoH, 0, 0, this.PRICING);
    }

    /* ── VALIDAZIONE: i limiti della linea (price_logic → PRICING_SSOT) ── */
    validateConfig() {
      const msgs = [];
      const { base, altezza, profilo } = this.state.config;
      const P = this.PRICING;
      if (!base || !altezza) {
        msgs.push({ type: 'error', text: 'Inserisci la misura della tela (base e altezza).' });
        return msgs;
      }
      if (!FR.telaAstaLAmmessa(base, altezza, P)) {
        msgs.push({ type: 'error', text: `Le cornici con asta a L si fanno per tele da ${P.TELA_ASTA_L_LATO_MIN_CM} cm di lato fino a ${P.TELA_ASTA_L_LATO_MAX_CM} cm sul lato lungo. Per una tela più grande richiedi un preventivo gratuito.` });
      }
      if (!profilo) msgs.push({ type: 'error', text: 'Scegli un profilo dalla galleria.' });
      return msgs;
    }

    /* ── PREZZO: FR.astaLNetto, la stessa formula del banco ── */
    calculateLive() {
      if (this._maybeInitAttaccaglie()) this.renderAttaccaglieStep();
      const validation = this.validateConfig();
      this._renderValidation(validation);
      if (validation.some(v => v.type === 'error')) { this._lastCalcResult = null; this._renderPrice(null); this._publishPrezziComponenti(null); return; }

      const { base, altezza, profilo, kit } = this.state.config;
      const arie = this._arieAmmesse(profilo);
      if (!arie.includes(this.state.config.ariaMm)) { this.state.config.ariaMm = this._ariaDefault(arie); this.renderAriaOptions(); }
      const ariaMm = this.state.config.ariaMm;

      const fascia   = this._getFascia(base, altezza);
      const isGrande = fascia.isGrande;
      const mk       = this._makeMk(isGrande);
      const attItems = this.state.accessori.filter(x => (x.categoria || '').includes('attaccaglie'));
      const attaccaglie = attItems.map(a => ({ prezzo: a.prezzo, qty: (this.state.config.attaccaglieQtys || {})[a.sku] || 0 }));
      const voci = FR.astaLNetto({ base, altezza, ariaMm, profilo, attaccaglie, kit, mk, P: this.PRICING });
      if (this._markupMancante(mk)) { this._lastCalcResult = null; this._renderPrice(null); this._publishPrezziComponenti(null); return; }

      const imponibile     = voci.netto + FR.imballoNetto(fascia);
      const prezzoUnitario = imponibile * (1 + this.PRICING.IVA);
      const quantita       = this.state.config.quantita || 1;
      const tot            = prezzoUnitario * quantita;
      this._lastCalcResult = { tot, prezzoUnitario, fascia };
      this._renderPrice(tot, fascia);
      this._publishPrezziComponenti(fascia.soloPreventivo ? null : {
        profilo: voci.asta, fronte: null, retro: null, passCartone: null, passLegno: null,
        stampa: null, accessori: voci.accessori,
      });

      const d = voci.d;
      const dimText = `${this._round(d.esternoB, 1)} × ${this._round(d.esternoH, 1)} cm`;
      const dimEstEl = document.getElementById('casm-dim-esterne');
      if (dimEstEl) { dimEstEl.textContent = `Dimensioni esterne: ${dimText}`; dimEstEl.style.display = ''; }
      document.querySelectorAll('.casm-price-dims').forEach(el => { el.textContent = dimText; el.style.display = ''; });
    }

    /* ── DISEGNO: il ramo floater ──
       La faccia intera del profilo (parete + fondo del canale) e' il 9-slice del master
       20×20 come per le aste; dentro la parete il canale resta legno, in ombra; la tela
       e' sospesa nel canale a `ariaMm` dal profilo, incassata di quanto il telaio e' piu'
       basso della portata (TELAIO_NOSTRA_TELA_MM, TELA_SPESSORE_MM). Niente sormonto:
       esterno = tela + 2×aria + 2×parete (FR.dimsAstaL). Taratura delle ombre sulle foto
       di riferimento del 12/10 (dossier §8). */
    _drawPreview(canvas) {
      const { ctx, W, H } = this._canvasCtx(canvas);
      const { base, altezza, profilo } = this.state.config;
      const P = this.PRICING;
      const aria       = this.state.config.ariaMm || P.ARIA_ASTA_L_B_MM;
      const faccia     = profilo?.larghezzaMm   || 35;
      const parete     = profilo?.pareteMm      || 10;
      const hParete    = profilo?.altezzaSezione || 40;
      const battente   = profilo?.battenteMm    || 30;
      const hFondoTela = Math.max(hParete - battente, 1);
      const hFondoGioco = profilo?.pianoGiocoMm || hFondoTela;
      const telaio = P.TELAIO_NOSTRA_TELA_MM || 15, telaMm = P.TELA_SPESSORE_MM || 1;

      const d     = FR.dimsAstaL(base, altezza, aria, parete);
      const scale = Math.min((W - CVS_PAD * 2) / d.esternoB, (H - CVS_PAD * 2) / d.esternoH);
      const dw = d.esternoB * scale, dh = d.esternoH * scale;
      const fx = (W - dw) / 2, fy = (H - dh) / 2;
      const facciaPx = faccia / 10 * scale, paretePx = parete / 10 * scale, ariaPx = aria / 10 * scale, mm = scale / 10;
      const quotaTela = hFondoTela + telaio + telaMm;
      const incasso   = hParete - quotaTela;
      const profGioco = hParete - hFondoGioco;
      const telaSopraFondo = quotaTela - hFondoGioco;

      ctx.clearRect(0, 0, W, H);
      if (this.__bgColor !== 'transparent') { ctx.fillStyle = this.__bgColor || '#f0ede8'; ctx.fillRect(0, 0, W, H); }
      if (!this.__noShadow) this._ombraCornice(ctx, fx, fy, dw, dh);
      ctx.fillStyle = '#ccc'; ctx.fillRect(fx, fy, dw, dh);

      /* LAYER 1: la faccia intera del profilo */
      if (this._frameImg && profilo) {
        const slicePx = this._frameImg.naturalWidth * (faccia / 200);
        this._draw9Slice(ctx, this._frameImg, slicePx, fx, fy, dw, dh, facciaPx, this._patternImg);
      } else {
        this._drawCorniceFallback(ctx, this._fallbackColor(profilo?.colore), fx, fy, dw, dh, facciaPx);
      }

      /* LAYER 2: il canale dentro la parete — fondo in ombra, luce da alto-sinistra */
      const cx = fx + paretePx, cy = fy + paretePx, cw = dw - 2 * paretePx, ch = dh - 2 * paretePx;
      const ratio = profGioco / Math.max(1, aria), buio = Math.min(0.38, 0.04 + 0.05 * ratio);
      ctx.save(); ctx.beginPath(); ctx.rect(cx, cy, cw, ch); ctx.clip();
      ctx.fillStyle = `rgba(0,0,0,${buio})`; ctx.fillRect(cx, cy, cw, ch);
      const L = Math.min(profGioco * mm * 0.6, ariaPx);
      let g = ctx.createLinearGradient(0, cy, 0, cy + L); g.addColorStop(0, 'rgba(0,0,0,0.6)'); g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g; ctx.fillRect(cx, cy, cw, L);
      g = ctx.createLinearGradient(cx, 0, cx + L, 0); g.addColorStop(0, 'rgba(0,0,0,0.55)'); g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g; ctx.fillRect(cx, cy, L, ch);
      if (incasso > 0) {
        const lit = Math.min(ariaPx * 0.6, Math.max(1.5, incasso * mm * 0.3));
        const col = 'rgba(255,255,255,0.5)';
        g = ctx.createLinearGradient(cx + cw, 0, cx + cw - lit, 0); g.addColorStop(0, col); g.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = g; ctx.fillRect(cx + cw - lit, cy + ariaPx, lit, ch - ariaPx);
        g = ctx.createLinearGradient(0, cy + ch, 0, cy + ch - lit); g.addColorStop(0, col); g.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = g; ctx.fillRect(cx + ariaPx, cy + ch - lit, cw - ariaPx, lit);
      }
      ctx.restore();
      ctx.strokeStyle = 'rgba(255,255,255,0.22)'; ctx.lineWidth = 1; ctx.strokeRect(cx + 0.5, cy + 0.5, cw - 1, ch - 1);

      /* LAYER 3: la tela, sospesa nel canale */
      const tx = cx + ariaPx, ty = cy + ariaPx, tw = cw - 2 * ariaPx, th = ch - 2 * ariaPx;
      if (this.__aperturaVuota) {
        ctx.clearRect(tx, ty, tw, th);
        if (this.__bgColor !== 'transparent') { ctx.fillStyle = this.__bgColor || '#f0ede8'; ctx.fillRect(tx, ty, tw, th); }
      } else {
        const sh = Math.max(1, telaSopraFondo * mm);
        ctx.save(); ctx.shadowColor = 'rgba(0,0,0,0.45)'; ctx.shadowBlur = sh * 0.8; ctx.shadowOffsetX = sh * 0.3; ctx.shadowOffsetY = sh * 0.45;
        ctx.fillStyle = '#f4f1ea'; ctx.fillRect(tx, ty, tw, th); ctx.restore();
        ctx.save(); ctx.beginPath(); ctx.rect(tx, ty, tw, th); ctx.clip();
        if (this._artworkImg) this._drawImgCover(ctx, this._artworkImg, tx, ty, tw, th);
        ctx.restore();
        fxWeave(ctx, tx, ty, tw, th, base);
        if (incasso > 0) {
          const s = Math.min(incasso * mm * 0.6, tw / 6);
          ctx.save(); ctx.beginPath(); ctx.rect(tx, ty, tw, th); ctx.clip();
          g = ctx.createLinearGradient(0, ty, 0, ty + s); g.addColorStop(0, 'rgba(0,0,0,0.28)'); g.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.fillStyle = g; ctx.fillRect(tx, ty, tw, s);
          g = ctx.createLinearGradient(tx, 0, tx + s, 0); g.addColorStop(0, 'rgba(0,0,0,0.22)'); g.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.fillStyle = g; ctx.fillRect(tx, ty, s, th);
          ctx.restore();
        }
        ctx.strokeStyle = 'rgba(0,0,0,0.25)'; ctx.lineWidth = 1; ctx.strokeRect(tx + 0.5, ty + 0.5, tw - 1, th - 1);
      }

      ctx.strokeStyle = 'rgba(0,0,0,0.08)'; ctx.lineWidth = 1; ctx.strokeRect(fx, fy, dw, dh);
      if (!this.__noShadow) this._drawDimLabels(ctx, W, H, fx, fy, dw, dh, d.esternoB, d.esternoH);
    }

    /* ── RIEPILOGO, MODAL, CARRELLO ── */
    _righeRiepilogo() {
      const { base, altezza, profilo, ariaMm, kit } = this.state.config;
      const d = FR.dimsAstaL(base || 0, altezza || 0, ariaMm || 0, profilo?.pareteMm || 0);
      const rows = [
        ['Misura della tela', base && altezza ? `${base} × ${altezza} cm` : '—'],
        ['Profilo asta',      nb(profilo) || '—'],
        ['Aria',              ariaMm ? `${ariaMm} mm` : '—'],
        ['Montaggio',         kit ? 'Con kit di montaggio' : 'Sola cornice'],
        ['Misura esterna',    profilo ? `${this._round(d.esternoB, 1)} × ${this._round(d.esternoH, 1)} cm` : '—'],
      ];
      const attItems = this.state.accessori.filter(x => (x.categoria || '').includes('attaccaglie'));
      const attQtys  = this.state.config.attaccaglieQtys || {};
      const attParts = attItems.filter(a => (attQtys[a.sku] || 0) > 0).map(a => `${attQtys[a.sku]} × ${nb(a)}`);
      if (attParts.length) rows.push(['Accessori', attParts.join(', ')]);
      return rows;
    }
    _buildRecap() {
      const el = document.getElementById('casm-modal-recap-rows');
      if (!el) return;
      el.innerHTML = this._righeRiepilogo().map(([l, v]) =>
        `<div class="casm-modal-recap-row"><span>${l}</span><span>${v}</span></div>`).join('');
      const priceEl = document.getElementById('casm-modal-price-value');
      const srcEl   = document.querySelector('.casm-price-amount');
      if (priceEl && srcEl) priceEl.textContent = srcEl.textContent;
    }
    _buildModalText() {
      const ta = document.getElementById('casm-modal-body');
      if (!ta) return;
      const profilo = this.state.config.profilo;
      if (profilo?.disponibile === false) {
        ta.value = `Richiesta disponibilità profilo\nProfilo: ${this._profiloCodice(profilo)}\n\nVorrei sapere quando questo profilo tornerà disponibile.`;
        return;
      }
      const lines = ['Configurazione Cornice con asta a L', ...this._righeRiepilogo().map(([l, v]) => `${l}: ${v}`)];
      const qty = this.state.config.quantita || 1;
      if (qty > 1) lines.push(`Quantità: ${qty}`);
      const srcEl = document.querySelector('.casm-price-amount');
      lines.push(`Stima prezzo: ${srcEl?.textContent || '—'}`);
      ta.value = lines.join('\n');
    }
    _buildCartProperties() {
      const { base, altezza, profilo, ariaMm, kit } = this.state.config;
      const attItems = this.state.accessori.filter(x => (x.categoria || '').includes('attaccaglie'));
      const attQtys  = this.state.config.attaccaglieQtys || {};
      const attParts = attItems.filter(a => (attQtys[a.sku] || 0) > 0).map(a => `${attQtys[a.sku]} × ${nb(a)}`);
      const props = {
        'Tipologia':          'Cornice con asta a L',
        'Misura della tela':  `${base} × ${altezza} cm`,
        'Profilo asta':       nb(profilo),
        'Aria':               `${ariaMm} mm`,
        'Montaggio':          kit ? 'Con kit di montaggio' : 'Sola cornice',
        '_prezzo_calcolato_pezzo': (this._lastCalcResult?.prezzoUnitario || 0).toFixed(2),
      };
      if (attParts.length) props['Accessori'] = attParts.join(', ');
      /* Chiavi nascoste: le rilegge core/fotorecord/ordini_articoli.estrai_config_riga
         (tipo asta_l → price_engine.calcola_asta_l). */
      props['_tipo_prodotto'] = 'asta_l';
      props['_profilo_id_db'] = profilo?.sku ? this._profiloCodice(profilo) : '';
      props['_aria_mm']       = String(ariaMm || '');
      props['_kit']           = kit ? '1' : '0';
      if (attParts.length) {
        props['_accessori_json'] = JSON.stringify(attItems.filter(a => (attQtys[a.sku] || 0) > 0).map(a => ({ sku: a.sku, qty: attQtys[a.sku] })));
      }
      return props;
    }
  }

  /* ══════════════════════════════════════════════════════════════════════════
     ESPOSIZIONE + BOOTSTRAP
     ══════════════════════════════════════════════════════════════════════════ */
  FR.CLASSES = {
    aste:      ConfiguratoreAste,
    montature: ConfiguratoreMontature,
    pannelli:  ConfiguratorePannelli,
    opere:     ConfiguratoreOpere,
    multifoto: ConfiguratoreMultifoto,
    aste_l:    ConfiguratoreAsteL,
  };

  /* Compat: classe aste esposta col nome storico — consumata dal renderer
     headless (generate_frame_images.py) e dal configuratore preventivi interno
     (configura_preventivo.html): constructor senza init, _drawPreview(canvas),
     loaders, __bgColor/__noShadow. */
  window.ConfiguratoreAsteSuMisura = ConfiguratoreAste;
  /* Idem per le cornici con aste a L (ADR 2026-10-12): stesso contratto, altro disegno. */
  window.ConfiguratoreAsteL = ConfiguratoreAsteL;

  function boot() {
    const rootAste = document.getElementById('configuratore-aste-su-misura');
    if (rootAste) {
      // la stessa sezione in modalita' passepartout multifoto (ADR 09/10/2026)
      const modalita = rootAste.dataset.modalita;
      const inst = modalita === 'passepartout_multifoto' ? new ConfiguratoreMultifoto()
                 : modalita === 'aste_a_l'               ? new ConfiguratoreAsteL()
                 : new ConfiguratoreAste();
      inst.init();
      rootAste._configuratore = inst;   // gotcha storico: l'istanza aste vive su el._configuratore
      FR.INSTANCES.casm = inst;
    }
    if (document.getElementById('configuratore-montature-giorno')) {
      FR.INSTANCES.cmg = new ConfiguratoreMontature();
    }
    if (document.getElementById('configuratore-pannelli-solo-vetro')) {
      FR.INSTANCES.cpv = new ConfiguratorePannelli();
    }
    const rootOpere = document.getElementById('configuratore-opere-autore');
    if (rootOpere) {
      const inst = new ConfiguratoreOpere();
      inst.init();
      rootOpere._configuratore = inst;
      FR.INSTANCES.caoa = inst;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
